<template>
  <div
    class="secret-page"
    :class="{ 'secret-page-menu': authenticated && activeSection === 'menu' }"
    :style="pageBackgroundStyle"
  >
    <section v-if="!authenticated" class="gate-card">
      <p class="eyebrow">Area riservata</p>
      <h1>Accesso segreto</h1>

      <div class="gate-form">
        <input
          v-model="enteredPassword"
          type="password"
          placeholder="Inserisci la password"
          @keyup.enter="checkPassword"
        />
        <button class="primary-button" @click="checkPassword">Accedi</button>
      </div>

      <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
    </section>

    <section
      v-else-if="activeSection !== 'play'"
      class="hub-shell"
      :class="'hub-shell-' + activeSection"
    >
      <div
        v-if="!['menu', 'shop', 'story', 'extras'].includes(activeSection)"
        class="hub-topbar"
      >
        <div class="hub-title-cluster">
          <h1>Le Cronache di Adalberto</h1>
        </div>
        <div class="hub-wallet">
          <span class="currency-label"><i class="coin-icon"></i>Oro</span>
          <strong>{{ gold }}</strong>
        </div>
      </div>

      <button
        v-if="activeSection === 'menu'"
        class="hub-menu title-screen"
        type="button"
        aria-label="Inizia"
        @click="openSection('shop')"
      >
        <span class="castle-showcase">
          <img
            :src="castleImage"
            class="castle-artwork"
            alt="Castello della Fortezza Sghiubba"
          />
          <span class="menu-title-overlay">
            <span>Le Cronache di</span>
            <strong>Adalberto</strong>
          </span>
          <span class="menu-start-overlay">Clicca per iniziare</span>
          <span class="battle-glow"></span>
        </span>
      </button>

      <section
        v-if="['shop', 'story', 'extras'].includes(activeSection)"
        class="hub-section menu-hall"
      >
        <div class="shop-hero">
          <div class="shop-hero-copy">
            <h1>Potenzia le tue difese.</h1>
            <p>
              Non permettere ai tiranni dei formaggi di conquistare la tua
              dimora.
            </p>
          </div>
          <div class="shop-status-item gold">
            <span>Oro</span>
            <strong><i class="coin-icon"></i>{{ gold }}</strong>
          </div>
          <div class="shop-status-item level">
            <span class="status-label"><i class="swords-icon"></i>Livello</span>
            <div class="shop-level-control">
              <button
                type="button"
                :disabled="selectedDefenseLevel <= 1"
                aria-label="Livello precedente"
                @click="changeSelectedDefenseLevel(-1)"
              >
                -
              </button>
              <select
                v-model.number="selectedDefenseLevel"
                aria-label="Seleziona livello Defender"
              >
                <option
                  v-for="level in playableDefenseLevels"
                  :key="level"
                  :value="level"
                >
                  {{ level }}
                </option>
              </select>
              <button
                type="button"
                :disabled="selectedDefenseLevel >= defenseCurrentLevel"
                aria-label="Livello successivo"
                @click="changeSelectedDefenseLevel(1)"
              >
                +
              </button>
            </div>
          </div>
          <div class="shop-command-group">
            <button class="primary-button shop-play-button" @click="startGame">
              Gioca <span class="button-arrow" aria-hidden="true">&rarr;</span>
            </button>
            <button
              class="secondary-button shop-dev-button"
              @click="openDevConfig"
            >
              Dev config
            </button>
          </div>
        </div>

        <nav class="hall-tabs" aria-label="Menu principale">
          <button
            class="hall-tab"
            :class="{ active: activeSection === 'shop' }"
            type="button"
            @click="openSection('shop')"
          >
            <span>Officina</span>
            <small>Potenziamenti e preparazione</small>
          </button>
          <button
            class="hall-tab"
            :class="{ active: activeSection === 'story' }"
            type="button"
            @click="openSection('story')"
          >
            <span>Storia</span>
            <small>Capitoli e pagine sbloccate</small>
          </button>
          <button
            class="hall-tab"
            :class="{ active: activeSection === 'extras' }"
            type="button"
            @click="openSection('extras')"
          >
            <span>Glossario</span>
            <small>Bestiario e conoscenza</small>
          </button>
        </nav>

        <div v-if="activeSection === 'shop'" class="shop-upgrade-sections">
          <section
            v-for="group in upgradeGroups"
            :key="group.id"
            class="upgrade-section"
          >
            <div class="upgrade-section-heading">
              <p class="eyebrow">{{ group.eyebrow }}</p>
            </div>

            <div class="upgrade-grid">
              <article
                v-for="upgrade in group.items"
                :key="upgrade.id"
                class="upgrade-card"
                :class="[
                  'upgrade-' + upgrade.id,
                  { maxed: upgrade.level >= upgrade.max },
                ]"
              >
                <span class="upgrade-icon">
                  <img
                    v-if="upgrade.image"
                    :src="upgrade.image"
                    :alt="upgrade.name"
                  />
                  <span v-else>{{ upgrade.icon }}</span>
                </span>
                <div>
                  <p class="upgrade-level">
                    {{ upgradeLevelLabel(upgrade) }}
                  </p>
                  <strong>{{ upgrade.name }}</strong>
                  <span
                    v-if="upgrade.abilityDescription"
                    class="upgrade-ability-description"
                  >
                    <em>Abilità</em>{{ upgrade.abilityDescription }}
                  </span>
                  <span>{{ upgrade.description }}</span>
                  <div class="upgrade-card-values">
                    <div
                      v-for="row in upgradeMetricRows(upgrade)"
                      :key="row.label"
                      class="upgrade-card-value"
                      :class="{
                        'single-value': row.next === null,
                        'has-details': row.details,
                        open: metricDetailsOpen(upgrade, row),
                      }"
                      :tabindex="row.details ? 0 : null"
                      :role="row.details ? 'button' : null"
                      :title="
                        row.details ? 'Mostra dettagli cooldown abilita' : null
                      "
                      @click.stop="toggleMetricDetails(upgrade, row)"
                      @keydown.enter.stop.prevent="
                        toggleMetricDetails(upgrade, row)
                      "
                      @keydown.space.stop.prevent="
                        toggleMetricDetails(upgrade, row)
                      "
                    >
                      <span>{{ row.label }}</span>
                      <strong>Attuale {{ row.current }}</strong>
                      <strong v-if="row.next !== null"
                        >Prossimo {{ row.next }}</strong
                      >
                      <div v-if="row.details" class="upgrade-detail-popover">
                        <div
                          v-for="detail in row.details"
                          :key="detail.label"
                          class="upgrade-detail-row"
                        >
                          <span>{{ detail.label }}</span>
                          <strong>Attuale {{ detail.current }}</strong>
                          <strong>Prossimo {{ detail.next }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="upgrade-track">
                    <div
                      class="upgrade-track-fill"
                      :style="{ width: upgradeProgress(upgrade) + '%' }"
                    ></div>
                  </div>
                </div>
                <span
                  v-if="upgrade.level >= upgrade.max"
                  class="upgrade-card-max"
                >
                  Livello max
                </span>
                <button
                  v-else
                  class="upgrade-card-cost"
                  :class="{ locked: upgradeLockedReason(upgrade) }"
                  :disabled="
                    gold < upgradeCost(upgrade) ||
                    !!upgradeLockedReason(upgrade)
                  "
                  :title="upgradeLockedReason(upgrade) || null"
                  :aria-label="'Acquista ' + upgrade.name"
                  @click.stop="buyUpgrade(upgrade.id)"
                >
                  <span class="upgrade-cost-amount">
                    <i class="coin-icon"></i>{{ upgradeCost(upgrade) }}
                  </span>
                  <span class="upgrade-cost-label">{{
                    upgradeLockedReason(upgrade) ? "Bloccato" : "Potenzia"
                  }}</span>
                </button>
              </article>
            </div>
          </section>
        </div>

        <div
          v-if="devConfigOpen"
          class="dev-modal-backdrop"
          @click.self="closeDevConfig"
        >
          <div class="dev-modal" role="dialog" aria-modal="true">
            <div class="dev-modal-header">
              <div>
                <p class="eyebrow">Strumenti sviluppatore</p>
                <h2>Config test defender</h2>
              </div>
              <button class="icon-button" @click="closeDevConfig">X</button>
            </div>

            <div class="dev-config-grid">
              <label class="dev-field">
                <span>Livello defender</span>
                <input
                  v-model.number="devConfigDraft.defenseLevel"
                  type="number"
                  min="1"
                  max="100"
                />
                <input
                  v-model.number="devConfigDraft.defenseLevel"
                  type="range"
                  min="1"
                  max="100"
                />
              </label>

              <label class="dev-field">
                <span>Oro disponibile</span>
                <input
                  v-model.number="devConfigDraft.gold"
                  type="number"
                  min="0"
                  max="999999"
                />
                <input
                  v-model.number="devConfigDraft.gold"
                  type="range"
                  min="0"
                  max="999999"
                  step="100"
                />
              </label>
            </div>

            <div
              v-for="group in upgradeGroups"
              :key="'dev-' + group.id"
              class="dev-abilities"
            >
              <p class="eyebrow">{{ group.eyebrow }}</p>
              <label
                v-for="upgrade in group.items"
                :key="upgrade.id"
                class="dev-ability-row"
              >
                <span>
                  <img
                    v-if="upgrade.image"
                    :src="upgrade.image"
                    :alt="upgrade.name"
                  />
                  <strong>{{ upgrade.name }}</strong>
                </span>
                <input
                  v-model.number="devConfigDraft.upgradeLevels[upgrade.id]"
                  type="range"
                  :min="upgradeMinLevel(upgrade)"
                  :max="upgrade.max"
                />
                <input
                  v-model.number="devConfigDraft.upgradeLevels[upgrade.id]"
                  type="number"
                  :min="upgradeMinLevel(upgrade)"
                  :max="upgrade.max"
                />
              </label>
            </div>

            <div class="dev-modal-actions">
              <button class="secondary-button" @click="closeDevConfig">
                Annulla
              </button>
              <button class="primary-button" @click="applyDevConfig">
                Applica configurazione
              </button>
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="activeSection === 'story'"
        class="hub-section story-section"
      >
        <div class="section-heading">
          <div>
            <p class="eyebrow">Libro proibito</p>
            <h2>Storia</h2>
          </div>
        </div>

        <article class="story-empty">
          <p class="eyebrow">Capitoli in preparazione</p>
          <h3>La storia arrivera qui.</h3>
          <p>
            Per ora la campagna si avvia dall'Officina. Questa sezione restera
            libera per le pagine narrative da sfogliare.
          </p>
        </article>
      </section>

      <section v-if="activeSection === 'extras'" class="hub-section">
        <div class="section-heading">
          <div>
            <p class="eyebrow">Archivio del nemico</p>
            <h2>Glossario</h2>
          </div>
        </div>

        <article class="glossary-panel">
          <div class="glossary-intro">
            <p class="eyebrow">Bestiario</p>
            <h3>Unita incontrate</h3>
            <p>
              Qui appaiono solo i nemici che hai gia visto nella campagna
              Defender. I valori indicano la stima al tuo livello massimo
              raggiunto: i formaggi diventano piu duri quando sale la pressione.
            </p>
          </div>

          <div class="bestiary-grid">
            <article
              v-for="entry in bestiaryEntries"
              :key="entry.id"
              class="bestiary-card"
            >
              <div
                class="bestiary-portrait"
                :class="'archetype-' + entry.archetype"
                :style="{
                  '--cheese-color': entry.color,
                  '--rind-color': entry.rind,
                }"
              >
                <span
                  v-if="entry.spritePreview"
                  class="sprite-strip-preview"
                  :style="entry.spritePreview"
                  :aria-label="entry.name"
                ></span>
                <img
                  v-else-if="entry.image"
                  :src="entry.image"
                  :alt="entry.name"
                />
                <span v-else class="cheese-avatar">
                  <span class="cheese-core"></span>
                  <span class="cheese-mark one"></span>
                  <span class="cheese-mark two"></span>
                </span>
              </div>

              <div class="bestiary-copy">
                <div class="bestiary-title-row">
                  <div>
                    <p class="bestiary-unlock">
                      Dal livello {{ entry.unlockLevel }}
                    </p>
                    <h3>{{ entry.name }}</h3>
                  </div>
                  <span>{{ entry.role }}</span>
                </div>

                <p class="bestiary-technical">{{ entry.technical }}</p>
                <p class="bestiary-lore">{{ entry.lore }}</p>

                <div class="bestiary-stats">
                  <span
                    v-for="stat in entry.stats"
                    :key="stat.label"
                    class="bestiary-stat"
                  >
                    <em>{{ stat.label }}</em>
                    <strong>{{ stat.value }}</strong>
                  </span>
                </div>

                <p class="bestiary-growth">
                  Statistiche stimate al livello {{ entry.statLevel }}. Base: HP
                  {{ entry.baseStats.hp }}, danno {{ entry.baseStats.damage }},
                  velocita {{ entry.baseStats.speed }}, oro
                  {{ entry.baseStats.reward }}.
                </p>
              </div>
            </article>
          </div>
        </article>
      </section>
    </section>

    <section
      v-else
      ref="gameShell"
      class="game-shell"
      :class="{
        paused: gamePaused,
        finished: gameOver || levelComplete,
      }"
      @mousemove="handlePointerMove"
      @mousedown.left.prevent="startFiring"
      @mouseup.left.prevent="stopFiring"
      @mouseleave="stopFiring"
      @contextmenu.prevent
    >
      <canvas ref="gameCanvas" class="game-canvas"></canvas>

      <div class="hud top-hud">
        <button
          v-if="!gameOver && !levelComplete"
          class="hud-menu-button floating-menu-button"
          type="button"
          aria-label="Metti in pausa"
          @mousedown.stop
          @mouseup.stop
          @click.stop="pauseGame"
        >
          <span class="pause-button-icon" aria-hidden="true">
            <span></span>
            <span></span>
          </span>
          Pausa
        </button>

        <div v-if="gameMode === 'defense'" class="level-panel">
          <div class="level-banner">
            <span>Livello</span>
            <strong>{{ wave }}</strong>
          </div>
          <div class="level-progress">
            <div class="level-progress-track">
              <div
                class="level-progress-fill"
                :style="{ width: defenseLevelProgressPercent + '%' }"
              ></div>
              <div
                class="cheese-marker"
                :style="{ left: defenseLevelProgressPercent + '%' }"
              >
                <span></span>
              </div>
            </div>
            <small>{{ defenseLevelProgressLabel }}</small>
          </div>
        </div>

        <div class="stat-grid" :class="{ defense: gameMode === 'defense' }">
          <div v-if="gameMode !== 'defense'" class="stat-pill">
            <span>{{ mainHealthLabel }}</span>
            <strong>{{ mainHealthValue }}</strong>
            <div class="meter">
              <div
                class="meter-fill health"
                :style="{ width: mainHealthPercent + '%' }"
              ></div>
            </div>
          </div>
          <div v-if="gameMode !== 'defense'" class="stat-pill">
            <span>{{ gameMode === "defense" ? "Livello" : "Ondata" }}</span>
            <strong>{{ wave }}</strong>
          </div>
          <div class="stat-pill resource-stat">
            <span class="currency-label"><i class="coin-icon"></i>Oro</span>
            <strong>{{ gold }}</strong>
          </div>
        </div>
      </div>

      <div v-if="gameMode === 'defense'" class="hud wall-resource-hud">
        <div class="resource-line">
          <em>HP</em>
          <div class="resource-meter">
            <div
              class="resource-fill health"
              :class="castleHealthClass"
              :style="{ width: mainHealthPercent + '%' }"
            ></div>
          </div>
          <strong>{{ mainHealthValue }}</strong>
        </div>
        <div class="resource-line">
          <em>MP</em>
          <div class="resource-meter">
            <div
              class="resource-fill mana"
              :style="{ width: castleManaPercent + '%' }"
            ></div>
          </div>
          <strong>{{ castleManaValue }}</strong>
        </div>
      </div>

      <div class="hud power-dock">
        <button
          v-for="ability in activeAbilities"
          :key="ability.id"
          class="power-button"
          :class="[
            'power-' + ability.id,
            {
              active: selectedPower === ability.id,
              cooling: ability.cooldownLeft > 0,
              cursed: abilitiesDisabled,
              locked: abilityLocked(ability),
              'no-mana': abilityManaLocked(ability),
            },
          ]"
          :disabled="
            ability.cooldownLeft > 0 ||
            abilitiesDisabled ||
            abilityLocked(ability)
          "
          :title="
            abilitiesDisabled
              ? 'Abilita disabilitate dal malocchio'
              : abilityLocked(ability)
              ? abilityLockedReason(ability)
              : abilityManaLocked(ability)
              ? 'Mana insufficiente'
              : ability.name + ' - ' + ability.description
          "
          @mousedown.stop
          @mouseup.stop
          @click.stop="selectPower(ability.id)"
        >
          <span class="power-icon">
            <img
              v-if="ability.image"
              :src="ability.image"
              :alt="ability.name"
            />
            <span v-else>{{ ability.icon }}</span>
          </span>
          <span
            v-if="ability.cooldownLeft > 0"
            class="power-cooldown-veil"
            :style="{ height: abilityCooldownRemainingPercent(ability) + '%' }"
          >
            <span class="power-cooldown-line"></span>
          </span>
          <span class="power-key">{{ ability.key }}</span>
          <span
            v-if="gameMode === 'defense' && abilityManaCost(ability)"
            class="power-cost"
          >
            <em>MP</em>{{ abilityManaCost(ability) }}
          </span>
          <small class="power-tooltip">
            <strong>{{ ability.name }}</strong>
            {{
              ability.cooldownLeft > 0
                ? ability.cooldownLeft.toFixed(1) + "s"
                : abilitiesDisabled
                ? "Malocchio: " + castle.abilitiesDisabledTimer.toFixed(1) + "s"
                : abilityLocked(ability)
                ? abilityLockedReason(ability)
                : abilityManaLocked(ability)
                ? "Mana insufficiente: " + abilityManaCost(ability) + " MP"
                : ability.description
            }}
          </small>
        </button>
      </div>

      <div v-if="abilitiesDisabled" class="hud curse-hud">
        Malocchio: abilita bloccate
        {{ castle.abilitiesDisabledTimer.toFixed(1) }}s
      </div>

      <div
        v-if="gamePaused && !gameOver && !levelComplete"
        class="pause-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Gioco in pausa"
        @mousedown.stop
        @mouseup.stop
        @mousemove.stop
        @click.stop
      >
        <div class="pause-panel">
          <button class="pause-action" type="button" @click="returnToHub">
            <span class="pause-action-icon" aria-hidden="true">⌂</span>
            <strong>Menu</strong>
          </button>
          <button class="pause-action" type="button" @click="retryCurrentLevel">
            <span class="pause-action-icon" aria-hidden="true">↻</span>
            <strong>Riprova</strong>
          </button>
          <button
            class="pause-action primary"
            type="button"
            @click="resumeGame"
          >
            <span class="pause-action-icon" aria-hidden="true">▶</span>
            <strong>Riprendi</strong>
          </button>
        </div>
      </div>

      <div v-if="gameOver" class="game-over">
        <div>
          <p class="eyebrow">Le mura hanno ceduto</p>
          <h2>{{ levelDefeatQuote }}</h2>
          <div class="level-complete-rewards defeat-rewards">
            <p class="level-complete-reward-line">
              <span class="currency-label centered"
                ><i class="coin-icon"></i>Oro guadagnato:</span
              >
              <strong>{{ levelGoldEarned }}</strong>
            </p>
          </div>
          <div class="level-complete-actions">
            <button class="secondary-button" @click="returnToHub">
              Torna al Menu
            </button>
            <button class="primary-button" @click="retryCurrentLevel">
              Riprova
            </button>
          </div>
        </div>
      </div>

      <div v-else-if="levelComplete" class="game-over level-complete">
        <div>
          <h2>Livello {{ completedLevel }} completato</h2>
          <p class="level-complete-quote">{{ levelCompleteQuote }}</p>
          <div class="level-complete-rewards">
            <p class="level-complete-reward-line">
              <span class="currency-label centered"
                ><i class="coin-icon"></i>Oro guadagnato:</span
              >
              <strong>{{ completedLevelGoldEarned }}</strong>
            </p>
            <p class="level-complete-reward-line flawless-bonus-line">
              <span class="currency-label centered"
                ><i class="coin-icon"></i>Castello inviolato:</span
              >
              <strong>{{ completedCastleFlawlessBonus }}</strong>
            </p>
          </div>
          <div class="level-complete-actions">
            <button class="secondary-button" @click="returnToHub">
              Torna al Menu
            </button>
            <button
              v-if="completedLevel < 100"
              class="primary-button"
              @click="startNextDefenseLevel"
            >
              Prossimo Livello
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import crossbowImage from "./img/adalberto-defender/Balestra.png";
import battleBackgroundImage1 from "./img/adalberto-defender/Background/Background1.png";
import battleBackgroundImage2 from "./img/adalberto-defender/Background/Background2.png";
import battleBackgroundImage3 from "./img/adalberto-defender/Background/Background3.png";
import battleBackgroundImage4 from "./img/adalberto-defender/Background/Background4.png";
import battleBackgroundImage5 from "./img/adalberto-defender/Background/Background5.png";
import barrierAbilityImage from "./img/adalberto-defender/Barriera.png";
import kickAbilityImage from "./img/adalberto-defender/Calcio.png";
import castleBackdropImage from "./img/adalberto-defender/Castle.png";
import castleImage from "./img/adalberto-defender/Castle.png";
import arrowsAbilityImage from "./img/adalberto-defender/Frecce.png";
import arrowDamageUpgradeImage from "./img/adalberto-defender/potenziamenti-forza-frecce.png";
import arrowSpeedUpgradeImage from "./img/adalberto-defender/potenziamenti-velocita-frecce.png";
import multiArrowUpgradeImage from "./img/adalberto-defender/potenziamenti-multi-freccia.png";
import castleHpUpgradeImage from "./img/adalberto-defender/potenziamenti-hp-castello.png";
import manaUpgradeImage from "./img/adalberto-defender/potenziamenti-riserva-mana.png";
import manaRegenUpgradeImage from "./img/adalberto-defender/potenziamenti-rigenerazione-mana.png";
import goldHunterUpgradeImage from "./img/adalberto-defender/potenziamenti-cacciatore-professionista.png";
import cooldownUpgradeImage from "./img/adalberto-defender/potenziamenti-rituali-rapidi.png";
import manaTowerImage from "./img/adalberto-defender/ManaTower.png";
import sghiubbaAbilityImage from "./img/adalberto-defender/Sghiubba.png";
import barbaroImage from "./img/adalberto-defender/Barbaro.png";
import barbaroImage2 from "./img/adalberto-defender/Barbaro2.png";
import barbaroImage3 from "./img/adalberto-defender/Barbaro3.png";
import barbaroImage4 from "./img/adalberto-defender/Barbaro4.png";
import bertoCastleImage from "./img/adalberto-defender/Berto1.png";
import barrieraBertoImage from "./img/adalberto-defender/BarrieraBerto.png";
import barrieraBerto2Image from "./img/adalberto-defender/BarrieraBerto2.png";
import calcioBertoImage from "./img/adalberto-defender/CalcioBerto.png";
import calcioBerto2Image from "./img/adalberto-defender/CalcioBerto2.png";
import frecceBertoImage from "./img/adalberto-defender/FrecceBerto.png";
import frecceBerto2Image from "./img/adalberto-defender/FrecceBerto2.png";
import sghiubbaBertoImage from "./img/adalberto-defender/SghiubbaBerto.png";
import sghiubbaBerto2Image from "./img/adalberto-defender/SghiubbaBerto2.png";
import wizardAttack1Image from "./img/adalberto-defender/Wizard/Attack1.png";
import wizardAttack2Image from "./img/adalberto-defender/Wizard/Attack2.png";
import wizardDeathImage from "./img/adalberto-defender/Wizard/Death.png";
import wizardIdleImage from "./img/adalberto-defender/Wizard/Idle.png";
import wizardRunImage from "./img/adalberto-defender/Wizard/Run.png";
import wizardHitImage from "./img/adalberto-defender/Wizard/Take hit.png";
import cheeseSoldierWalkImage from "./img/adalberto-defender/CheeseSoldier/cheesewalk.png";
import cheeseSoldierIdleImage from "./img/adalberto-defender/CheeseSoldier/solderidle.png";
import cheeseSoldierAttackImage from "./img/adalberto-defender/CheeseSoldier/soldierattack.png";
import cheeseSoldierDeathImage from "./img/adalberto-defender/CheeseSoldier/soldierdeath.png";
import cheeseSoldierHitImage from "./img/adalberto-defender/CheeseSoldier/soldierhit.png";
import golemBossSheetImage from "./img/adalberto-defender/GolemBoss/PNG sheet/Character_sheet.png";
import golemArmProjectileImage from "./img/adalberto-defender/GolemBoss/weapon PNG/arm_projectile.png";
import golemArmProjectileGlowingImage from "./img/adalberto-defender/GolemBoss/weapon PNG/arm_projectile_glowing.png";
import golemLaserSheetImage from "./img/adalberto-defender/GolemBoss/weapon PNG/Laser_sheet.png";

const PASSWORD = "adalghiro";
const FIRE_RATE = 670;
const BASE_WIDTH = 170;
const TURRET_OFFSET_X = 132;
const ARROW_SPEED = 900;
const GUIDED_ARROW_SPEED = 980;
const MULTI_ARROW_STEP = (Math.PI / 180) * 2.5;
const CASTLE_HP_BY_WALL_LEVEL = [
  100, 110, 120, 130, 150, 160, 170, 180, 190, 210, 220, 230, 240, 250, 270,
  280, 290, 300, 310, 320, 330, 340, 350, 360, 380,
];
const DEFENSE_POWER_MANA_COSTS = {
  sghiubbe: 35,
  ai: 45,
  barrier: 50,
  kickboxing: 60,
  barbaro: 75,
};
const MANA_UNLOCK_DEFENSE_LEVEL = 5;
const POWER_MANA_UNLOCK_LEVELS = {
  sghiubbe: 1,
  kickboxing: 1,
  ai: 5,
  barrier: 5,
  barbaro: 10,
};
const MANA_TOWER_FRAME = {
  width: 204,
  height: 408,
  cropY: 72,
  cropHeight: 236,
};
const BARBARO_IMAGES = [
  barbaroImage,
  barbaroImage2,
  barbaroImage3,
  barbaroImage4,
];
const WIZARD_IMAGE_SOURCES = {
  idle: wizardIdleImage,
  run: wizardRunImage,
  attack1: wizardAttack1Image,
  attack2: wizardAttack2Image,
  death: wizardDeathImage,
  hit: wizardHitImage,
};
const CHEESE_SOLDIER_IMAGE_SOURCES = {
  idle: cheeseSoldierIdleImage,
  walk: cheeseSoldierWalkImage,
  attack: cheeseSoldierAttackImage,
  hit: cheeseSoldierHitImage,
  death: cheeseSoldierDeathImage,
};
const CROSSBOW_DAMAGE_SPEED_COSTS = [
  100, 200, 300, 500, 700, 900, 1200, 1500, 1800, 2500, 3000, 4000, 5500, 7000,
  9000, 12000, 15000, 20000, 25000, 30000,
];
const UPGRADE_COSTS = {
  damage: CROSSBOW_DAMAGE_SPEED_COSTS,
  speed: CROSSBOW_DAMAGE_SPEED_COSTS,
  arrows: [10000, 25000, 75000, 200000],
  walls: [
    200, 300, 500, 800, 1200, 1600, 2000, 2500, 3100, 3700, 4300, 5000, 5400,
    6000, 6700, 7400, 8200, 9000, 10000, 11200, 12500, 14000, 16000, 18000,
  ],
  mana: [
    200, 300, 400, 500, 600, 700, 800, 900, 1000, 1200, 1500, 2000, 2500, 3000,
    3500, 4000, 4500, 5000, 10000, 15000,
  ],
  manaRegen: [500, 1500, 2500, 5000, 8000, 12000, 20000, 30000, 50000, 75000],
  goldHunter: [
    5000, 10000, 15000, 20000, 25000, 30000, 35000, 40000, 45000, 50000,
  ],
  sghiubbePower: [
    200, 400, 700, 1000, 1400, 1900, 2500, 3200, 4100, 5200, 6500, 8000, 9800,
    12000, 14500, 17500, 21000, 25000, 30000, 36000,
  ],
  arrowsPower: [
    200, 400, 700, 1000, 1400, 1900, 2500, 3200, 4100, 5200, 6500, 8000, 9800,
    12000, 14500, 17500, 21000, 25000, 30000, 36000,
  ],
  barrierPower: [
    200, 400, 700, 1000, 1400, 1900, 2500, 3200, 4100, 5200, 6500, 8000, 9800,
    12000, 14500, 17500, 21000, 25000, 30000, 36000,
  ],
  kickPower: [
    200, 400, 700, 1000, 1400, 1900, 2500, 3200, 4100, 5200, 6500, 8000, 9800,
    12000, 14500, 17500, 21000, 25000, 30000, 36000,
  ],
  barbaroPower: [
    500, 1500, 2400, 3000, 4000, 6000, 9000, 12000, 15000, 20000, 25000, 30000,
    40000, 50000, 65000, 80000, 100000, 125000, 150000, 200000,
  ],
  cooldownPower: [
    1000, 2500, 5000, 9000, 14000, 20000, 28000, 38000, 50000, 65000,
  ],
};
const PRIMARY_ABILITY_EFFECT_SCALE = 0.55;

const BARBARO_GRID = {
  columns: 5,
  rows: 3,
  width: 1536,
  height: 1024,
};
const BARBARO_FRAME_BLEED_BY_TIER = [0, 0, 0, 0];
const BARBARO_FRAMES = {
  idle: [0, 1, 2, 3, 4].map((column) => ({ row: 0, column })),
  attack: [0, 1, 2, 3, 4].map((column) => ({ row: 1, column })),
  death: [0, 1, 2, 3, 4].map((column) => ({ row: 2, column })),
};
const LEVEL_COMPLETE_QUOTES = [
  "Rimaniamo allerta, il formaggio può introfularsi ovunque",
  "Anche oggi niente formaggio a tavola",
  "C'è puzza di formaggio qua fuori... coff coff...",
  "Bella vittoria, Ada schiaccia un pisolino...",
  "Adalberto fa un sonnellino",
  "Ada si fa una sghiubbetta",
  "Chissà se i nemici stagionati torneranno...",
  "Dite alle damigelle che la vittoria è nostra",
];

const LEVEL_DEFEAT_QUOTES = [
  "I nemici hanno conquistato la fortezza",
  "Il castello brulica di groviera",
  "Il buon vecchio Ada viene assalito dal formaggio filante",
  "Si salvi chi pu\u00f2!",
  "Quest'oggi \u00e8 caduto il regno di Berto",
];

const BARBARO_ANIMATIONS = {
  idle: { source: "idle", frames: [1, 2, 1, 0], fps: 2.2 },
  walk: { source: "idle", frames: [1, 2, 3, 2], fps: 5.2 },
  attack: { source: "attack", frames: [0, 1, 2, 3, 4], fps: 10.5 },
  death: { source: "death", frames: [0, 1, 2, 3, 4], fps: 5.8 },
};
const WIZARD_ANIMATIONS = {
  idle: { source: "idle", frames: 8, fps: 6.5, loop: true },
  run: { source: "run", frames: 8, fps: 10, loop: true },
  attack1: { source: "attack1", frames: 8, fps: 12, duration: 0.64 },
  attack2: { source: "attack2", frames: 8, fps: 12, duration: 0.72 },
  death: { source: "death", frames: 7, fps: 9, duration: 0.86 },
  hit: { source: "hit", frames: 3, fps: 12, duration: 0.26 },
};
const CHEESE_SOLDIER_FRAMES = {
  idle: [
    { x: 37, y: 104, w: 180, h: 138 },
    { x: 280, y: 104, w: 185, h: 138 },
    { x: 516, y: 105, w: 186, h: 137 },
    { x: 731, y: 105, w: 185, h: 137 },
    { x: 926, y: 105, w: 154, h: 137 },
  ],
  walk: [
    { x: 31, y: 137, w: 116, h: 106 },
    { x: 169, y: 137, w: 117, h: 105 },
    { x: 305, y: 139, w: 113, h: 104 },
    { x: 436, y: 142, w: 116, h: 101 },
    { x: 571, y: 142, w: 116, h: 103 },
    { x: 708, y: 144, w: 114, h: 101 },
    { x: 837, y: 146, w: 110, h: 100 },
    { x: 958, y: 158, w: 111, h: 88 },
  ],
  attack: [
    { x: 49, y: 135, w: 141, h: 103 },
    { x: 261, y: 143, w: 134, h: 95 },
    { x: 466, y: 148, w: 178, h: 90 },
    { x: 695, y: 146, w: 138, h: 93 },
    { x: 900, y: 135, w: 143, h: 104 },
  ],
  hit: [
    { x: 47, y: 143, w: 141, h: 110 },
    { x: 232, y: 151, w: 138, h: 102 },
    { x: 402, y: 145, w: 136, h: 108 },
    { x: 586, y: 168, w: 123, h: 85 },
    { x: 762, y: 151, w: 132, h: 102 },
    { x: 927, y: 145, w: 137, h: 108 },
  ],
  death: [
    { x: 38, y: 167, w: 146, h: 117 },
    { x: 209, y: 176, w: 142, h: 109 },
    { x: 379, y: 193, w: 133, h: 96 },
    { x: 536, y: 235, w: 124, h: 57 },
    { x: 681, y: 241, w: 124, h: 52 },
    { x: 826, y: 267, w: 107, h: 27 },
    { x: 963, y: 264, w: 88, h: 30 },
  ],
};
const CHEESE_SOLDIER_ANIMATIONS = {
  idle: {
    source: "idle",
    fps: 5.4,
    loop: true,
  },
  walk: {
    source: "walk",
    fps: 11,
    loop: true,
  },
  attack: {
    source: "attack",
    fps: 15,
    duration: 0.46,
  },
  hit: {
    source: "hit",
    fps: 16,
    duration: 0.34,
  },
  death: {
    source: "death",
    fps: 13,
    duration: 0.78,
  },
};
const GOLEM_BOSS_ANIMATIONS = {
  idle: { row: 0, frames: [0, 1, 2, 3], fps: 3.2 },
  walk: { row: 1, frames: [0, 1, 2, 3, 4, 5, 6, 7], fps: 8.2 },
  rock: { row: 2, frames: [2, 3, 4, 5, 6, 7, 8, 9], fps: 9.5, duration: 0.72 },
  laser: { row: 5, frames: [1, 2, 3, 4, 5, 6], fps: 9.5, duration: 0.72 },
  hurt: { row: 7, frames: [0, 1, 2, 3, 4], fps: 8.5, duration: 0.36 },
  death: { row: 8, frames: [0, 1, 2, 3], fps: 5.5, duration: 0.8 },
};
const GOLEM_BOSS_CELL_SIZE = 100;
const GOLEM_LASER_FRAME_HEIGHT = 150;
const GOLEM_ARM_PROJECTILE_CROP = { x: 60, y: 30, w: 35, h: 14 };
const GOLEM_ARM_GLOWING_CROP = { x: 160, y: 30, w: 35, h: 14 };
const BARBARO_NORMALIZED_FRAME = {
  width: 360,
  height: 300,
};
const BERTO_CASTLE_FRAMES = {
  idle: { x: 196, y: 14, w: 228, h: 391 },
  wake: { x: 610, y: 4, w: 252, h: 367 },
  sleep: { x: 219, y: 405, w: 205, h: 363 },
  angry: { x: 599, y: 405, w: 236, h: 331 },
};
const BERTO_ABILITY_ANIMATIONS = {
  sghiubbe: {
    columns: 2,
    rows: 2,
    duration: 1.45,
    effectDelay: 0.5,
  },
  sghiubbe2: {
    columns: 2,
    rows: 2,
    duration: 1.62,
    effectDelay: 0.5,
    drawHeightFactor: 0.3,
    maxDrawHeight: 260,
    drawOffsetX: -10,
    drawYOffsetFactor: 0.66,
    frames: [
      { x: 90, y: 0, w: 580, h: 512 },
      { x: 778, y: 0, w: 730, h: 512 },
      { x: 10, y: 512, w: 760, h: 512 },
      { x: 768, y: 512, w: 760, h: 512 },
    ],
  },
  ai: {
    columns: 2,
    rows: 2,
    duration: 1.1,
    drawHeightFactor: 0.25,
    maxDrawHeight: 218,
    drawOffsetX: -4,
    drawYOffsetFactor: 0.64,
    frames: [
      { x: 66, y: 28, w: 610, h: 438 },
      { x: 744, y: 8, w: 540, h: 466 },
      { x: 42, y: 498, w: 660, h: 462 },
      { x: 690, y: 512, w: 846, h: 442 },
    ],
  },
  ai2: {
    columns: 2,
    rows: 2,
    duration: 1.18,
    drawHeightFactor: 0.27,
    maxDrawHeight: 238,
    drawOffsetX: -8,
    drawYOffsetFactor: 0.64,
    frames: [
      { x: 12, y: 0, w: 730, h: 512 },
      { x: 742, y: 0, w: 794, h: 512 },
      { x: 0, y: 512, w: 760, h: 500 },
      { x: 672, y: 512, w: 864, h: 500 },
    ],
  },
  kickboxing: {
    columns: 2,
    rows: 2,
    duration: 0.78,
  },
  kickboxing2: {
    columns: 2,
    rows: 2,
    duration: 0.82,
    drawHeightFactor: 0.27,
    maxDrawHeight: 238,
    drawOffsetX: -8,
    drawYOffsetFactor: 0.62,
    frames: [
      { x: 40, y: 12, w: 660, h: 500 },
      { x: 790, y: 12, w: 650, h: 500 },
      { x: 40, y: 548, w: 720, h: 456 },
      { x: 768, y: 512, w: 768, h: 500 },
    ],
  },
  barrier: {
    columns: 2,
    rows: 2,
    duration: 1.12,
    drawHeightFactor: 0.29,
    maxDrawHeight: 250,
    drawOffsetX: -10,
    drawYOffsetFactor: 0.64,
    frames: [
      { x: 145, y: 24, w: 500, h: 430 },
      { x: 790, y: 0, w: 570, h: 470 },
      { x: 145, y: 430, w: 540, h: 530 },
      { x: 760, y: 430, w: 640, h: 530 },
    ],
  },
  barrier2: {
    columns: 2,
    rows: 2,
    duration: 1.22,
    drawHeightFactor: 0.31,
    maxDrawHeight: 270,
    drawOffsetX: -18,
    drawYOffsetFactor: 0.66,
    frames: [
      { x: 44, y: 8, w: 690, h: 504 },
      { x: 768, y: 8, w: 744, h: 504 },
      { x: 18, y: 512, w: 760, h: 500 },
      { x: 768, y: 512, w: 760, h: 510 },
    ],
  },
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function randomBetween(min, max) {
  return Math.random() * (max - min) + min;
}

function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export default {
  name: "MyPage",
  data() {
    return {
      enteredPassword: "",
      authenticated: false,
      errorMessage: "",
      activeSection: "menu",
      selectedStoryId: "defense",
      gameMode: "defense",
      battleBackgroundImages: [
        battleBackgroundImage1,
        battleBackgroundImage2,
        battleBackgroundImage3,
        battleBackgroundImage4,
        battleBackgroundImage5,
      ],
      castleImage,
      castleBackdropImage,
      crossbowImage,
      bertoCastleImage,
      manaTowerImage,
      barrieraBertoImage,
      barrieraBerto2Image,
      calcioBertoImage,
      calcioBerto2Image,
      frecceBertoImage,
      frecceBerto2Image,
      sghiubbaBertoImage,
      sghiubbaBerto2Image,
      abilityImages: {
        sghiubbe: sghiubbaAbilityImage,
        ai: arrowsAbilityImage,
        barrier: barrierAbilityImage,
        kickboxing: kickAbilityImage,
        barbaro: barbaroImage,
      },
      battleBackgroundSprites: [],
      crossbowSprite: null,
      castleBackdropSprite: null,
      manaTowerSprite: null,
      bertoCastleSprite: null,
      bertoCastleFrame: "idle",
      bertoCastleMode: "intro",
      bertoCastleTimer: 0,
      bertoAbilitySprites: {},
      bertoAbilityFrames: {},
      activeBertoAbility: null,
      bertoAbilityTimer: 0,
      pendingBertoAbilityEffects: [],
      golemBossSheetImage,
      golemArmProjectileImage,
      golemArmProjectileGlowingImage,
      golemLaserSheetImage,
      golemBossSprite: null,
      golemArmProjectileSprite: null,
      golemArmProjectileGlowingSprite: null,
      golemLaserSprite: null,
      barbaroSprite: null,
      barbaroSprites: [],
      barbaroFrameCanvasesByTier: {},
      barbaroFrameCanvases: {},
      barbaroIconImage: null,
      barbaroIconImages: {},
      wizardSprites: {},
      cheeseSoldierSprites: {},
      animationFrameId: null,
      lastFrameAt: 0,
      lastShotAt: 0,
      pointer: { x: 720, y: 320 },
      canvasSize: { width: 1280, height: 720, ratio: 1 },
      isFiring: false,
      storeOpen: false,
      gamePaused: false,
      activeMetricDetails: null,
      devConfigOpen: false,
      devConfigDraft: {
        defenseLevel: 1,
        gold: 0,
        upgradeLevels: {},
      },
      gameOver: false,
      wave: 1,
      defenseCurrentLevel: 1,
      selectedDefenseLevel: 1,
      waveTimer: 0,
      spawnTimer: 0,
      levelKills: 0,
      levelGoldEarned: 0,
      levelTarget: 0,
      bossSpawned: false,
      bossActive: false,
      castleDamagedThisLevel: false,
      levelComplete: false,
      completedLevel: 0,
      completedLevelGoldEarned: 0,
      completedCastleFlawlessBonus: 0,
      levelCompleteQuote: "",
      levelDefeatQuote: "",
      gold: 0,
      score: 0,
      message:
        "Difendi il castello. Tieni premuto il click sinistro per tirare.",
      castle: {
        hp: 100,
        maxHp: 100,
        mana: 0,
        maxMana: 0,
        barrierTimer: 0,
        abilitiesDisabledTimer: 0,
      },
      turret: {
        x: 132,
        y: 360,
      },
      arrows: [],
      enemies: [],
      enemyProjectiles: [],
      barbaroAllies: [],
      effects: [],
      particles: [],
      assaultUnits: [],
      cheeseProjectiles: [],
      assaultBase: {
        hp: 240,
        maxHp: 240,
        shotTimer: 0,
        lavaTimer: 0,
      },
      berto: {
        x: 240,
        y: 360,
        targetX: 240,
        targetY: 360,
        hp: 180,
        maxHp: 180,
      },
      bertoEnemies: [],
      bertoSpawnTimer: 0,
      selectedPower: "sghiubbe",
      guidedArrowBursts: [],
      powers: [
        {
          id: "sghiubbe",
          key: "1",
          name: "Sghiubba Nebbiosa",
          icon: "S",
          image: sghiubbaAbilityImage,
          description: "rallenta l'orda",
          cooldown: 8,
          cooldownLeft: 0,
        },
        {
          id: "ai",
          key: "2",
          name: "Frecce predittive",
          icon: "AI",
          image: arrowsAbilityImage,
          description: "frecce guidate",
          cooldown: 10,
          cooldownLeft: 0,
        },
        {
          id: "barrier",
          key: "3",
          name: "Barriera",
          icon: "B",
          image: barrierAbilityImage,
          description: "blocca i danni",
          cooldown: 12,
          cooldownLeft: 0,
        },
        {
          id: "kickboxing",
          key: "4",
          name: "Calcio rotante",
          icon: "K",
          image: kickAbilityImage,
          description: "onda d'urto",
          cooldown: 9,
          cooldownLeft: 0,
        },
        {
          id: "barbaro",
          key: "5",
          name: "Evoca Barbaro",
          icon: "BR",
          image: null,
          description: "alleato in prima linea",
          cooldown: 18,
          cooldownLeft: 0,
        },
      ],
      upgrades: [
        {
          id: "damage",
          category: "crossbow",
          name: "Punte rinforzate",
          icon: "DMG",
          image: arrowDamageUpgradeImage,
          description: "Aumenta il danno delle frecce.",
          level: 0,
          max: 20,
          baseCost: 40,
        },
        {
          id: "speed",
          category: "crossbow",
          name: "Corda rapida",
          icon: "SPD",
          image: arrowSpeedUpgradeImage,
          description: "Aumenta la velocita dei colpi.",
          level: 0,
          max: 20,
          baseCost: 55,
        },
        {
          id: "arrows",
          category: "crossbow",
          name: "Arco a raffica",
          icon: "x1",
          image: multiArrowUpgradeImage,
          description: "Aggiunge frecce simultanee al tiro principale.",
          level: 1,
          max: 5,
          baseCost: 40,
        },
        {
          id: "walls",
          category: "crossbow",
          name: "Mura piu spesse",
          icon: "HP",
          image: castleHpUpgradeImage,
          description: "Aumenta gli HP massimi del castello.",
          level: 1,
          max: 25,
          baseCost: 65,
        },
        {
          id: "mana",
          category: "crossbow",
          name: "Riserva mana",
          icon: "MP",
          image: manaUpgradeImage,
          description: "Aumenta il mana massimo del castello.",
          level: 0,
          max: 20,
          baseCost: 72,
        },
        {
          id: "manaRegen",
          category: "crossbow",
          name: "Rigenerazione mana",
          icon: "RG",
          image: manaRegenUpgradeImage,
          description: "Aumenta il mana recuperato al secondo.",
          level: 0,
          max: 10,
          baseCost: 95,
        },
        {
          id: "goldHunter",
          category: "crossbow",
          name: "Cacciatore professionista",
          icon: "ORO",
          image: goldHunterUpgradeImage,
          description: "Aumenta l'oro ottenuto eliminando i nemici.",
          level: 0,
          max: 10,
          baseCost: 5000,
        },
        {
          id: "sghiubbePower",
          category: "ability",
          powerId: "sghiubbe",
          name: "Sghiubba",
          icon: "S",
          image: sghiubbaAbilityImage,
          abilityDescription:
            "Rallenta tutti i nemici del 50% e infligge danno immediato.",
          description: "Aumenta durata del rallentamento e danno.",
          level: 0,
          max: 20,
          baseCost: 70,
        },
        {
          id: "arrowsPower",
          category: "ability",
          powerId: "ai",
          name: "Frecce predittive",
          icon: "AI",
          image: arrowsAbilityImage,
          abilityDescription:
            "Lancia una raffica di frecce guidate che inseguono i nemici più vicini.",
          description: "Aumenta numero e danno delle frecce guidate.",
          level: 0,
          max: 20,
          baseCost: 78,
        },
        {
          id: "barrierPower",
          category: "ability",
          powerId: "barrier",
          name: "Barriera",
          icon: "B",
          image: barrierAbilityImage,
          abilityDescription:
            "Rende il castello invulnerabile per alcuni secondi.",
          description: "Aumenta la durata dell'invulnerabilità del castello.",
          level: 0,
          max: 20,
          baseCost: 86,
        },
        {
          id: "kickPower",
          category: "ability",
          powerId: "kickboxing",
          name: "Calcio rotante",
          icon: "K",
          image: kickAbilityImage,
          abilityDescription:
            "Respinge e danneggia i nemici davanti al castello.",
          description: "Aumenta danno, portata e respinta.",
          level: 0,
          max: 20,
          baseCost: 82,
        },
        {
          id: "barbaroPower",
          category: "ability",
          powerId: "barbaro",
          name: "Barbaro",
          icon: "BR",
          image: null,
          abilityDescription:
            "Evoca un Barbaro alleato che blocca e attacca i nemici in prima linea.",
          description: "Aumenta HP, danno, velocità e frequenza d'attacco.",
          level: 0,
          max: 20,
          baseCost: 112,
        },
        {
          id: "cooldownPower",
          category: "crossbow",
          name: "Rituali rapidi",
          icon: "CD",
          image: cooldownUpgradeImage,
          description: "Riduce il cooldown di tutte le abilita.",
          level: 0,
          max: 10,
          baseCost: 1000,
        },
      ],
      assaultAbilities: [
        {
          id: "organism",
          key: "Q",
          name: "Organismo OGM",
          icon: "O",
          description: "truppa rapida",
          cooldown: 1.2,
          cooldownLeft: 0,
        },
        {
          id: "grater",
          key: "W",
          name: "Grattugia",
          icon: "G",
          description: "tank triturante",
          cooldown: 2.6,
          cooldownLeft: 0,
        },
        {
          id: "fireSghiubba",
          key: "E",
          name: "Sghiubba infuocata",
          icon: "F",
          description: "brucia la linea",
          cooldown: 4.2,
          cooldownLeft: 0,
        },
      ],
      bertoAbilities: [
        {
          id: "graterSword",
          key: "Q",
          name: "Spada-grattugia",
          icon: "SG",
          description: "taglio ravvicinato",
          cooldown: 0.75,
          cooldownLeft: 0,
        },
        {
          id: "kick",
          key: "W",
          name: "Kick box",
          icon: "K",
          description: "respinta circolare",
          cooldown: 2.2,
          cooldownLeft: 0,
        },
        {
          id: "electricity",
          key: "E",
          name: "Elettricita",
          icon: "E",
          description: "scarica ad area",
          cooldown: 3.8,
          cooldownLeft: 0,
        },
      ],
    };
  },
  computed: {
    wallUpgradeLevel() {
      return this.getUpgradeLevel("walls");
    },
    manaUpgradeLevel() {
      return this.getUpgradeLevel("mana");
    },
    manaUnlockReached() {
      return this.defenseCurrentLevel >= MANA_UNLOCK_DEFENSE_LEVEL;
    },
    manaTowerFrameIndex() {
      const level = this.manaUpgradeLevel;
      if (level >= 10) {
        return 2;
      }
      if (level >= 5) {
        return 1;
      }
      return level >= 1 ? 0 : -1;
    },
    battleBackgroundTier() {
      const level = this.wallUpgradeLevel;
      return clamp(
        Math.floor((level - 1) / 5),
        0,
        this.battleBackgroundImages.length - 1
      );
    },
    currentBattleBackgroundImage() {
      return this.battleBackgroundImages[this.battleBackgroundTier];
    },
    pageBackgroundStyle() {
      if (
        !this.authenticated ||
        this.activeSection === "menu" ||
        this.activeSection === "shop" ||
        this.activeSection === "story" ||
        this.activeSection === "extras"
      ) {
        return {};
      }
      return {
        backgroundImage: `linear-gradient(90deg, rgba(18, 24, 39, 0.8), rgba(9, 35, 27, 0.58)), url(${this.currentBattleBackgroundImage})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
      };
    },
    playableDefenseLevels() {
      const maxLevel = clamp(Math.floor(this.defenseCurrentLevel), 1, 100);
      return Array.from({ length: maxLevel }, (_, index) => index + 1);
    },
    bestiaryEntries() {
      const unlockedLevel = clamp(Math.floor(this.defenseCurrentLevel), 1, 100);
      return this.bestiaryEnemyCatalog()
        .filter((entry) => entry.unlockLevel <= unlockedLevel)
        .map((entry) => this.decorateBestiaryEntry(entry, unlockedLevel));
    },
    activeAbilities() {
      if (this.gameMode === "assault") {
        return this.assaultAbilities;
      }
      if (this.gameMode === "berto") {
        return this.bertoAbilities;
      }
      return this.powers;
    },
    upgradeGroups() {
      return [
        {
          id: "crossbow",
          eyebrow: "Castello",
          items: this.upgrades.filter(
            (upgrade) => upgrade.category === "crossbow"
          ),
        },
        {
          id: "ability",
          eyebrow: "Abilità",
          items: this.upgrades.filter(
            (upgrade) => upgrade.category === "ability"
          ),
        },
      ];
    },
    castleHealthPercent() {
      return clamp((this.castle.hp / this.castle.maxHp) * 100, 0, 100);
    },
    castleHealthClass() {
      if (this.castleHealthPercent < 20) {
        return "danger";
      }
      if (this.castleHealthPercent < 50) {
        return "warning";
      }
      return "healthy";
    },
    castleManaPercent() {
      if (this.castle.maxMana <= 0) {
        return 0;
      }
      return clamp((this.castle.mana / this.castle.maxMana) * 100, 0, 100);
    },
    castleManaValue() {
      if (this.castle.maxMana <= 0) {
        return "0 / 0";
      }
      return `${Math.floor(this.castle.mana)} / ${this.castle.maxMana}`;
    },
    mainHealthLabel() {
      if (this.gameMode === "assault") {
        return "Base nemica";
      }
      if (this.gameMode === "berto") {
        return "Berto";
      }
      return "Mura";
    },
    mainHealthValue() {
      if (this.gameMode === "assault") {
        return `${Math.ceil(this.assaultBase.hp)} / ${this.assaultBase.maxHp}`;
      }
      if (this.gameMode === "berto") {
        return `${Math.ceil(this.berto.hp)} / ${this.berto.maxHp}`;
      }
      return `${Math.ceil(this.castle.hp)} / ${this.castle.maxHp}`;
    },
    mainHealthPercent() {
      if (this.gameMode === "assault") {
        return clamp(
          (this.assaultBase.hp / this.assaultBase.maxHp) * 100,
          0,
          100
        );
      }
      if (this.gameMode === "berto") {
        return clamp((this.berto.hp / this.berto.maxHp) * 100, 0, 100);
      }
      return this.castleHealthPercent;
    },
    controlHint() {
      if (this.gameMode === "assault") {
        return "Mouse: scegli corsia | Q/W/E o pulsanti: schiera truppe | Esc: pausa";
      }
      if (this.gameMode === "berto") {
        return "Mouse: muovi Berto | Click/Q: spada | W: kick box | E: elettricita | Esc: pausa";
      }
      return "Mouse: mira e spara | 1-4: poteri | Esc: pausa";
    },
    defenseLevelProgressPercent() {
      if (this.bossActive) {
        const boss = this.enemies.find((enemy) => enemy.isBoss);
        if (!boss) {
          return 100;
        }
        const bossProgress = 1 - boss.hp / boss.maxHp;
        return clamp(86 + bossProgress * 14, 0, 100);
      }
      return clamp((this.levelKills / this.levelTarget) * 86, 0, 86);
    },
    defenseLevelProgressLabel() {
      if (this.bossActive) {
        return this.wave === 100 ? "Superboss in campo" : "Boss in campo";
      }
      if (this.bossSpawned) {
        return "Ingresso boss";
      }
      return `${this.levelKills} / ${this.levelTarget} formaggi`;
    },
    damageBonus() {
      return this.getUpgradeLevel("damage");
    },
    shotInterval() {
      return Math.max(66, FIRE_RATE - this.getUpgradeLevel("speed") * 30.2);
    },
    abilitiesDisabled() {
      return this.castle.abilitiesDisabledTimer > 0;
    },
    arrowCount() {
      return clamp(this.getUpgradeLevel("arrows"), 1, 5);
    },
    cooldownMultiplier() {
      return this.cooldownMultiplierForLevel(
        this.getUpgradeLevel("cooldownPower")
      );
    },
    powerStrength() {
      return 1 + this.highestAbilityLevel * 0.22;
    },
    highestAbilityLevel() {
      return Math.max(
        this.getUpgradeLevel("sghiubbePower"),
        this.getUpgradeLevel("arrowsPower"),
        this.getUpgradeLevel("barrierPower"),
        this.getUpgradeLevel("kickPower"),
        this.getUpgradeLevel("barbaroPower")
      );
    },
    arrowMaxRange() {
      const horizontalRange = this.canvasSize.width - this.turret.x + 220;
      return Math.hypot(horizontalRange, this.canvasSize.height);
    },
    manaRegenRate() {
      return this.manaRegenRateForLevel(this.getUpgradeLevel("manaRegen"));
    },
  },
  watch: {
    authenticated(value) {
      this.setPageChromeHidden(value);
      if (value) {
        this.activeSection = "menu";
        this.stopLoop();
      }
    },
  },
  mounted() {
    this.setAdalbertoRouteChrome(true);
    this.setPageChromeHidden(this.authenticated);
    this.loadGameSprites();
    window.addEventListener("resize", this.resizeCanvas);
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("mouseup", this.stopFiring);
  },
  beforeDestroy() {
    this.setPageChromeHidden(false);
    this.setAdalbertoRouteChrome(false);
    window.removeEventListener("resize", this.resizeCanvas);
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("mouseup", this.stopFiring);
    this.stopLoop();
  },
  methods: {
    loadGameSprites() {
      this.battleBackgroundImages.forEach((source, index) => {
        this.loadSprite(source, "battleBackgroundSprites", index);
      });
      this.loadSprite(this.crossbowImage, "crossbowSprite");
      this.loadSprite(this.castleBackdropImage, "castleBackdropSprite");
      this.loadSprite(this.manaTowerImage, "manaTowerSprite");
      this.loadSprite(this.bertoCastleImage, "bertoCastleSprite");
      this.loadBertoAbilitySprite("sghiubbe", this.sghiubbaBertoImage);
      this.loadBertoAbilitySprite("sghiubbe2", this.sghiubbaBerto2Image);
      this.loadBertoAbilitySprite("ai", this.frecceBertoImage);
      this.loadBertoAbilitySprite("ai2", this.frecceBerto2Image);
      this.loadBertoAbilitySprite("barrier", this.barrieraBertoImage);
      this.loadBertoAbilitySprite("barrier2", this.barrieraBerto2Image);
      this.loadBertoAbilitySprite("kickboxing", this.calcioBertoImage);
      this.loadBertoAbilitySprite("kickboxing2", this.calcioBerto2Image);
      this.loadSprite(this.golemBossSheetImage, "golemBossSprite");
      this.loadSprite(this.golemArmProjectileImage, "golemArmProjectileSprite");
      this.loadSprite(
        this.golemArmProjectileGlowingImage,
        "golemArmProjectileGlowingSprite"
      );
      this.loadSprite(this.golemLaserSheetImage, "golemLaserSprite");
      BARBARO_IMAGES.forEach((source, index) => {
        this.loadSprite(source, "barbaroSprites", index);
      });
      Object.entries(WIZARD_IMAGE_SOURCES).forEach(([key, source]) => {
        this.loadNamedSprite(source, "wizardSprites", key);
      });
      Object.entries(CHEESE_SOLDIER_IMAGE_SOURCES).forEach(([key, source]) => {
        this.loadNamedSprite(source, "cheeseSoldierSprites", key);
      });
    },
    loadNamedSprite(source, collectionKey, spriteKey) {
      const image = new Image();
      image.onload = () => {
        this.$set(this[collectionKey], spriteKey, image);
      };
      image.src = source;
    },
    loadBertoAbilitySprite(powerId, source) {
      const image = new Image();
      image.onload = () => {
        this.$set(this.bertoAbilitySprites, powerId, image);
        this.prepareBertoAbilityFrames(powerId);
      };
      image.src = source;
    },
    loadSprite(source, key, index = null) {
      const image = new Image();
      image.onload = () => {
        if (index === null) {
          this[key] = image;
          if (key === "barbaroSprite") {
            this.prepareBarbaroFrames();
          }
          return;
        }
        this.$set(this[key], index, image);
        if (key === "barbaroSprites") {
          if (index === 0) {
            this.barbaroSprite = image;
          }
          this.prepareBarbaroFrames(index);
        }
      };
      image.src = source;
    },
    barbaroTierForUpgradeLevel(level) {
      if (level >= 16) {
        return 3;
      }
      if (level >= 11) {
        return 2;
      }
      if (level >= 6) {
        return 1;
      }
      return 0;
    },
    prepareBarbaroFrames(tier = 0) {
      const sprite = this.barbaroSprites[tier] || this.barbaroSprite;
      if (!sprite) {
        return;
      }

      const cache = {};
      Object.entries(BARBARO_FRAMES).forEach(([state, frames]) => {
        cache[state] = frames.map((frame) =>
          this.createCleanBarbaroFrame(frame, sprite, tier)
        );
      });
      this.$set(this.barbaroFrameCanvasesByTier, tier, cache);
      if (tier === 0) {
        this.barbaroFrameCanvases = cache;
      }

      const iconFrame = this.createBarbaroIconFrame(
        cache.idle && cache.idle[0]
      );
      if (iconFrame) {
        this.$set(
          this.barbaroIconImages,
          tier,
          iconFrame.canvas.toDataURL("image/png")
        );
        this.updateBarbaroIconForCurrentLevel();
      }
    },
    createBarbaroIconFrame(frame) {
      if (!frame || !frame.canvas) {
        return null;
      }

      const context = frame.canvas.getContext("2d");
      const imageData = context.getImageData(
        0,
        0,
        frame.canvas.width,
        frame.canvas.height
      );
      const data = imageData.data;
      let minX = frame.canvas.width;
      let minY = frame.canvas.height;
      let maxX = 0;
      let maxY = 0;

      for (let y = 0; y < frame.canvas.height; y += 1) {
        for (let x = 0; x < frame.canvas.width; x += 1) {
          if (data[(y * frame.canvas.width + x) * 4 + 3] > 8) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }

      if (minX > maxX || minY > maxY) {
        return frame;
      }

      const padding = 8;
      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(frame.canvas.width - 1, maxX + padding);
      maxY = Math.min(frame.canvas.height - 1, maxY + padding);

      const iconCanvas = document.createElement("canvas");
      iconCanvas.width = maxX - minX + 1;
      iconCanvas.height = maxY - minY + 1;
      iconCanvas
        .getContext("2d")
        .drawImage(
          frame.canvas,
          minX,
          minY,
          iconCanvas.width,
          iconCanvas.height,
          0,
          0,
          iconCanvas.width,
          iconCanvas.height
        );

      return {
        canvas: iconCanvas,
        width: iconCanvas.width,
        height: iconCanvas.height,
      };
    },
    updateBarbaroIconForCurrentLevel() {
      const tier = this.barbaroTierForUpgradeLevel(
        this.getUpgradeLevel("barbaroPower")
      );
      const icon = this.barbaroIconImages[tier] || this.barbaroIconImages[0];
      if (!icon) {
        return;
      }
      this.barbaroIconImage = icon;
      this.powers.forEach((power) => {
        if (power.id === "barbaro") {
          power.image = icon;
        }
      });
      this.upgrades.forEach((upgrade) => {
        if (upgrade.id === "barbaroPower") {
          upgrade.image = icon;
        }
      });
    },
    prepareBertoAbilityFrames(powerId) {
      const sprite = this.bertoAbilitySprites[powerId];
      const animation = BERTO_ABILITY_ANIMATIONS[powerId];
      if (!sprite || !animation) {
        return;
      }

      const frameWidth = sprite.width / animation.columns;
      const frameHeight = sprite.height / animation.rows;
      const frames = [];
      const frameRects =
        animation.frames ||
        Array.from(
          { length: animation.rows * animation.columns },
          (_, index) => {
            const row = Math.floor(index / animation.columns);
            const column = index % animation.columns;
            return {
              x: Math.floor(column * frameWidth),
              y: Math.floor(row * frameHeight),
              w: Math.ceil(frameWidth),
              h: Math.ceil(frameHeight),
            };
          }
        );

      frameRects.forEach((frameRect) => {
        const x = clamp(Math.floor(frameRect.x), 0, sprite.width - 1);
        const y = clamp(Math.floor(frameRect.y), 0, sprite.height - 1);
        frames.push(
          this.createCleanBertoAbilityFrame(sprite, {
            x,
            y,
            w: Math.min(Math.ceil(frameRect.w), sprite.width - x),
            h: Math.min(Math.ceil(frameRect.h), sprite.height - y),
          })
        );
      });

      if (!frames.length) {
        return;
      }
      this.$set(this.bertoAbilityFrames, powerId, frames);
    },
    createCleanBertoAbilityFrame(sprite, frameRect) {
      const sourceCanvas = document.createElement("canvas");
      sourceCanvas.width = frameRect.w;
      sourceCanvas.height = frameRect.h;
      const sourceContext = sourceCanvas.getContext("2d");
      sourceContext.drawImage(
        sprite,
        frameRect.x,
        frameRect.y,
        frameRect.w,
        frameRect.h,
        0,
        0,
        frameRect.w,
        frameRect.h
      );

      const imageData = sourceContext.getImageData(
        0,
        0,
        frameRect.w,
        frameRect.h
      );
      const data = imageData.data;
      const visited = new Uint8Array(frameRect.w * frameRect.h);
      const stack = [];
      const pushPixel = (x, y) => {
        if (x < 0 || y < 0 || x >= frameRect.w || y >= frameRect.h) {
          return;
        }
        const index = y * frameRect.w + x;
        if (visited[index]) {
          return;
        }
        const offset = index * 4;
        const red = data[offset];
        const green = data[offset + 1];
        const blue = data[offset + 2];
        const alpha = data[offset + 3];
        const channelSpread =
          Math.max(red, green, blue) - Math.min(red, green, blue);
        const isSheetBackground =
          alpha < 12 ||
          (red > 178 && green > 178 && blue > 178 && channelSpread < 64);
        if (!isSheetBackground) {
          return;
        }
        visited[index] = 1;
        stack.push([x, y]);
      };

      for (let x = 0; x < frameRect.w; x += 1) {
        pushPixel(x, 0);
        pushPixel(x, frameRect.h - 1);
      }
      for (let y = 0; y < frameRect.h; y += 1) {
        pushPixel(0, y);
        pushPixel(frameRect.w - 1, y);
      }

      while (stack.length) {
        const [x, y] = stack.pop();
        data[(y * frameRect.w + x) * 4 + 3] = 0;
        pushPixel(x + 1, y);
        pushPixel(x - 1, y);
        pushPixel(x, y + 1);
        pushPixel(x, y - 1);
      }

      let minX = frameRect.w;
      let minY = frameRect.h;
      let maxX = 0;
      let maxY = 0;
      for (let y = 0; y < frameRect.h; y += 1) {
        for (let x = 0; x < frameRect.w; x += 1) {
          if (data[(y * frameRect.w + x) * 4 + 3] > 8) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }

      sourceContext.putImageData(imageData, 0, 0);
      if (minX > maxX || minY > maxY) {
        return {
          canvas: sourceCanvas,
          width: frameRect.w,
          height: frameRect.h,
        };
      }

      const padding = 12;
      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(frameRect.w - 1, maxX + padding);
      maxY = Math.min(frameRect.h - 1, maxY + padding);

      const cleanCanvas = document.createElement("canvas");
      cleanCanvas.width = maxX - minX + 1;
      cleanCanvas.height = maxY - minY + 1;
      cleanCanvas
        .getContext("2d")
        .drawImage(
          sourceCanvas,
          minX,
          minY,
          cleanCanvas.width,
          cleanCanvas.height,
          0,
          0,
          cleanCanvas.width,
          cleanCanvas.height
        );

      return {
        canvas: cleanCanvas,
        width: cleanCanvas.width,
        height: cleanCanvas.height,
      };
    },
    barbaroFrameRect(frame, sprite, tier = 0) {
      const cellWidth = sprite.width / BARBARO_GRID.columns;
      const cellHeight = sprite.height / BARBARO_GRID.rows;
      const bleed = BARBARO_FRAME_BLEED_BY_TIER[tier] || 0;
      const x = Math.max(0, Math.floor(frame.column * cellWidth - bleed));
      const y = Math.max(0, Math.floor(frame.row * cellHeight - bleed));
      const right = Math.min(
        sprite.width,
        Math.ceil((frame.column + 1) * cellWidth + bleed)
      );
      const bottom = Math.min(
        sprite.height,
        Math.ceil((frame.row + 1) * cellHeight + bleed)
      );
      return {
        x,
        y,
        w: right - x,
        h: bottom - y,
      };
    },
    createCleanBarbaroFrame(frame, sprite = this.barbaroSprite, tier = 0) {
      const frameRect = this.barbaroFrameRect(frame, sprite, tier);
      const sourceCanvas = document.createElement("canvas");
      sourceCanvas.width = frameRect.w;
      sourceCanvas.height = frameRect.h;
      const sourceContext = sourceCanvas.getContext("2d");
      sourceContext.drawImage(
        sprite,
        frameRect.x,
        frameRect.y,
        frameRect.w,
        frameRect.h,
        0,
        0,
        frameRect.w,
        frameRect.h
      );

      const imageData = sourceContext.getImageData(
        0,
        0,
        frameRect.w,
        frameRect.h
      );
      const data = imageData.data;
      const visited = new Uint8Array(frameRect.w * frameRect.h);
      const stack = [];
      const pushPixel = (x, y) => {
        if (x < 0 || y < 0 || x >= frameRect.w || y >= frameRect.h) {
          return;
        }
        const index = y * frameRect.w + x;
        if (visited[index]) {
          return;
        }
        const offset = index * 4;
        const red = data[offset];
        const green = data[offset + 1];
        const blue = data[offset + 2];
        const alpha = data[offset + 3];
        const channelSpread =
          Math.max(red, green, blue) - Math.min(red, green, blue);
        const isSheetBackground =
          alpha < 12 ||
          (red > 168 && green > 168 && blue > 168 && channelSpread < 58);
        if (!isSheetBackground) {
          return;
        }
        visited[index] = 1;
        stack.push([x, y]);
      };

      for (let x = 0; x < frameRect.w; x += 1) {
        pushPixel(x, 0);
        pushPixel(x, frameRect.h - 1);
      }
      for (let y = 0; y < frameRect.h; y += 1) {
        pushPixel(0, y);
        pushPixel(frameRect.w - 1, y);
      }

      while (stack.length) {
        const [x, y] = stack.pop();
        const offset = (y * frameRect.w + x) * 4;
        data[offset + 3] = 0;
        pushPixel(x + 1, y);
        pushPixel(x - 1, y);
        pushPixel(x, y + 1);
        pushPixel(x, y - 1);
      }

      const componentLabels = new Int32Array(frameRect.w * frameRect.h);
      const componentSizes = [0];
      const componentBounds = [
        {
          minX: 0,
          minY: 0,
          maxX: 0,
          maxY: 0,
        },
      ];
      let bestComponent = 0;
      let bestSize = 0;
      let componentId = 0;
      const alphaAt = (x, y) => data[(y * frameRect.w + x) * 4 + 3];

      for (let y = 0; y < frameRect.h; y += 1) {
        for (let x = 0; x < frameRect.w; x += 1) {
          const startIndex = y * frameRect.w + x;
          if (componentLabels[startIndex] || alphaAt(x, y) <= 8) {
            continue;
          }

          componentId += 1;
          let componentSize = 0;
          const bounds = { minX: x, minY: y, maxX: x, maxY: y };
          const componentStack = [[x, y]];
          componentLabels[startIndex] = componentId;

          while (componentStack.length) {
            const [cx, cy] = componentStack.pop();
            componentSize += 1;
            bounds.minX = Math.min(bounds.minX, cx);
            bounds.minY = Math.min(bounds.minY, cy);
            bounds.maxX = Math.max(bounds.maxX, cx);
            bounds.maxY = Math.max(bounds.maxY, cy);
            [
              [cx + 1, cy],
              [cx - 1, cy],
              [cx, cy + 1],
              [cx, cy - 1],
              [cx + 1, cy + 1],
              [cx - 1, cy - 1],
              [cx + 1, cy - 1],
              [cx - 1, cy + 1],
            ].forEach(([nx, ny]) => {
              if (nx < 0 || ny < 0 || nx >= frameRect.w || ny >= frameRect.h) {
                return;
              }
              const nextIndex = ny * frameRect.w + nx;
              if (componentLabels[nextIndex] || alphaAt(nx, ny) <= 8) {
                return;
              }
              componentLabels[nextIndex] = componentId;
              componentStack.push([nx, ny]);
            });
          }

          componentSizes[componentId] = componentSize;
          componentBounds[componentId] = bounds;
          if (componentSize > bestSize) {
            bestSize = componentSize;
            bestComponent = componentId;
          }
        }
      }

      if (bestComponent) {
        const bestBounds = componentBounds[bestComponent];
        for (let index = 0; index < componentLabels.length; index += 1) {
          const label = componentLabels[index];
          if (!label || label === bestComponent) {
            continue;
          }
          const bounds = componentBounds[label];
          const closeToMain =
            bounds &&
            bestBounds &&
            bounds.maxX >= bestBounds.minX - 8 &&
            bounds.minX <= bestBounds.maxX + 8 &&
            bounds.maxY >= bestBounds.minY - 8 &&
            bounds.minY <= bestBounds.maxY + 8;
          if (!closeToMain) {
            data[index * 4 + 3] = 0;
          }
        }
      }

      let minX = frameRect.w;
      let minY = frameRect.h;
      let maxX = 0;
      let maxY = 0;
      for (let y = 0; y < frameRect.h; y += 1) {
        for (let x = 0; x < frameRect.w; x += 1) {
          const alpha = data[(y * frameRect.w + x) * 4 + 3];
          if (alpha > 8) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }

      sourceContext.putImageData(imageData, 0, 0);
      if (minX > maxX || minY > maxY) {
        return {
          canvas: sourceCanvas,
          width: frameRect.w,
          height: frameRect.h,
          offsetY: 0,
        };
      }

      const padding = 14;
      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(frameRect.w - 1, maxX + padding);
      maxY = Math.min(frameRect.h - 1, maxY + padding);

      const trimmedWidth = maxX - minX + 1;
      const trimmedHeight = maxY - minY + 1;
      const cleanCanvas = document.createElement("canvas");
      cleanCanvas.width = BARBARO_NORMALIZED_FRAME.width;
      cleanCanvas.height = BARBARO_NORMALIZED_FRAME.height;
      const cleanContext = cleanCanvas.getContext("2d");
      const fitScale = Math.min(
        1,
        (cleanCanvas.width - 20) / trimmedWidth,
        (cleanCanvas.height - 28) / trimmedHeight
      );
      const drawWidth = Math.round(trimmedWidth * fitScale);
      const drawHeight = Math.round(trimmedHeight * fitScale);
      const destinationX = Math.round((cleanCanvas.width - drawWidth) / 2);
      const destinationY = Math.round(
        (cleanCanvas.height - drawHeight) / 2 - cleanCanvas.height * 0.05
      );
      cleanContext.drawImage(
        sourceCanvas,
        minX,
        minY,
        trimmedWidth,
        trimmedHeight,
        destinationX,
        destinationY,
        drawWidth,
        drawHeight
      );

      return {
        canvas: cleanCanvas,
        width: cleanCanvas.width,
        height: cleanCanvas.height,
        offsetY: 0,
      };
    },
    setAdalbertoRouteChrome(active) {
      document.body.classList.toggle("adalberto-secret-route", active);
      document.documentElement.classList.toggle(
        "adalberto-secret-route",
        active
      );
    },
    setPageChromeHidden(hidden) {
      document.body.classList.toggle("adalberto-page-active", hidden);
      document.documentElement.classList.toggle(
        "adalberto-page-active",
        hidden
      );
    },
    checkPassword() {
      if (this.enteredPassword.trim().toLowerCase() === PASSWORD) {
        this.authenticated = true;
        this.errorMessage = "";
        return;
      }

      this.errorMessage = "Password errata. La fortezza resta sigillata.";
    },
    resizeCanvas() {
      const canvas = this.$refs.gameCanvas;
      const shell = this.$refs.gameShell;
      if (!canvas || !shell) {
        return;
      }

      const rect = shell.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      this.canvasSize = {
        width: rect.width,
        height: rect.height,
        ratio,
      };
      canvas.width = Math.floor(rect.width * ratio);
      canvas.height = Math.floor(rect.height * ratio);
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      const context = canvas.getContext("2d");
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      this.turret.x = Math.min(TURRET_OFFSET_X, rect.width * 0.18);
      this.turret.y = rect.height * 0.5;
    },
    startLoop() {
      if (this.animationFrameId) {
        return;
      }

      this.lastFrameAt = performance.now();
      this.animationFrameId = requestAnimationFrame(this.tick);
    },
    stopLoop() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      this.isFiring = false;
    },
    tick(now) {
      if (this.activeSection !== "play") {
        this.animationFrameId = null;
        return;
      }

      const delta = Math.min((now - this.lastFrameAt) / 1000, 0.05);
      this.lastFrameAt = now;

      if (!this.gameOver && !this.levelComplete && !this.gamePaused) {
        this.updateGame(delta, now);
      }

      this.drawGame();
      this.animationFrameId = requestAnimationFrame(this.tick);
    },
    updateGame(delta, now) {
      if (this.gameMode === "assault") {
        this.updateAssault(delta);
        this.updateEffects(delta);
        this.updateParticles(delta);
        return;
      }

      if (this.gameMode === "berto") {
        this.updateBerto(delta);
        this.updateEffects(delta);
        this.updateParticles(delta);
        return;
      }

      this.spawnTimer -= delta;

      if (this.spawnTimer <= 0) {
        if (this.levelKills < this.levelTarget) {
          const spawned = this.spawnEnemy();
          const pressure = clamp(this.wave * 0.006, 0, 0.42);
          const groupPause = clamp(spawned * 0.018, 0, 0.42);
          this.spawnTimer = randomBetween(1.05, 1.7) + groupPause - pressure;
        } else if (
          !this.bossSpawned &&
          (this.enemies.length === 0 ||
            (this.isBossLevel(this.wave) && this.wave >= 30))
        ) {
          if (this.isBossLevel(this.wave)) {
            this.spawnBossEnemy();
            this.spawnTimer = this.wave >= 30 ? randomBetween(1.45, 2.35) : 999;
          } else {
            this.advanceDefenseLevel();
            this.spawnTimer = 0.8;
          }
        } else if (this.bossActive && this.wave >= 30) {
          this.spawnBossSupportWave();
          this.spawnTimer =
            randomBetween(1.35, 2.4) - clamp(this.wave * 0.004, 0, 0.34);
        }
      }

      if (this.isFiring && now - this.lastShotAt > this.shotInterval) {
        this.fireArrow(now);
      }

      this.tickAbilityCooldowns(delta);
      this.regenerateCastleMana(delta);
      this.updateBertoCastle(delta);
      this.updateBertoAbilityAnimation(delta);

      this.updateGuidedArrowBursts(delta);
      this.updateArrows(delta);
      this.updateBarbaroAllies(delta);
      this.updateEnemies(delta);
      this.updateEnemyProjectiles(delta);
      this.castle.barrierTimer = Math.max(0, this.castle.barrierTimer - delta);
      this.castle.abilitiesDisabledTimer = Math.max(
        0,
        this.castle.abilitiesDisabledTimer - delta
      );
      this.updateEffects(delta);
      this.updateParticles(delta);

      if (
        this.bossSpawned &&
        this.levelKills >= this.levelTarget &&
        this.enemies.length === 0
      ) {
        this.advanceDefenseLevel();
      }

      if (this.castle.hp <= 0) {
        this.castle.hp = 0;
        this.levelDefeatQuote =
          LEVEL_DEFEAT_QUOTES[
            Math.floor(Math.random() * LEVEL_DEFEAT_QUOTES.length)
          ];
        this.gameOver = true;
        this.isFiring = false;
        this.storeOpen = false;
      }
    },
    tickAbilityCooldowns(delta) {
      [
        ...this.powers,
        ...this.assaultAbilities,
        ...this.bertoAbilities,
      ].forEach((ability) => {
        ability.cooldownLeft = Math.max(0, ability.cooldownLeft - delta);
      });
    },
    regenerateCastleMana(delta) {
      this.castle.mana = Math.min(
        this.castle.maxMana,
        this.castle.mana + this.manaRegenRate * delta
      );
    },
    resetBertoCastleIntro() {
      this.bertoCastleMode = "intro";
      this.bertoCastleFrame = "idle";
      this.bertoCastleTimer = 0;
    },
    updateBertoCastle(delta) {
      if (this.gameMode !== "defense") {
        return;
      }
      if (this.bertoCastleMode === "alert") {
        this.bertoCastleTimer = Math.max(0, this.bertoCastleTimer - delta);
        if (this.bertoCastleTimer <= 0) {
          this.bertoCastleMode = "sleep";
          this.bertoCastleFrame = "sleep";
        }
        return;
      }
      if (this.bertoCastleMode === "sleep") {
        this.bertoCastleFrame = "sleep";
        return;
      }

      this.bertoCastleTimer += delta;
      if (this.bertoCastleTimer < 2) {
        this.bertoCastleFrame = "idle";
      } else if (this.bertoCastleTimer < 4) {
        this.bertoCastleFrame = "wake";
      } else {
        this.bertoCastleMode = "sleep";
        this.bertoCastleFrame = "sleep";
      }
    },
    wakeBertoCastle() {
      if (this.gameMode !== "defense") {
        return;
      }
      this.bertoCastleMode = "alert";
      this.bertoCastleFrame = "angry";
      this.bertoCastleTimer = 1;
    },
    bertoAbilityAnimationKey(powerId, abilityLevel = null) {
      const level =
        abilityLevel === null || abilityLevel === undefined
          ? this.abilityUpgradeLevel(powerId)
          : abilityLevel;
      const enhancedKey = `${powerId}2`;
      if (level >= 15 && BERTO_ABILITY_ANIMATIONS[enhancedKey]) {
        return enhancedKey;
      }
      return powerId;
    },
    startBertoAbilityAnimation(powerId, abilityLevel = null) {
      let animationKey = this.bertoAbilityAnimationKey(powerId, abilityLevel);
      let animation = BERTO_ABILITY_ANIMATIONS[animationKey];
      let frames = this.bertoAbilityFrames[animationKey];
      if (
        (!animation || !frames || !frames.length) &&
        animationKey !== powerId
      ) {
        animationKey = powerId;
        animation = BERTO_ABILITY_ANIMATIONS[animationKey];
        frames = this.bertoAbilityFrames[animationKey];
      }
      if (!animation || !frames || !frames.length) {
        return 0;
      }
      this.activeBertoAbility = animationKey;
      this.bertoAbilityTimer = animation.duration;
      return animation.duration;
    },
    updateBertoAbilityAnimation(delta) {
      if (!this.activeBertoAbility) {
        this.updatePendingBertoAbilityEffects(delta);
        return;
      }
      this.bertoAbilityTimer = Math.max(0, this.bertoAbilityTimer - delta);
      if (this.bertoAbilityTimer <= 0) {
        this.activeBertoAbility = null;
      }
      this.updatePendingBertoAbilityEffects(delta);
    },
    queueBertoAbilityEffect(powerId, delay, payload = {}) {
      if (delay <= 0) {
        this.applyDelayedBertoAbilityEffect(powerId, payload);
        return;
      }
      this.pendingBertoAbilityEffects.push({
        powerId,
        timer: delay,
        payload,
      });
    },
    updatePendingBertoAbilityEffects(delta) {
      if (!this.pendingBertoAbilityEffects.length) {
        return;
      }
      const readyEffects = [];
      this.pendingBertoAbilityEffects.forEach((effect) => {
        effect.timer -= delta;
        if (effect.timer <= 0) {
          readyEffects.push(effect);
        }
      });
      this.pendingBertoAbilityEffects = this.pendingBertoAbilityEffects.filter(
        (effect) => effect.timer > 0
      );
      readyEffects.forEach((effect) => {
        this.applyDelayedBertoAbilityEffect(effect.powerId, effect.payload);
      });
    },
    applyDelayedBertoAbilityEffect(powerId, payload) {
      if (powerId === "sghiubbe") {
        this.applySghiubbeEffect(payload.effectiveLevel, payload.strength);
      }
      if (powerId === "kickboxing") {
        this.applyKickboxingEffect(payload.effectiveLevel, payload.strength);
      }
    },
    bestiaryEnemyCatalog() {
      const metaByName = {
        "Gorgonzola Raider": {
          unlockLevel: 1,
          role: "Assaltatore",
          technical:
            "Unita frontale equilibrata: avanza verso le mura e colpisce in mischia.",
          lore: "Il soldato semplice della crosta blu. Non brilla, ma quando arriva in gruppo sa diventare un problema molto concreto.",
        },
        "Brie Fantasma": {
          unlockLevel: 1,
          role: "Rapido",
          technical:
            "Ha pochi HP, ma cammina piu veloce dei raider e prova a consumare le mura con attacchi frequenti.",
          lore: "Scivola nella nebbia come se il campo gli dovesse dei soldi. La sua specialita e farsi sottovalutare.",
        },
        "Caciocavallo Pesante": {
          unlockLevel: 1,
          role: "Tank",
          technical:
            "Lento e resistente, infligge danni alti se riesce ad arrivare sotto il castello.",
          lore: "Una ruota corazzata con pessime maniere. Nelle fila nemiche viene mandato avanti quando serve aprire una breccia.",
        },
        "Provolone Runner": {
          unlockLevel: 1,
          role: "Incursore",
          technical:
            "Molto veloce, fragile, pensato per superare la mira e costringerti a cambiare bersaglio.",
          lore: "Corre perche qualcuno gli ha detto che il coraggio e una questione di accelerazione. A volte funziona.",
        },
        "Arciere di Emmental": {
          unlockLevel: 11,
          role: "Ranged",
          technical:
            "Si ferma lontano dalle mura e tira proiettili. Va eliminato prima che accumuli troppi colpi gratis.",
          lore: "Si addestra bucando bersagli e formaggi rivali. Dice di avere mira nobile, ma combatte da molto lontano.",
        },
        "Cecchino Taleggio": {
          unlockLevel: 11,
          role: "Ranged",
          technical:
            "Meno resistente, ma piu pericoloso a distanza: colpi pesanti e traiettorie rapide.",
          lore: "Un professionista dell'odore e della pazienza. Aspetta, tira, si lamenta del vento, poi tira ancora.",
        },
        "Orco di Groviera": {
          unlockLevel: 21,
          role: "Elite",
          technical:
            "Moltiplica molto gli HP e spesso arriva con scorta. Richiede danno sostenuto o controllo.",
          lore: "Non e un formaggio, e un reparto d'assedio con le gambe. I piccoli lo seguono perche dietro di lui piove meno.",
        },
        "Catapulta di Fonduta": {
          unlockLevel: 31,
          role: "Artiglieria",
          technical:
            "Resta nelle retrovie e lancia colpi pesanti. Lenta, resistente e molto dannosa se ignorata.",
          lore: "La diplomazia della Fonduta: prima ti scrivono, poi ti lanciano una pentola bollente sopra le mura.",
        },
        "Spadaccino Stracchino": {
          unlockLevel: 41,
          role: "Duellante",
          technical:
            "Unita molto veloce grazie al moltiplicatore di movimento. Punisce le difese troppo lente.",
          lore: "Fa scena, sparisce, riappare, inciampa raramente. Nel suo clan la modestia e considerata una malattia.",
        },
        "Drago di Formaggio": {
          unlockLevel: 51,
          role: "Aereo",
          technical:
            "Alterna colpi a distanza e picchiate sulle mura. Ha molti HP e danni alti.",
          lore: "Nato in una grotta troppo calda e cresciuto con troppa autostima. Il regno caseario lo usa come minaccia volante.",
        },
        "Stregone Assassino": {
          unlockLevel: 61,
          role: "Mago",
          technical:
            "Attacca da lontano e puo bloccare temporaneamente le abilita del castello con il malocchio.",
          lore: "Non conquista: maledice. Secondo lui il formaggio dovrebbe governare per diritto mistico e pessimo carattere.",
        },
      };

      const seen = new Set();
      const regularEntries = this.defenseEnemyTypesForLevel(100)
        .filter((type) => {
          if (seen.has(type.name)) {
            return false;
          }
          seen.add(type.name);
          return true;
        })
        .map((type) => ({
          ...type,
          id: type.name.toLowerCase().replace(/\s+/g, "-"),
          ...(metaByName[type.name] || {}),
        }));

      return [
        ...regularEntries,
        {
          id: "duca-caseario",
          archetype: "golemBoss",
          name: "Golem Fuso",
          color: "#69e7ff",
          rind: "#4b4b54",
          hp: 72,
          speed: 26,
          damage: 36,
          reward: 100,
          radius: 52,
          unlockLevel: 10,
          role: "Boss",
          technical:
            "Boss a doppia distanza: lancia rocce da lontano, avanza, colpisce col laser e poi arretra.",
          lore: "Un ammasso di pietra e fonduta animata. Ogni passo sa di miniera crollata e formaggio troppo caldo.",
        },
        {
          id: "gran-re-fonduta",
          archetype: "superboss",
          name: "Gran Re della Fonduta",
          color: "#ff8f5a",
          rind: "#6c241c",
          hp: 72,
          speed: 18,
          damage: 58,
          reward: 1000,
          radius: 56,
          unlockLevel: 100,
          role: "Superboss",
          technical:
            "Il nemico finale: HP enormi, danno altissimo e presenza da fine campagna.",
          lore: "Siede su un trono caldo, parla in bolle e considera il mondo una ricetta incompleta.",
        },
      ].sort(
        (a, b) => a.unlockLevel - b.unlockLevel || a.name.localeCompare(b.name)
      );
    },
    decorateBestiaryEntry(entry, unlockedLevel) {
      const statLevel =
        entry.archetype === "boss" || entry.archetype === "golemBoss"
          ? Math.max(10, Math.floor(unlockedLevel / 10) * 10)
          : clamp(unlockedLevel, entry.unlockLevel, 100);
      const stats = this.bestiaryStatsForEntry(entry, statLevel);

      return {
        ...entry,
        spritePreview:
          entry.spriteSet === "cheeseSoldier"
            ? {
                backgroundImage: `url(${cheeseSoldierIdleImage})`,
                "--preview-width": "112px",
                "--preview-height": "112px",
                "--preview-size": "670px 214px",
                "--preview-position": "-23px -52px",
              }
            : entry.archetype === "wizard"
            ? {
                backgroundImage: `url(${wizardIdleImage})`,
                "--preview-width": "112px",
                "--preview-height": "112px",
                "--preview-size": "1800px 225px",
                "--preview-position": "-58px -58px",
              }
            : entry.archetype === "golemBoss"
            ? {
                backgroundImage: `url(${golemBossSheetImage})`,
                "--preview-width": "112px",
                "--preview-height": "112px",
                "--preview-size": "1250px 1250px",
                "--preview-position": "-7px -8px",
              }
            : null,
        image:
          entry.spriteSet === "cheeseSoldier"
            ? null
            : entry.archetype === "wizard"
            ? null
            : entry.archetype === "golemBoss"
            ? null
            : null,
        statLevel,
        baseStats: {
          hp: Math.round(entry.hp),
          damage: Math.round(entry.damage),
          speed: Math.round(entry.speed),
          reward: Math.round(entry.reward),
        },
        stats: [
          { label: "HP", value: Math.round(stats.hp) },
          { label: "Danno", value: stats.damage.toFixed(1) },
          { label: "Velocita", value: Math.round(stats.speed) },
          { label: "Taglia in oro", value: Math.round(stats.reward) },
        ],
      };
    },
    bestiaryStatsForEntry(entry, level) {
      const hpScale = this.defenseHpScaleForLevel(level);
      const damageScale = this.defenseDamageScaleForLevel(level);
      if (entry.archetype === "superboss") {
        const strongCheeseHp = 72 * hpScale * 1.16;
        return {
          hp: strongCheeseHp * 50,
          damage: 58 * damageScale,
          speed: 18 * 1.75,
          reward: 1000,
        };
      }
      if (entry.archetype === "boss" || entry.archetype === "golemBoss") {
        const strongCheeseHp = 72 * hpScale * 1.16;
        return {
          hp: strongCheeseHp * 20,
          damage: 36 * damageScale,
          speed: 24 * 1.75,
          reward: 100,
        };
      }

      const strength = entry.strengthMultiplier || 1;
      const speedMultiplier = entry.speedMultiplier || 1;
      return {
        hp: entry.hp * hpScale * strength * 1.16,
        damage: entry.damage * damageScale * Math.sqrt(strength),
        speed:
          (entry.speed + Math.floor((Math.max(1, level) - 1) / 10) * 2.2) *
          speedMultiplier *
          1.75,
        reward: entry.reward,
      };
    },
    defenseTargetForLevel(level) {
      const baseTarget =
        7 + Math.floor(level * 1.1) + Math.floor((level - 1) / 10) * 3;
      return Math.min(360, baseTarget * 5);
    },
    defenseTierForLevel(level) {
      return clamp(Math.floor((Math.max(1, level) - 1) / 10), 0, 9);
    },
    defenseHpScaleForLevel(level) {
      return 1 + this.defenseTierForLevel(level) * 0.4;
    },
    defenseDamageScaleForLevel(level) {
      return 1 + this.defenseTierForLevel(level) * 0.2;
    },
    isBossLevel(level) {
      return (
        level === 100 ||
        (level >= 10 && level < 100 && level % 10 === 0) ||
        (level >= 91 && level <= 99)
      );
    },
    advanceDefenseLevel() {
      this.completedLevel = this.wave;
      this.completedLevelGoldEarned = this.levelGoldEarned;
      this.completedCastleFlawlessBonus = this.castleDamagedThisLevel
        ? 0
        : this.wave * 100;
      this.gold += this.completedCastleFlawlessBonus;
      this.levelCompleteQuote =
        LEVEL_COMPLETE_QUOTES[
          Math.floor(Math.random() * LEVEL_COMPLETE_QUOTES.length)
        ];
      const nextUnlockedLevel =
        this.wave >= 100 ? this.wave : Math.min(100, this.wave + 1);
      this.defenseCurrentLevel = Math.max(
        this.defenseCurrentLevel,
        nextUnlockedLevel
      );
      this.updateBarbaroIconForCurrentLevel();
      this.levelComplete = true;
      this.isFiring = false;
      this.storeOpen = false;
      this.bossActive = false;
      this.message =
        this.wave >= 100
          ? "Adalberto ha completato i 100 livelli della Fortezza Sghiubba."
          : `Livello ${this.wave} completato.`;
    },
    updateAssault(delta) {
      this.waveTimer += delta;
      this.tickAbilityCooldowns(delta);

      this.assaultBase.shotTimer -= delta;
      this.assaultBase.lavaTimer -= delta;
      if (this.assaultBase.shotTimer <= 0) {
        this.assaultBase.shotTimer = Math.max(0.55, 1.25 - this.wave * 0.03);
        const target = this.assaultUnits[0];
        if (target) {
          const angle = Math.atan2(
            target.y - this.canvasSize.height * 0.5,
            target.x - (this.canvasSize.width - 92)
          );
          this.cheeseProjectiles.push({
            x: this.canvasSize.width - 92,
            y: this.canvasSize.height * 0.5,
            vx: Math.cos(angle) * 310,
            vy: Math.sin(angle) * 310,
            damage: 14 + this.wave,
            radius: 7,
          });
        }
      }

      this.assaultUnits.forEach((unit) => {
        unit.x += unit.speed * delta;
        unit.attackTimer -= delta;

        const lavaX = this.canvasSize.width * 0.73;
        if (Math.abs(unit.x - lavaX) < 18) {
          unit.hp -= (10 + this.wave) * delta;
          unit.speed = Math.max(unit.baseSpeed * 0.56, unit.speed - 8 * delta);
        }

        if (unit.x > this.canvasSize.width - 155 && unit.attackTimer <= 0) {
          unit.attackTimer = unit.attackRate;
          this.assaultBase.hp -= unit.damage;
          this.addImpact(this.canvasSize.width - 130, unit.y, unit.color);
        }
      });

      this.cheeseProjectiles.forEach((shot) => {
        shot.x += shot.vx * delta;
        shot.y += shot.vy * delta;
      });

      this.cheeseProjectiles = this.cheeseProjectiles.filter((shot) => {
        const hit = this.assaultUnits.find(
          (unit) =>
            Math.hypot(unit.x - shot.x, unit.y - shot.y) <
            unit.radius + shot.radius
        );
        if (hit) {
          hit.hp -= shot.damage;
          this.addImpact(shot.x, shot.y, "#f6df7a");
          return false;
        }
        return (
          shot.x > -40 && shot.y > -40 && shot.y < this.canvasSize.height + 40
        );
      });

      this.assaultUnits = this.assaultUnits.filter((unit) => unit.hp > 0);

      if (this.assaultBase.hp <= 0) {
        this.assaultBase.hp = 0;
        this.gold += 60 + this.wave * 8;
        this.score += 1;
        this.wave += 1;
        this.message =
          "Base casearia conquistata. Nuovo avamposto da assaltare.";
        this.resetAssaultField();
      }
    },
    updateBerto(delta) {
      this.waveTimer += delta;
      this.bertoSpawnTimer -= delta;
      this.tickAbilityCooldowns(delta);

      const dx = this.berto.targetX - this.berto.x;
      const dy = this.berto.targetY - this.berto.y;
      const length = Math.hypot(dx, dy);
      if (length > 4) {
        const speed = 260;
        this.berto.x += (dx / length) * speed * delta;
        this.berto.y += (dy / length) * speed * delta;
      }

      if (this.bertoSpawnTimer <= 0) {
        this.bertoSpawnTimer = Math.max(0.65, 1.35 - this.wave * 0.04);
        this.bertoEnemies.push({
          x: randomBetween(140, this.canvasSize.width - 100),
          y: randomBetween(100, this.canvasSize.height - 80),
          hp: 42 + this.wave * 5,
          radius: 20,
          speed: randomBetween(55, 88) + this.wave,
          damageTimer: 0,
          color: "#f6df7a",
        });
      }

      this.bertoEnemies.forEach((enemy) => {
        const angle = Math.atan2(
          this.berto.y - enemy.y,
          this.berto.x - enemy.x
        );
        enemy.x += Math.cos(angle) * enemy.speed * delta;
        enemy.y += Math.sin(angle) * enemy.speed * delta;
        enemy.damageTimer -= delta;
        if (
          Math.hypot(enemy.x - this.berto.x, enemy.y - this.berto.y) <
            enemy.radius + 20 &&
          enemy.damageTimer <= 0
        ) {
          enemy.damageTimer = 0.75;
          this.berto.hp -= 9 + this.wave;
          this.addImpact(this.berto.x, this.berto.y, "#ff8f5a");
        }
      });

      const before = this.bertoEnemies.length;
      this.bertoEnemies = this.bertoEnemies.filter((enemy) => enemy.hp > 0);
      const defeated = before - this.bertoEnemies.length;
      if (defeated > 0) {
        this.score += defeated;
        this.gold += defeated * 6;
      }

      if (this.score > 0 && this.score % 18 === 0) {
        this.wave = Math.max(this.wave, Math.floor(this.score / 18) + 1);
      }

      if (this.berto.hp <= 0) {
        this.berto.hp = 0;
        this.levelDefeatQuote =
          LEVEL_DEFEAT_QUOTES[
            Math.floor(Math.random() * LEVEL_DEFEAT_QUOTES.length)
          ];
        this.gameOver = true;
        this.message = "Berto e' finito nella fonduta. Serve un altro giro.";
      }
    },
    defenseEnemyTypesForLevel(level) {
      const walkers = [
        {
          archetype: "walker",
          name: "Gorgonzola Raider",
          spriteSet: "cheeseSoldier",
          color: "#78c6ff",
          rind: "#214f72",
          hp: 44,
          speed: 44,
          damage: 11,
          reward: 10,
          radius: 22,
        },
        {
          archetype: "walker",
          name: "Brie Fantasma",
          color: "#fff1c7",
          rind: "#d9b66c",
          hp: 38,
          speed: 58,
          damage: 8,
          reward: 5,
          radius: 19,
        },
        {
          archetype: "walker",
          name: "Caciocavallo Pesante",
          color: "#f6ba47",
          rind: "#9e5b22",
          hp: 72,
          speed: 34,
          damage: 17,
          reward: 14,
          radius: 26,
        },
        {
          archetype: "walker",
          name: "Provolone Runner",
          color: "#f6df7a",
          rind: "#bf8f2f",
          hp: 32,
          speed: 72,
          damage: 7,
          reward: 4,
          radius: 17,
        },
      ];
      const pool = [...walkers];
      if (level >= 11) {
        pool.push(
          {
            archetype: "archer",
            name: "Arciere di Emmental",
            color: "#ffe18b",
            rind: "#9c7728",
            hp: 28,
            speed: 36,
            damage: 15,
            reward: 8,
            radius: 18,
            rangeStop: 520,
            attackRate: 1.55,
            projectileSpeed: 360,
          },
          {
            archetype: "archer",
            name: "Cecchino Taleggio",
            color: "#ffd46a",
            rind: "#805328",
            hp: 24,
            speed: 42,
            damage: 18,
            reward: 12,
            radius: 17,
            rangeStop: 580,
            attackRate: 1.8,
            projectileSpeed: 390,
          }
        );
      }
      if (level >= 21) {
        pool.push({
          archetype: "orc",
          name: "Orco di Groviera",
          color: "#d5a642",
          rind: "#5a3d22",
          hp: 60,
          speed: 27,
          damage: 15,
          reward: 25,
          radius: 36,
          strengthMultiplier: 5,
          escortChance: 0.58,
        });
      }
      if (level >= 31) {
        pool.push({
          archetype: "catapult",
          name: "Catapulta di Fonduta",
          color: "#c89437",
          rind: "#4d3320",
          hp: 120,
          speed: 18,
          damage: 32,
          reward: 35,
          radius: 40,
          rangeStop: 690,
          attackRate: 3.1,
          projectileSpeed: 250,
          strengthMultiplier: 2.8,
        });
      }
      if (level >= 41) {
        pool.push({
          archetype: "ninja",
          name: "Spadaccino Stracchino",
          color: "#e9ecf0",
          rind: "#2f3443",
          hp: 38,
          speed: 58,
          damage: 10,
          reward: 20,
          radius: 18,
          speedMultiplier: 5,
        });
      }
      if (level >= 51) {
        pool.push({
          archetype: "dragon",
          name: "Drago di Formaggio",
          color: "#ffcf5a",
          rind: "#7a3323",
          hp: 160,
          speed: 38,
          damage: 30,
          reward: 60,
          radius: 42,
          rangeStop: 650,
          attackRate: 2.25,
          projectileSpeed: 430,
          strengthMultiplier: 3.6,
          diveRate: 5.6,
          diveSpeed: 420,
        });
      }
      if (level >= 61) {
        pool.push({
          archetype: "wizard",
          name: "Stregone Assassino",
          color: "#151015",
          rind: "#050407",
          hp: 72,
          speed: 34,
          damage: 5,
          reward: 40,
          radius: 25,
          rangeStopRatio: 0.76,
          attackRate: 4.4,
          projectileSpeed: 360,
          strengthMultiplier: 1.65,
        });
      }

      return pool;
    },
    spawnEnemy() {
      const types = this.defenseEnemyTypesForLevel(this.wave);
      const type = this.chooseDefenseSpawnType(types);
      const groupSize = this.spawnGroupSizeForType(type);
      const baseY = randomBetween(92, this.canvasSize.height - 80);
      const ySpread = Math.min(170, 34 + groupSize * 5.5);
      let spawned = 0;

      for (let index = 0; index < groupSize; index += 1) {
        const forcedY = clamp(
          baseY + randomBetween(-ySpread, ySpread),
          72,
          this.canvasSize.height - 58
        );
        this.spawnDefenseEnemy(type, forcedY, index * randomBetween(20, 46));
        spawned += 1;
      }

      if (type.archetype === "orc" && Math.random() < type.escortChance) {
        const escorts = Math.min(5, 1 + Math.floor(this.wave / 20));
        const walkers = this.defenseEnemyTypesForLevel(20).filter(
          (item) => item.archetype === "walker"
        );
        for (let index = 0; index < escorts; index += 1) {
          const escortType =
            walkers[Math.floor(Math.random() * walkers.length)];
          this.spawnDefenseEnemy(
            escortType,
            baseY + randomBetween(-54, 54),
            groupSize * 28 + randomBetween(22, 96)
          );
          spawned += 1;
        }
      }

      return spawned;
    },
    chooseDefenseSpawnType(types) {
      const level = this.wave;
      const weighted = [];
      types.forEach((type) => {
        let weight = 1;
        if (type.archetype === "walker") {
          weight = level < 11 ? 12 : level < 31 ? 9 : 7;
        } else if (type.archetype === "archer") {
          weight = level >= 40 ? 8 : 3;
        } else if (type.archetype === "orc") {
          weight = level >= 50 ? 3 : 2;
        } else if (type.archetype === "catapult") {
          weight = level >= 60 ? 2.6 : 1.6;
        } else if (type.archetype === "ninja") {
          weight = level >= 70 ? 3.2 : 2.2;
        } else if (type.archetype === "dragon") {
          weight = level >= 80 ? 2.2 : 1.2;
        } else if (type.archetype === "wizard") {
          weight = level >= 80 ? 1.8 : 1;
        }
        for (let index = 0; index < Math.round(weight * 2); index += 1) {
          weighted.push(type);
        }
      });
      return weighted[Math.floor(Math.random() * weighted.length)] || types[0];
    },
    spawnGroupSizeForType(type) {
      const level = this.wave;
      const tier = Math.floor((Math.max(1, level) - 1) / 10);
      if (type.archetype === "walker") {
        if (level <= 2) {
          return Math.floor(randomBetween(1, 2.99));
        }
        if (level <= 5) {
          return Math.floor(randomBetween(2, 4.99));
        }
        return Math.floor(randomBetween(4 + tier * 2, 8 + tier * 5));
      }
      if (type.archetype === "archer") {
        if (level < 30) {
          return Math.floor(randomBetween(1, 3.99));
        }
        if (level < 40) {
          return Math.floor(randomBetween(4, 9.99));
        }
        return Math.floor(randomBetween(10 + tier, 18 + tier * 2.2));
      }
      if (type.archetype === "orc") {
        return Math.floor(randomBetween(1, Math.min(4, 2 + tier * 0.35)));
      }
      if (type.archetype === "catapult") {
        return Math.floor(randomBetween(1, Math.min(4, 2 + tier * 0.28)));
      }
      if (type.archetype === "ninja") {
        return Math.floor(randomBetween(2, Math.min(10, 4 + tier)));
      }
      if (type.archetype === "dragon") {
        return Math.floor(randomBetween(1, Math.min(4, 2 + tier * 0.25)));
      }
      if (type.archetype === "wizard") {
        return Math.floor(randomBetween(1, Math.min(5, 2 + tier * 0.35)));
      }
      return 1;
    },
    spawnDefenseEnemy(type, forcedY, xOffset = 0) {
      const hpScale = this.defenseHpScaleForLevel(this.wave);
      const damageScale = this.defenseDamageScaleForLevel(this.wave);
      const strength = type.strengthMultiplier || 1;
      const speedMultiplier = type.speedMultiplier || 1;
      const hp = type.hp * hpScale * strength * 1.16;
      const damage = type.damage * damageScale * Math.sqrt(strength);
      const y = clamp(
        forcedY || randomBetween(92, this.canvasSize.height - 80),
        72,
        this.canvasSize.height - 58
      );
      const enemy = {
        ...type,
        id: Date.now() + Math.random(),
        x: this.canvasSize.width + 50 + xOffset,
        y,
        hp,
        maxHp: hp,
        speed:
          (type.speed + Math.floor((Math.max(1, this.wave) - 1) / 10) * 2.2) *
          speedMultiplier *
          1.75,
        damage,
        reward: type.reward,
        slow: 0,
        attackTimer: randomBetween(0.2, 1.15),
        diveTimer: type.diveRate ? randomBetween(2, type.diveRate) : 0,
        diving: false,
        retreating: false,
        homeX: this.enemyRangedStopX(type),
        wobble: Math.random() * Math.PI * 2,
        animTime: Math.random() * 2,
        attackAnimTimer: 0,
        hitAnimTimer: 0,
        attackKind: null,
        deathTimer: 0,
        defeated: false,
        isBoss: false,
      };
      this.enemies.push(enemy);
      return enemy;
    },
    spawnBossEnemy() {
      const isSuperBoss = this.wave === 100;
      const hpScale = this.defenseHpScaleForLevel(this.wave);
      const damageScale = this.defenseDamageScaleForLevel(this.wave);
      const strongCheeseHp = 72 * hpScale * 1.16;
      const hp = strongCheeseHp * (isSuperBoss ? 50 : 20);
      const bossName = isSuperBoss
        ? "Gran Re della Fonduta"
        : `Golem Fuso Lv. ${this.wave}`;
      const bossCount = 1;
      this.bossSpawned = true;
      this.bossActive = true;
      for (let index = 0; index < bossCount; index += 1) {
        this.enemies.push({
          id: Date.now() + Math.random(),
          name: bossCount > 1 ? `${bossName} ${index + 1}` : bossName,
          color: isSuperBoss ? "#ff8f5a" : "#69e7ff",
          rind: isSuperBoss ? "#6c241c" : "#4b4b54",
          x: this.canvasSize.width + 110 + index * 78,
          y:
            bossCount === 1
              ? this.canvasSize.height * 0.5
              : this.canvasSize.height * (0.34 + index * 0.16),
          hp,
          maxHp: hp,
          speed: (isSuperBoss ? 18 : 54) * 1.75,
          damage: (isSuperBoss ? 58 : 36) * damageScale,
          reward: isSuperBoss ? 1000 : 100,
          radius: isSuperBoss ? 56 : 52,
          slow: 0,
          attackTimer: isSuperBoss ? 0 : randomBetween(0.8, 1.35),
          wobble: Math.random() * Math.PI * 2,
          isBoss: true,
          isSuperBoss,
          archetype: isSuperBoss ? "superboss" : "golemBoss",
          golemPhase: "rock",
          golemRockShots: 0,
          laserTimer: 0,
          laserEffectTimer: 0,
          attackAnimTimer: 0,
          hitAnimTimer: 0,
          animTime: Math.random() * 2,
        });
      }
      if (this.wave >= 30) {
        this.spawnBossSupportUnits();
      }
      this.message = isSuperBoss
        ? "SUPERBOSS: il Gran Re della Fonduta entra in campo."
        : `BOSS: ${bossName} guida l'assalto.`;
    },
    spawnBossSupportUnits() {
      const waves = this.wave >= 70 ? 3 : 2;
      for (let index = 0; index < waves; index += 1) {
        this.spawnBossSupportWave(true);
      }
    },
    spawnBossSupportWave(initial = false) {
      const regularEnemies = this.enemies.filter((enemy) => !enemy.isBoss);
      const supportCap = Math.min(80, 14 + Math.floor(this.wave * 0.55));
      if (!initial && regularEnemies.length >= supportCap) {
        return 0;
      }

      const supportPool = this.defenseEnemyTypesForLevel(this.wave).filter(
        (type) =>
          type.archetype !== "dragon" || this.wave >= 60 || Math.random() < 0.22
      );
      const type = this.chooseDefenseSpawnType(supportPool);
      const groupSize = Math.min(
        this.spawnGroupSizeForType(type),
        Math.max(1, supportCap - regularEnemies.length)
      );
      const baseY = randomBetween(92, this.canvasSize.height - 80);
      const ySpread = Math.min(170, 42 + groupSize * 5);

      for (let index = 0; index < groupSize; index += 1) {
        this.spawnDefenseEnemy(
          type,
          clamp(
            baseY + randomBetween(-ySpread, ySpread),
            72,
            this.canvasSize.height - 58
          ),
          120 + index * randomBetween(22, 48)
        );
      }

      return groupSize;
    },
    fireArrow(now) {
      const angle = Math.atan2(
        this.pointer.y - this.turret.y,
        this.pointer.x - this.turret.x
      );
      const count = this.arrowCount;
      const spread = count === 1 ? 0 : MULTI_ARROW_STEP;
      this.lastShotAt = now;
      for (let index = 0; index < count; index += 1) {
        const offset = index - (count - 1) / 2;
        const arrowAngle = angle + offset * spread;
        this.arrows.push({
          x: this.turret.x + Math.cos(arrowAngle) * 34,
          y: this.turret.y + Math.sin(arrowAngle) * 34,
          vx: Math.cos(arrowAngle) * ARROW_SPEED,
          vy: Math.sin(arrowAngle) * ARROW_SPEED,
          angle: arrowAngle,
          damage: 20 + this.damageBonus,
          traveled: 0,
          maxRange: this.arrowMaxRange,
          guided: false,
        });
      }
    },
    guidedArrowCountForLevel(level) {
      return Math.round(7 + clamp(Math.floor(level), 0, 20) * 4.65);
    },
    startGuidedArrowBurst(abilityLevel, effectiveLevel) {
      const total = this.guidedArrowCountForLevel(abilityLevel);
      this.guidedArrowBursts.push({
        total,
        remaining: total,
        fired: 0,
        timer: 0,
        interval: clamp(1.45 / total, 0.012, 0.045),
        effectiveLevel,
      });
    },
    updateGuidedArrowBursts(delta) {
      if (!this.guidedArrowBursts.length) {
        return;
      }

      this.guidedArrowBursts.forEach((burst) => {
        burst.timer -= delta;
        let firedThisFrame = 0;
        while (burst.remaining > 0 && burst.timer <= 0 && firedThisFrame < 8) {
          this.fireGuidedBurstArrow(burst);
          burst.remaining -= 1;
          burst.fired += 1;
          burst.timer += burst.interval;
          firedThisFrame += 1;
        }
      });

      this.guidedArrowBursts = this.guidedArrowBursts.filter(
        (burst) => burst.remaining > 0
      );
    },
    fireGuidedBurstArrow(burst) {
      const liveEnemies = this.enemies
        .filter((enemy) => enemy.hp > 0 && !enemy.defeated)
        .sort(
          (first, second) =>
            distance(this.turret, first) - distance(this.turret, second)
        );
      const target = liveEnemies.length
        ? liveEnemies[burst.fired % liveEnemies.length]
        : null;
      const baseAngle = target
        ? Math.atan2(target.y - this.turret.y, target.x - this.turret.x)
        : randomBetween(-0.36, 0.36);
      const laneOffset = (burst.fired % 11) - 5;
      const angle = baseAngle + randomBetween(-0.16, 0.16);
      const y = target
        ? target.y
        : randomBetween(120, this.canvasSize.height - 90);

      this.arrows.push({
        x: this.turret.x + Math.cos(angle) * 34,
        y: this.turret.y + Math.sin(angle) * 34 + laneOffset * 3.8,
        vx: Math.cos(angle) * GUIDED_ARROW_SPEED,
        vy: Math.sin(angle) * GUIDED_ARROW_SPEED + (y - this.turret.y) * 0.05,
        angle,
        damage: 20 + this.damageBonus * 0.8 + burst.effectiveLevel * 3.4,
        traveled: 0,
        maxRange: this.arrowMaxRange * (1.15 + burst.effectiveLevel * 0.015),
        guided: true,
        targetId: target ? target.id : null,
      });
    },
    updateArrows(delta) {
      this.arrows.forEach((arrow) => {
        if (arrow.guided) {
          const currentTarget =
            arrow.targetId === null || arrow.targetId === undefined
              ? null
              : this.enemies.find(
                  (enemy) =>
                    enemy.id === arrow.targetId &&
                    enemy.hp > 0 &&
                    !enemy.defeated
                );
          const closestTarget = this.findClosestEnemy(arrow);
          const target =
            currentTarget && closestTarget
              ? distance(arrow, closestTarget) <
                distance(arrow, currentTarget) * 0.72
                ? closestTarget
                : currentTarget
              : currentTarget || closestTarget;
          if (target) {
            arrow.targetId = target.id;
            const angle = Math.atan2(target.y - arrow.y, target.x - arrow.x);
            const targetDistance = Math.max(1, distance(arrow, target));
            const speed = Math.max(
              GUIDED_ARROW_SPEED,
              Math.hypot(arrow.vx, arrow.vy)
            );
            const steer = clamp(
              0.28 + (260 - targetDistance) / 520,
              0.28,
              0.62
            );
            arrow.vx = arrow.vx * (1 - steer) + Math.cos(angle) * speed * steer;
            arrow.vy = arrow.vy * (1 - steer) + Math.sin(angle) * speed * steer;
            arrow.angle = Math.atan2(arrow.vy, arrow.vx);
            if (targetDistance < target.radius + 22) {
              arrow.forceHitId = target.id;
            }
          }
        }

        const moveX = arrow.vx * delta;
        const moveY = arrow.vy * delta;
        arrow.x += moveX;
        arrow.y += moveY;
        arrow.traveled += Math.hypot(moveX, moveY);
      });

      this.arrows = this.arrows.filter((arrow) => {
        if (
          arrow.traveled > arrow.maxRange ||
          arrow.x > this.canvasSize.width + 180 ||
          arrow.y < -60 ||
          arrow.y > this.canvasSize.height + 60
        ) {
          return false;
        }

        const hit = this.enemies.find(
          (enemy) =>
            enemy.hp > 0 &&
            !enemy.defeated &&
            (arrow.forceHitId === enemy.id || this.arrowHitsEnemy(arrow, enemy))
        );
        if (hit) {
          this.damageEnemy(hit, arrow.damage);
          this.addImpact(arrow.x, arrow.y, "#f8e9a1");
          return false;
        }

        return true;
      });
    },
    arrowHitsEnemy(arrow, enemy) {
      if (enemy.spriteSet === "cheeseSoldier") {
        const hitbox = this.cheeseSoldierHitbox(enemy);
        const closestX = clamp(arrow.x, hitbox.left, hitbox.right);
        const closestY = clamp(arrow.y, hitbox.top, hitbox.bottom);
        return (
          Math.hypot(arrow.x - closestX, arrow.y - closestY) <
          Math.max(10, arrow.radius || 8)
        );
      }

      if (enemy.archetype !== "wizard") {
        return distance(arrow, enemy) < enemy.radius + 8;
      }

      const hitbox = this.wizardHitbox(enemy);
      const closestX = clamp(arrow.x, hitbox.left, hitbox.right);
      const closestY = clamp(arrow.y, hitbox.top, hitbox.bottom);
      return (
        Math.hypot(arrow.x - closestX, arrow.y - closestY) <
        Math.max(10, arrow.radius || 8)
      );
    },
    wizardHitbox(enemy) {
      const drawHeight = enemy.radius * (enemy.isBoss ? 13.95 : 12.75);
      const visualTop = enemy.y - drawHeight * 0.54;
      const visualBottom = enemy.y - drawHeight * 0.13;
      const halfWidth = enemy.radius * 1.45;
      return {
        left: enemy.x - halfWidth,
        right: enemy.x + halfWidth,
        top: visualTop,
        bottom: visualBottom,
      };
    },
    cheeseSoldierHitbox(enemy) {
      const halfWidth = enemy.radius * 1.18;
      return {
        left: enemy.x - halfWidth,
        right: enemy.x + halfWidth,
        top: enemy.y - enemy.radius * 3.7,
        bottom: enemy.y + enemy.radius * 0.55,
      };
    },
    updateBarbaroAllies(delta) {
      this.barbaroAllies.forEach((ally) => {
        ally.wobble += delta * 4;
        ally.animTime += delta;

        if (ally.hp <= 0) {
          ally.state = "death";
          ally.deathTimer -= delta;
          return;
        }

        const target =
          this.enemies.find(
            (enemy) => enemy.id === ally.targetId && enemy.hp > 0
          ) || this.findClosestEnemy(ally);

        ally.targetId = target ? target.id : null;
        ally.attackTimer -= delta;

        if (!target) {
          ally.state = ally.x < this.canvasSize.width * 0.72 ? "walk" : "idle";
          ally.x = Math.min(
            this.canvasSize.width * 0.72,
            ally.x + ally.speed * 0.42 * delta
          );
          return;
        }

        const gap = distance(ally, target);
        const combatDistance = ally.radius + target.radius + 8;

        if (gap > combatDistance) {
          ally.state = "walk";
          const angle = Math.atan2(target.y - ally.y, target.x - ally.x);
          ally.x += Math.cos(angle) * ally.speed * delta;
          ally.y += Math.sin(angle) * ally.speed * delta * 0.72;
          ally.y = clamp(ally.y, 72, this.canvasSize.height - 58);
          return;
        }

        ally.state = "attack";
        target.allyTargetId = ally.id;
        if (ally.attackTimer <= 0) {
          ally.attackTimer = ally.attackRate;
          ally.attackFlash = 0.18;
          ally.animTime = 0;
          this.damageEnemy(target, ally.damage);
          this.addImpact(target.x, target.y, "#ffcf5a");
        }

        ally.attackFlash = Math.max(0, ally.attackFlash - delta);
      });

      this.barbaroAllies = this.barbaroAllies.filter(
        (ally) => ally.hp > 0 || ally.deathTimer > 0
      );
    },
    findBlockingAlly(enemy) {
      return this.barbaroAllies.find(
        (ally) =>
          ally.hp > 0 && distance(ally, enemy) < ally.radius + enemy.radius + 10
      );
    },
    updateEnemies(delta) {
      this.enemies.forEach((enemy) => {
        enemy.animTime = (enemy.animTime || 0) + delta;
        enemy.attackAnimTimer = Math.max(
          0,
          (enemy.attackAnimTimer || 0) - delta
        );
        enemy.hitAnimTimer = Math.max(0, (enemy.hitAnimTimer || 0) - delta);

        if (enemy.hp <= 0 || enemy.defeated) {
          if (
            enemy.archetype === "wizard" ||
            enemy.archetype === "golemBoss" ||
            enemy.spriteSet === "cheeseSoldier"
          ) {
            enemy.deathTimer = Math.max(0, (enemy.deathTimer || 0) - delta);
          }
          return;
        }

        const slowFactor = enemy.slow > 0 ? 0.5 : 1;
        enemy.slow = Math.max(0, enemy.slow - delta);
        enemy.wobble += delta * 5;
        if (enemy.archetype === "golemBoss") {
          this.updateGolemBoss(enemy, delta, slowFactor);
          return;
        }
        const blockingAlly =
          this.barbaroAllies.find(
            (ally) => ally.id === enemy.allyTargetId && ally.hp > 0
          ) || this.findBlockingAlly(enemy);

        if (blockingAlly) {
          enemy.allyTargetId = blockingAlly.id;
          enemy.attackTimer -= delta;
          if (enemy.attackTimer <= 0) {
            enemy.attackTimer = enemy.isBoss ? 0.92 : 1.1;
            if (enemy.archetype === "wizard") {
              this.triggerWizardAttack(enemy, "melee");
            }
            if (enemy.spriteSet === "cheeseSoldier") {
              this.triggerCheeseSoldierAttack(enemy);
            }
            blockingAlly.hp -= enemy.damage * 0.58;
            this.addImpact(blockingAlly.x, blockingAlly.y, enemy.color);
          }
          return;
        }

        enemy.allyTargetId = null;
        const isRanged =
          enemy.archetype === "archer" ||
          enemy.archetype === "catapult" ||
          enemy.archetype === "dragon" ||
          enemy.archetype === "wizard";
        const stopX = isRanged
          ? this.enemyRangedStopX(enemy)
          : BASE_WIDTH + enemy.radius;

        if (enemy.archetype === "dragon") {
          if (this.updateDragonEnemy(enemy, delta, slowFactor, stopX)) {
            return;
          }
        }

        if (enemy.x > stopX) {
          enemy.x -= enemy.speed * slowFactor * delta;
          enemy.y += Math.sin(enemy.wobble) * 12 * delta;
          return;
        }

        enemy.attackTimer -= delta;
        if (enemy.attackTimer <= 0) {
          if (isRanged) {
            this.fireEnemyProjectile(enemy);
            enemy.attackTimer =
              (enemy.attackRate || 1.45) *
              (enemy.slow > 0 ? 1.35 : 1) *
              randomBetween(0.85, 1.12);
          } else {
            enemy.attackTimer = enemy.isBoss ? 0.88 : 1.05;
            if (enemy.spriteSet === "cheeseSoldier") {
              this.triggerCheeseSoldierAttack(enemy);
            }
            this.damageCastle(enemy.damage, BASE_WIDTH, enemy.y, enemy.color);
          }
        }
      });

      this.enemies = this.enemies.filter(
        (enemy) => enemy.hp > 0 || enemy.deathTimer > 0
      );
    },
    triggerWizardAttack(enemy, kind) {
      enemy.attackKind = kind;
      enemy.attackAnimTimer =
        WIZARD_ANIMATIONS[kind === "melee" ? "attack1" : "attack2"].duration;
      enemy.animTime = 0;
    },
    triggerCheeseSoldierAttack(enemy) {
      enemy.attackAnimTimer = CHEESE_SOLDIER_ANIMATIONS.attack.duration;
      enemy.animTime = 0;
    },
    golemFarStopX() {
      return clamp(
        BASE_WIDTH + (this.canvasSize.width - BASE_WIDTH) * 0.76,
        BASE_WIDTH + 360,
        this.canvasSize.width - 145
      );
    },
    golemCloseStopX() {
      return clamp(
        BASE_WIDTH + (this.canvasSize.width - BASE_WIDTH) * 0.25,
        BASE_WIDTH + 220,
        this.golemFarStopX() - 180
      );
    },
    updateGolemBoss(enemy, delta, slowFactor) {
      const farX = this.golemFarStopX();
      const closeX = this.golemCloseStopX();
      enemy.golemPhase = enemy.golemPhase || "rock";
      enemy.golemState = "idle";
      enemy.y += Math.sin(enemy.wobble) * 5 * delta;
      enemy.y = clamp(enemy.y, 90, this.canvasSize.height - 84);

      if (enemy.golemPhase === "rock") {
        if (enemy.x > farX) {
          enemy.golemState = "walk";
          enemy.x -= enemy.speed * slowFactor * delta;
          return;
        }
        enemy.attackTimer -= delta;
        if (enemy.attackTimer <= 0) {
          enemy.golemState = "rock";
          enemy.attackAnimTimer = GOLEM_BOSS_ANIMATIONS.rock.duration;
          enemy.animTime = 0;
          this.fireGolemRock(enemy);
          enemy.attackTimer = randomBetween(1.15, 1.55);
          enemy.golemRockShots = (enemy.golemRockShots || 0) + 1;
          if (enemy.golemRockShots >= 1) {
            enemy.golemPhase = "approach";
          }
        }
        return;
      }

      if (enemy.golemPhase === "approach") {
        enemy.golemState = "walk";
        if (enemy.x > closeX) {
          enemy.x -= enemy.speed * 1.16 * slowFactor * delta;
          return;
        }
        enemy.golemPhase = "laserCharge";
        enemy.laserTimer = 0.54;
        enemy.attackAnimTimer = GOLEM_BOSS_ANIMATIONS.laser.duration;
        enemy.animTime = 0;
        this.startGolemLaserCharge(enemy);
        return;
      }

      if (enemy.golemPhase === "laserCharge") {
        enemy.golemState = "laser";
        enemy.laserTimer -= delta;
        if (enemy.laserTimer <= 0) {
          this.fireGolemLaser(enemy);
          enemy.golemPhase = "laserRecover";
          enemy.laserTimer = 2;
        }
        return;
      }

      if (enemy.golemPhase === "laserRecover") {
        enemy.golemState = "laser";
        enemy.laserTimer -= delta;
        if (enemy.laserTimer <= 0) {
          enemy.golemPhase = "retreat";
          enemy.attackAnimTimer = 0;
        }
        return;
      }

      if (enemy.golemPhase === "retreat") {
        enemy.golemState = "walk";
        if (enemy.x < farX) {
          enemy.x += enemy.speed * 1.02 * slowFactor * delta;
          return;
        }
        enemy.golemPhase = "rock";
        enemy.golemRockShots = 0;
        enemy.attackTimer = randomBetween(0.65, 1.15);
      }
    },
    enemyRangedStopX(enemy) {
      if (enemy.rangeStopRatio) {
        return clamp(
          this.canvasSize.width * enemy.rangeStopRatio,
          BASE_WIDTH + 260,
          this.canvasSize.width - 140
        );
      }
      return BASE_WIDTH + (enemy.rangeStop || 520);
    },
    fireGolemRock(enemy) {
      const projectileOrigin = {
        x: enemy.x - enemy.radius * 1.05,
        y: enemy.y - enemy.radius * 0.22,
      };
      const speed = 650;
      this.enemyProjectiles.push({
        x: projectileOrigin.x,
        y: projectileOrigin.y,
        vx: -speed,
        vy: 0,
        damage: enemy.damage * 0.82,
        radius: 18,
        color: "#7fefff",
        isGolemRock: true,
        angle: Math.PI,
      });
    },
    golemFaceOrigin(enemy) {
      return {
        x: enemy.x - enemy.radius * 0.34,
        y: enemy.y - enemy.radius * 1.72,
      };
    },
    startGolemLaserCharge(enemy) {
      const origin = this.golemFaceOrigin(enemy);
      this.effects.push({
        type: "golemLaserCharge",
        x: origin.x,
        y: origin.y,
        radius: 1,
        grow: 0,
        color: "#69e7ff",
        life: 0.54,
        maxLife: 0.54,
      });
    },
    fireGolemLaser(enemy) {
      const origin = this.golemFaceOrigin(enemy);
      const targetY = clamp(origin.y, 58, this.canvasSize.height - 52);
      this.effects.push({
        type: "golemLaser",
        x: origin.x,
        y: origin.y,
        targetX: BASE_WIDTH - 8,
        targetY,
        radius: 1,
        grow: 0,
        color: "#69e7ff",
        life: 2,
        maxLife: 2,
        damagePerSecond: (enemy.damage * 1.18) / 2,
        impactTimer: 0,
      });
    },
    updateDragonEnemy(enemy, delta, slowFactor, stopX) {
      enemy.diveTimer -= delta;
      if (!enemy.diving && !enemy.retreating && enemy.diveTimer <= 0) {
        enemy.diving = true;
      }

      if (enemy.diving) {
        enemy.x -= enemy.diveSpeed * slowFactor * delta;
        enemy.y += Math.sin(enemy.wobble * 1.4) * 44 * delta;
        if (enemy.x <= BASE_WIDTH + enemy.radius) {
          this.damageCastle(
            enemy.damage * 1.55,
            BASE_WIDTH,
            enemy.y,
            enemy.color
          );
          enemy.diving = false;
          enemy.retreating = true;
          enemy.diveTimer = enemy.diveRate || 5.6;
        }
        return true;
      }

      if (enemy.retreating) {
        enemy.x += enemy.speed * 2.25 * delta;
        enemy.y += Math.sin(enemy.wobble) * 20 * delta;
        if (enemy.x >= stopX) {
          enemy.x = stopX;
          enemy.retreating = false;
        }
        return true;
      }

      return false;
    },
    fireEnemyProjectile(enemy) {
      const target = {
        x: BASE_WIDTH - 12,
        y: clamp(
          enemy.y + randomBetween(-56, 56),
          54,
          this.canvasSize.height - 48
        ),
      };
      const speed = enemy.projectileSpeed || 340;
      const isCatapult = enemy.archetype === "catapult";
      const isWizard = enemy.archetype === "wizard";
      const isDragon = enemy.archetype === "dragon";
      const projectileOrigin = isWizard
        ? {
            x: enemy.x - enemy.radius * 0.45,
            y: enemy.y - enemy.radius * 2.25,
          }
        : {
            x: enemy.x - enemy.radius * 0.7,
            y: enemy.y,
          };
      const angle = Math.atan2(
        target.y - projectileOrigin.y,
        target.x - projectileOrigin.x
      );
      if (isWizard) {
        this.triggerWizardAttack(enemy, "ranged");
      }
      this.enemyProjectiles.push({
        x: projectileOrigin.x,
        y: projectileOrigin.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        damage: enemy.damage,
        radius: isCatapult ? 16 : isDragon ? 12 : isWizard ? 10 : 8,
        color: isWizard
          ? "#8d5cff"
          : isDragon
          ? "#ff8f5a"
          : isCatapult
          ? "#f0a83d"
          : "#ffe18b",
        isCatapult,
        disablesAbilities: isWizard,
      });
    },
    updateEnemyProjectiles(delta) {
      this.enemyProjectiles.forEach((shot) => {
        shot.x += shot.vx * delta;
        shot.y += shot.vy * delta;
        if (shot.isCatapult) {
          shot.vy += 80 * delta;
        }
      });

      this.enemyProjectiles = this.enemyProjectiles.filter((shot) => {
        const allyHit = this.barbaroAllies.find(
          (ally) =>
            ally.hp > 0 && distance(shot, ally) < ally.radius + shot.radius
        );
        if (allyHit) {
          allyHit.hp -= shot.damage * 0.72;
          this.addImpact(shot.x, shot.y, shot.color);
          return false;
        }

        if (shot.x <= BASE_WIDTH) {
          const damaged = this.damageCastle(
            shot.damage,
            BASE_WIDTH,
            shot.y,
            shot.color
          );
          if (damaged && shot.disablesAbilities) {
            this.castle.abilitiesDisabledTimer = Math.max(
              this.castle.abilitiesDisabledTimer,
              20
            );
          }
          return false;
        }
        return (
          shot.x > -80 && shot.y > -80 && shot.y < this.canvasSize.height + 100
        );
      });
    },
    updateEffects(delta) {
      this.effects.forEach((effect) => {
        if (effect.type === "golemLaser" && effect.damagePerSecond) {
          const damaged = this.damageCastle(
            effect.damagePerSecond * delta,
            BASE_WIDTH,
            effect.targetY,
            effect.color,
            { showImpact: false }
          );
          effect.impactTimer = (effect.impactTimer || 0) - delta;
          if (effect.impactTimer <= 0) {
            this.addImpact(
              BASE_WIDTH,
              effect.targetY,
              damaged ? effect.color : "#67c7ff"
            );
            effect.impactTimer = 0.16;
          }
        }
        effect.life -= delta;
        effect.radius += effect.grow * delta;
      });
      this.effects = this.effects.filter((effect) => effect.life > 0);
    },
    updateParticles(delta) {
      this.particles.forEach((particle) => {
        particle.x += particle.vx * delta;
        particle.y += particle.vy * delta;
        particle.life -= delta;
      });
      this.particles = this.particles.filter((particle) => particle.life > 0);
    },
    damageCastle(amount, x, y, color, options = {}) {
      if (this.castle.barrierTimer > 0) {
        if (options.showImpact !== false) {
          this.addImpact(x, y, "#67c7ff");
        }
        return false;
      }
      this.castle.hp -= amount;
      this.castleDamagedThisLevel = true;
      if (options.showImpact !== false) {
        this.addImpact(x, y, color);
      }
      if (options.wake !== false) {
        this.wakeBertoCastle();
      }
      return true;
    },
    damageEnemy(enemy, amount) {
      if (enemy.hp <= 0 || enemy.defeated) {
        return;
      }
      enemy.hp -= amount;
      if (
        (enemy.archetype === "wizard" ||
          enemy.archetype === "golemBoss" ||
          enemy.spriteSet === "cheeseSoldier") &&
        enemy.hp > 0
      ) {
        enemy.hitAnimTimer =
          enemy.spriteSet === "cheeseSoldier"
            ? CHEESE_SOLDIER_ANIMATIONS.hit.duration
            : enemy.archetype === "golemBoss"
            ? GOLEM_BOSS_ANIMATIONS.hurt.duration
            : WIZARD_ANIMATIONS.hit.duration;
        enemy.animTime = 0;
      }
      if (enemy.hp <= 0) {
        enemy.defeated = true;
        if (enemy.archetype === "wizard") {
          enemy.deathTimer = WIZARD_ANIMATIONS.death.duration;
          enemy.animTime = 0;
        }
        if (enemy.archetype === "golemBoss") {
          enemy.deathTimer = GOLEM_BOSS_ANIMATIONS.death.duration;
          enemy.animTime = 0;
        }
        if (enemy.spriteSet === "cheeseSoldier") {
          enemy.deathTimer = CHEESE_SOLDIER_ANIMATIONS.death.duration;
          enemy.animTime = 0;
        }
        const goldReward = this.enemyGoldReward(enemy);
        this.gold += goldReward;
        this.score += 1;
        if (this.gameMode === "defense") {
          this.levelGoldEarned += goldReward;
          if (enemy.isBoss) {
            this.bossActive = this.enemies.some(
              (item) => item !== enemy && item.isBoss && item.hp > 0
            );
            this.message = enemy.isSuperBoss
              ? "Il Gran Re della Fonduta e' stato sconfitto. Fortezza completata."
              : `${enemy.name} sconfitto. Il livello ${this.wave} e' salvo.`;
          } else {
            this.levelKills = Math.min(this.levelTarget, this.levelKills + 1);
            this.message = enemy.name + " e' stato fuso.";
          }
        } else {
          this.message = enemy.name + " e' stato fuso.";
        }
        this.addBurst(enemy.x, enemy.y, enemy.color);
      }
    },
    addImpact(x, y, color) {
      this.effects.push({
        type: "ring",
        x,
        y,
        radius: 8,
        grow: 90,
        color,
        life: 0.28,
        maxLife: 0.28,
      });
    },
    addBurst(x, y, color) {
      for (let index = 0; index < 12; index += 1) {
        const angle = randomBetween(0, Math.PI * 2);
        const speed = randomBetween(60, 190);
        this.particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          color,
          life: randomBetween(0.28, 0.6),
        });
      }
    },
    selectPower(powerId) {
      if (this.gamePaused) {
        return;
      }
      this.selectedPower = powerId;
      if (this.gameMode === "assault") {
        this.deployAssaultUnit(powerId);
        return;
      }
      if (this.gameMode === "berto") {
        this.castBertoAbility(powerId);
        return;
      }
      this.castPower(powerId);
    },
    openSection(section) {
      this.stopLoop();
      this.storeOpen = false;
      this.gamePaused = false;
      this.devConfigOpen = false;
      this.activeSection = section;
      this.updateBarbaroIconForCurrentLevel();
    },
    openDevConfig() {
      const upgradeLevels = {};
      this.upgrades.forEach((upgrade) => {
        upgradeLevels[upgrade.id] = upgrade.level;
      });
      this.devConfigDraft = {
        defenseLevel: this.defenseCurrentLevel,
        gold: this.gold,
        upgradeLevels,
      };
      this.devConfigOpen = true;
    },
    closeDevConfig() {
      this.devConfigOpen = false;
    },
    applyDevConfig() {
      const nextLevel = clamp(
        Math.round(Number(this.devConfigDraft.defenseLevel) || 1),
        1,
        100
      );
      const nextGold = clamp(
        Math.round(Number(this.devConfigDraft.gold) || 0),
        0,
        999999
      );

      this.defenseCurrentLevel = nextLevel;
      this.selectedDefenseLevel = nextLevel;
      if (this.gameMode === "defense") {
        this.wave = nextLevel;
        this.levelTarget = this.defenseTargetForLevel(nextLevel);
      }
      this.gold = nextGold;

      const previousCastleMax = this.castle.maxHp;
      const previousManaMax = this.castle.maxMana;

      this.upgrades.forEach((upgrade) => {
        const rawLevel = this.devConfigDraft.upgradeLevels[upgrade.id];
        const minLevel = this.upgradeMinLevel(upgrade);
        upgrade.level = clamp(
          Math.round(Number(rawLevel) || minLevel),
          minLevel,
          upgrade.max
        );
        if (upgrade.id === "arrows") {
          upgrade.icon = `x${upgrade.level}`;
        }
      });

      this.castle.maxHp = this.castleMaxHpForWallLevel(this.wallUpgradeLevel);
      this.castle.hp = Math.min(
        this.castle.maxHp,
        this.castle.hp + Math.max(0, this.castle.maxHp - previousCastleMax)
      );
      this.castle.maxMana = this.castleMaxManaForLevel(
        this.getUpgradeLevel("mana")
      );
      this.castle.mana = Math.min(
        this.castle.maxMana,
        this.castle.mana + Math.max(0, this.castle.maxMana - previousManaMax)
      );

      this.message = `Config dev applicata: livello ${nextLevel}, oro ${nextGold}.`;
      this.updateBarbaroIconForCurrentLevel();
      this.devConfigOpen = false;
    },
    normalizeSelectedDefenseLevel() {
      this.selectedDefenseLevel = clamp(
        Math.round(Number(this.selectedDefenseLevel) || 1),
        1,
        this.defenseCurrentLevel
      );
    },
    changeSelectedDefenseLevel(delta) {
      this.selectedDefenseLevel = clamp(
        this.selectedDefenseLevel + delta,
        1,
        this.defenseCurrentLevel
      );
    },
    startGame() {
      this.gameMode = this.selectedStoryId;
      if (this.gameMode === "defense") {
        this.normalizeSelectedDefenseLevel();
      }
      this.restartGame();
      this.activeSection = "play";
      this.storeOpen = false;
      this.$nextTick(() => {
        this.resizeCanvas();
        this.startLoop();
      });
    },
    pauseGame() {
      if (
        this.activeSection !== "play" ||
        this.gameOver ||
        this.levelComplete
      ) {
        return;
      }
      this.gamePaused = true;
      this.isFiring = false;
    },
    resumeGame() {
      if (this.activeSection !== "play") {
        return;
      }
      this.gamePaused = false;
      this.lastFrameAt = performance.now();
    },
    retryCurrentLevel() {
      if (this.gameMode === "defense") {
        this.selectedDefenseLevel = clamp(
          this.wave,
          1,
          this.defenseCurrentLevel
        );
      }
      this.restartGame();
      this.$nextTick(() => {
        this.resizeCanvas();
        this.startLoop();
      });
    },
    returnToHub() {
      this.openSection("shop");
    },
    startNextDefenseLevel() {
      if (this.gameMode === "defense") {
        this.selectedDefenseLevel = clamp(
          this.completedLevel + 1,
          1,
          this.defenseCurrentLevel
        );
      }
      this.restartGame();
    },
    abilityUpgradeLevel(powerId) {
      const upgrade = this.upgrades.find((item) => item.powerId === powerId);
      return upgrade ? upgrade.level : 0;
    },
    effectiveAbilityLevel(level, powerId) {
      return ["sghiubbe", "ai", "barrier", "kickboxing"].includes(powerId)
        ? level * PRIMARY_ABILITY_EFFECT_SCALE
        : level;
    },
    abilityStrength(powerId) {
      return this.abilityStrengthForLevel(
        this.abilityUpgradeLevel(powerId),
        powerId
      );
    },
    cooldownMultiplierForLevel(level) {
      return Math.max(0.5, 1 - clamp(Math.floor(level), 0, 10) * 0.05);
    },
    abilityCooldownMultiplier() {
      return this.cooldownMultiplierForLevel(
        this.getUpgradeLevel("cooldownPower")
      );
    },
    abilityCooldownDuration(ability) {
      const isDefensePower = this.powers.some((item) => item.id === ability.id);
      const multiplier = isDefensePower
        ? this.abilityCooldownMultiplier(ability.id)
        : this.cooldownMultiplier;
      return Math.max(0.1, ability.cooldown * multiplier);
    },
    abilityCooldownForPowerId(
      powerId,
      cooldownLevel = this.getUpgradeLevel("cooldownPower")
    ) {
      const power = this.powers.find((item) => item.id === powerId);
      if (!power) {
        return null;
      }
      return Math.max(
        0.1,
        power.cooldown * this.cooldownMultiplierForLevel(cooldownLevel)
      );
    },
    abilityCooldownLabelForPowerId(powerId, cooldownLevel) {
      const cooldown = this.abilityCooldownForPowerId(powerId, cooldownLevel);
      return cooldown === null ? "-" : `${cooldown.toFixed(1)}s`;
    },
    abilityShopName(power) {
      const labels = {
        sghiubbe: "Sghiubba",
        ai: "Frecce predittive",
        barrier: "Barriera",
        kickboxing: "Calcio rotante",
        barbaro: "Barbaro",
      };
      return labels[power.id] || power.name;
    },
    metricRowKey(upgrade, row) {
      return `${upgrade.id}:${row.label}`;
    },
    metricDetailsOpen(upgrade, row) {
      return (
        row.details &&
        this.activeMetricDetails === this.metricRowKey(upgrade, row)
      );
    },
    toggleMetricDetails(upgrade, row) {
      if (!row.details) {
        return;
      }
      const key = this.metricRowKey(upgrade, row);
      this.activeMetricDetails = this.activeMetricDetails === key ? null : key;
    },
    cooldownMetricRowForPowerId(powerId, currentLevel, nextLevel) {
      return {
        label: "Cooldown",
        current: this.abilityCooldownLabelForPowerId(powerId, currentLevel),
        next:
          nextLevel === null
            ? null
            : this.abilityCooldownLabelForPowerId(powerId, nextLevel),
      };
    },
    abilityCooldownRemainingPercent(ability) {
      if (ability.cooldownLeft <= 0) {
        return 0;
      }
      return clamp(
        (ability.cooldownLeft / this.abilityCooldownDuration(ability)) * 100,
        0,
        100
      );
    },
    abilityManaCost(ability) {
      return DEFENSE_POWER_MANA_COSTS[ability.id] || 0;
    },
    powerRequiredManaLevel(powerId) {
      return POWER_MANA_UNLOCK_LEVELS[powerId] || 0;
    },
    abilityLockedReason(ability) {
      if (this.gameMode !== "defense" || !ability) {
        return "";
      }
      const requiredManaLevel = this.powerRequiredManaLevel(ability.id);
      if (!requiredManaLevel) {
        return "";
      }
      if (!this.manaUnlockReached) {
        return `Mana sbloccabile dal livello ${MANA_UNLOCK_DEFENSE_LEVEL}.`;
      }
      if (this.manaUpgradeLevel < requiredManaLevel) {
        return `Richiede Riserva mana livello ${requiredManaLevel}.`;
      }
      return "";
    },
    abilityLocked(ability) {
      return !!this.abilityLockedReason(ability);
    },
    abilityManaLocked(ability) {
      if (this.gameMode !== "defense") {
        return false;
      }
      if (this.abilityLocked(ability)) {
        return false;
      }
      return this.castle.mana < this.abilityManaCost(ability);
    },
    upgradeLockedReason(upgrade) {
      if (!upgrade) {
        return "";
      }
      if (upgrade.id === "mana") {
        return this.manaUnlockReached
          ? ""
          : `Sblocco al livello ${MANA_UNLOCK_DEFENSE_LEVEL}.`;
      }
      if (upgrade.id === "manaRegen") {
        if (!this.manaUnlockReached) {
          return `Sblocco al livello ${MANA_UNLOCK_DEFENSE_LEVEL}.`;
        }
        return this.manaUpgradeLevel >= 1
          ? ""
          : "Richiede Riserva mana livello 1.";
      }
      if (upgrade.category === "ability" && upgrade.powerId) {
        const requiredManaLevel = this.powerRequiredManaLevel(upgrade.powerId);
        if (!requiredManaLevel) {
          return "";
        }
        if (!this.manaUnlockReached) {
          return `Mana sbloccabile dal livello ${MANA_UNLOCK_DEFENSE_LEVEL}.`;
        }
        return this.manaUpgradeLevel >= requiredManaLevel
          ? ""
          : `Richiede Riserva mana livello ${requiredManaLevel}.`;
      }
      return "";
    },
    castPower(powerId) {
      if (this.gameOver || this.gamePaused) {
        return;
      }
      if (this.abilitiesDisabled) {
        this.message = "Malocchio attivo: le abilita sono bloccate.";
        return;
      }

      const power = this.powers.find((item) => item.id === powerId);
      if (!power || power.cooldownLeft > 0) {
        return;
      }
      const lockedReason = this.abilityLockedReason(power);
      if (lockedReason) {
        this.message = lockedReason;
        return;
      }
      const manaCost = this.abilityManaCost(power);
      if (this.castle.mana < manaCost) {
        this.message = `Mana insufficiente: servono ${manaCost} MP.`;
        return;
      }

      const abilityLevel = this.abilityUpgradeLevel(powerId);
      const effectiveLevel = this.effectiveAbilityLevel(abilityLevel, powerId);
      const strength = this.abilityStrength(powerId);
      this.castle.mana = Math.max(0, this.castle.mana - manaCost);
      power.cooldownLeft =
        power.cooldown * this.abilityCooldownMultiplier(powerId);

      if (powerId === "sghiubbe") {
        const animationKey = this.bertoAbilityAnimationKey(
          powerId,
          abilityLevel
        );
        this.startBertoAbilityAnimation(powerId, abilityLevel);
        this.queueBertoAbilityEffect(
          powerId,
          BERTO_ABILITY_ANIMATIONS[animationKey].effectDelay,
          {
            effectiveLevel,
            strength,
          }
        );
        this.message = "Berto prepara la Sghiubba Nebbiosa.";
      }

      if (powerId === "ai") {
        this.startBertoAbilityAnimation(powerId, abilityLevel);
        this.startGuidedArrowBurst(abilityLevel, effectiveLevel);
        this.message = "Frecce predittive: scarica guidata in arrivo.";
      }

      if (powerId === "barrier") {
        this.startBertoAbilityAnimation(powerId, abilityLevel);
        const duration = 3.2 + effectiveLevel * 0.55;
        const pulseCenter = {
          x: BASE_WIDTH,
          y: this.canvasSize.height * 0.5,
        };
        this.castle.barrierTimer = Math.max(this.castle.barrierTimer, duration);
        this.effects.push({
          type: "field",
          x: pulseCenter.x,
          y: pulseCenter.y,
          radius: this.canvasSize.height * (0.72 + effectiveLevel * 0.018),
          grow: 0,
          color: "#67c7ff",
          life: duration,
          maxLife: duration,
        });
        this.message = `Barriera attiva: danni bloccati per ${duration.toFixed(
          1
        )} secondi.`;
      }

      if (powerId === "kickboxing") {
        const animationDuration = this.startBertoAbilityAnimation(
          powerId,
          abilityLevel
        );
        this.queueBertoAbilityEffect(powerId, animationDuration, {
          effectiveLevel,
          strength,
        });
        this.message = "Berto prepara il Calcio rotante.";
      }

      if (powerId === "barbaro") {
        const ally = this.spawnBarbaroAlly(abilityLevel);
        this.effects.push({
          type: "ring",
          x: ally.x,
          y: ally.y,
          radius: 18,
          grow: 180,
          color: "#ffcf5a",
          life: 0.42,
          maxLife: 0.42,
        });
        this.message = "Il Barbaro scende dalle mura e blocca l'orda.";
      }
    },
    applySghiubbeEffect(effectiveLevel, strength) {
      const slowDuration = 5.2 * strength;
      const pulseDamage = 8 + this.damageBonus * 0.25;
      this.enemies.forEach((enemy) => {
        enemy.slow = Math.max(enemy.slow, slowDuration);
        this.damageEnemy(enemy, pulseDamage + effectiveLevel * 2.2);
      });
      this.effects.push({
        type: "field",
        x: BASE_WIDTH + (this.canvasSize.width - BASE_WIDTH) * 0.5,
        y: this.canvasSize.height * 0.5,
        radius: Math.max(this.canvasSize.width, this.canvasSize.height),
        grow: 0,
        color: "#7ad66d",
        life: 1.55 * strength,
        maxLife: 1.55 * strength,
      });
      this.message = "La Sghiubba Nebbiosa impasta l'avanzata.";
    },
    applyKickboxingEffect(effectiveLevel, strength) {
      const reach =
        this.canvasSize.width * Math.min(1, 0.82 + effectiveLevel * 0.025);
      const push = 110 * strength;
      const damage = 34 + this.damageBonus * 0.65 + effectiveLevel * 4;
      const waveLife = 0.72;
      const waveGrow = (this.canvasSize.width + 180) / waveLife;
      this.enemies.forEach((enemy) => {
        if (enemy.x >= BASE_WIDTH - enemy.radius * 1.5 && enemy.x < reach) {
          enemy.x += push;
          enemy.slow = Math.max(enemy.slow, 0.8 * strength);
          this.damageEnemy(enemy, damage);
        }
      });
      this.effects.push({
        type: "wave",
        x: BASE_WIDTH + 18,
        y: this.turret.y,
        radius: 58,
        grow: waveGrow * strength,
        color: "#ff8f5a",
        life: waveLife,
        maxLife: waveLife,
      });
      this.message = "Calcio rotante sulle croste: respinta secca.";
    },
    spawnBarbaroAlly(level) {
      const activeAllies = this.barbaroAllies.filter((ally) => ally.hp > 0);
      const maxAllies = 2 + Math.floor(level / 5);
      if (activeAllies.length >= maxAllies) {
        const weakest = activeAllies.reduce((chosen, ally) =>
          ally.hp < chosen.hp ? ally : chosen
        );
        weakest.hp = Math.min(weakest.maxHp, weakest.hp + 60 + level * 10);
        weakest.damage += 4 + level * 0.8;
        weakest.state = "idle";
        return weakest;
      }

      const hp = 150 + level * 24;
      const y = clamp(
        this.turret.y + randomBetween(-72, 72),
        86,
        this.canvasSize.height - 78
      );
      const ally = {
        id: Date.now() + Math.random(),
        barbaroTier: this.barbaroTierForUpgradeLevel(level),
        x: BASE_WIDTH + 38,
        y,
        hp,
        maxHp: hp,
        radius: 28,
        speed: 96 + level * 5,
        damage: 24 + this.damageBonus * 0.18 + level * 5.2,
        attackRate: Math.max(0.48, 0.82 - level * 0.026),
        attackTimer: 0,
        attackFlash: 0,
        animTime: Math.random() * 2,
        targetId: null,
        wobble: Math.random() * Math.PI * 2,
        frameOffset: Math.random() * 10,
        deathTimer: 0.85,
        state: "idle",
      };
      this.barbaroAllies.push(ally);
      return ally;
    },
    deployAssaultUnit(unitId) {
      const ability = this.assaultAbilities.find((item) => item.id === unitId);
      if (!ability || ability.cooldownLeft > 0 || this.gameOver) {
        return;
      }

      const y = clamp(
        this.pointer.y || this.canvasSize.height * 0.5,
        90,
        this.canvasSize.height - 70
      );
      const blueprints = {
        organism: {
          hp: 54,
          speed: 120,
          damage: 11,
          radius: 15,
          color: "#62d7ad",
          attackRate: 0.45,
        },
        grater: {
          hp: 120,
          speed: 66,
          damage: 24,
          radius: 22,
          color: "#c8d3da",
          attackRate: 0.8,
        },
        fireSghiubba: {
          hp: 78,
          speed: 88,
          damage: 32,
          radius: 19,
          color: "#ff8f5a",
          attackRate: 1.05,
        },
      };
      const blueprint = blueprints[unitId] || blueprints.organism;
      ability.cooldownLeft = ability.cooldown * this.cooldownMultiplier;
      this.assaultUnits.push({
        ...blueprint,
        x: BASE_WIDTH + 22,
        y,
        maxHp: blueprint.hp,
        baseSpeed: blueprint.speed,
        attackTimer: 0,
      });
      this.message = `${ability.name} in marcia verso la base casearia.`;
    },
    castBertoAbility(abilityId) {
      const ability = this.bertoAbilities.find((item) => item.id === abilityId);
      if (!ability || ability.cooldownLeft > 0 || this.gameOver) {
        return;
      }

      ability.cooldownLeft = ability.cooldown * this.cooldownMultiplier;
      if (abilityId === "graterSword") {
        this.damageBertoEnemies(86, 28 + this.damageBonus * 0.55);
        this.addImpact(this.berto.x, this.berto.y, "#d8dde3");
        this.message = "Berto affetta con la spada-grattugia.";
      }
      if (abilityId === "kick") {
        this.bertoEnemies.forEach((enemy) => {
          const dist = Math.hypot(
            enemy.x - this.berto.x,
            enemy.y - this.berto.y
          );
          if (dist < 132) {
            const angle = Math.atan2(
              enemy.y - this.berto.y,
              enemy.x - this.berto.x
            );
            enemy.x += Math.cos(angle) * 80;
            enemy.y += Math.sin(angle) * 80;
            enemy.hp -= 22 + this.damageBonus * 0.35;
          }
        });
        this.effects.push({
          type: "wave",
          x: this.berto.x,
          y: this.berto.y,
          radius: 20,
          grow: 260,
          color: "#ff8f5a",
          life: 0.4,
          maxLife: 0.4,
        });
        this.message = "Kick box: le croste arretrano.";
      }
      if (abilityId === "electricity") {
        this.damageBertoEnemies(190, 36 + this.damageBonus * 0.45);
        this.effects.push({
          type: "field",
          x: this.berto.x,
          y: this.berto.y,
          radius: 190,
          grow: 0,
          color: "#67c7ff",
          life: 0.55,
          maxLife: 0.55,
        });
        this.message = "Scarica elettrica nella fossa.";
      }
    },
    damageBertoEnemies(radius, damage) {
      this.bertoEnemies.forEach((enemy) => {
        if (
          Math.hypot(enemy.x - this.berto.x, enemy.y - this.berto.y) <
          radius + enemy.radius
        ) {
          enemy.hp -= damage;
          this.addBurst(enemy.x, enemy.y, enemy.color);
        }
      });
    },
    findClosestEnemy(origin) {
      let closest = null;
      let closestDistance = Infinity;
      this.enemies.forEach((enemy) => {
        if (enemy.hp <= 0 || enemy.defeated) {
          return;
        }
        const currentDistance = distance(origin, enemy);
        if (currentDistance < closestDistance) {
          closest = enemy;
          closestDistance = currentDistance;
        }
      });
      return closest;
    },
    handlePointerMove(event) {
      if (this.gamePaused) {
        return;
      }
      const rect = this.$refs.gameShell.getBoundingClientRect();
      this.pointer = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      if (this.gameMode === "berto") {
        this.berto.targetX = this.pointer.x;
        this.berto.targetY = this.pointer.y;
      }
    },
    startFiring(event) {
      if (
        this.storeOpen ||
        this.gameOver ||
        this.levelComplete ||
        this.gamePaused
      ) {
        return;
      }

      this.handlePointerMove(event);
      if (this.gameMode === "assault") {
        this.deployAssaultUnit(this.selectedPower || "organism");
        return;
      }
      if (this.gameMode === "berto") {
        this.castBertoAbility("graterSword");
        return;
      }

      this.isFiring = true;
      this.fireArrow(performance.now());
    },
    stopFiring() {
      this.isFiring = false;
    },
    handleKeyDown(event) {
      if (!this.authenticated) {
        return;
      }

      if (this.activeSection !== "play") {
        if (event.code === "Escape") {
          this.openSection("menu");
        }
        return;
      }

      if (this.gameMode !== "defense") {
        const keyToAbility = {
          KeyQ: this.gameMode === "assault" ? "organism" : "graterSword",
          KeyW: this.gameMode === "assault" ? "grater" : "kick",
          KeyE: this.gameMode === "assault" ? "fireSghiubba" : "electricity",
        };

        if (event.code === "Escape") {
          event.preventDefault();
          if (this.gamePaused) {
            this.resumeGame();
          } else {
            this.pauseGame();
          }
          return;
        }

        if (keyToAbility[event.code]) {
          event.preventDefault();
          if (this.gamePaused) {
            return;
          }
          this.selectPower(keyToAbility[event.code]);
        }
        return;
      }

      const keyToPower = {
        Digit1: "sghiubbe",
        Digit2: "ai",
        Digit3: "barrier",
        Digit4: "kickboxing",
        Digit5: "barbaro",
      };

      if (event.code === "Space") {
        event.preventDefault();
        return;
      }

      if (event.code === "Escape") {
        event.preventDefault();
        if (this.gamePaused) {
          this.resumeGame();
        } else {
          this.pauseGame();
        }
        return;
      }

      if (keyToPower[event.code]) {
        event.preventDefault();
        if (this.gamePaused) {
          return;
        }
        this.selectPower(keyToPower[event.code]);
      }
    },
    toggleStore() {
      this.storeOpen = !this.storeOpen;
      this.isFiring = false;
    },
    fireIntervalForSpeedLevel(level) {
      return Math.max(66, FIRE_RATE - level * 30.2);
    },
    shotsPerSecondForSpeedLevel(level) {
      return (1000 / this.fireIntervalForSpeedLevel(level)).toFixed(2);
    },
    abilityStrengthForLevel(level, powerId = null) {
      return 1 + this.effectiveAbilityLevel(level, powerId) * 0.22;
    },
    upgradeMetricRows(upgrade) {
      const current = upgrade.level;
      const next = Math.min(upgrade.max, upgrade.level + 1);
      const same = upgrade.level >= upgrade.max;
      const nextLevel = same ? current : next;
      const currentEffect = this.effectiveAbilityLevel(
        current,
        upgrade.powerId
      );
      const nextEffect = this.effectiveAbilityLevel(nextLevel, upgrade.powerId);

      if (upgrade.id === "damage") {
        return [
          {
            label: "Danno freccia",
            current: 20 + current,
            next: 20 + nextLevel,
          },
        ];
      }
      if (upgrade.id === "speed") {
        return [
          {
            label: "Colpi/sec",
            current: this.shotsPerSecondForSpeedLevel(current),
            next: this.shotsPerSecondForSpeedLevel(nextLevel),
          },
        ];
      }
      if (upgrade.id === "arrows") {
        return [
          {
            label: "Frecce simultanee",
            current,
            next: nextLevel,
          },
        ];
      }
      if (upgrade.id === "walls") {
        return [
          {
            label: "HP castello",
            current: this.castleMaxHpForWallLevel(current),
            next: this.castleMaxHpForWallLevel(nextLevel),
          },
        ];
      }
      if (upgrade.id === "mana") {
        return [
          {
            label: "Mana massimo",
            current: this.castleMaxManaForLevel(current),
            next: this.castleMaxManaForLevel(nextLevel),
          },
        ];
      }
      if (upgrade.id === "manaRegen") {
        return [
          {
            label: "Mana/sec",
            current: this.manaRegenRateForLevel(current).toFixed(1),
            next: this.manaRegenRateForLevel(nextLevel).toFixed(1),
          },
        ];
      }
      if (upgrade.id === "goldHunter") {
        return [
          {
            label: "Oro extra",
            current: `+${current * 5}%`,
            next: `+${nextLevel * 5}%`,
          },
        ];
      }
      if (upgrade.id === "cooldownPower") {
        return [
          {
            label: "Riduzione cooldown",
            current: `${current * 5}%`,
            next: `${nextLevel * 5}%`,
            details: this.powers.map((power) => ({
              label: this.abilityShopName(power),
              current: this.abilityCooldownLabelForPowerId(power.id, current),
              next: this.abilityCooldownLabelForPowerId(power.id, nextLevel),
            })),
          },
        ];
      }
      if (upgrade.id === "sghiubbePower") {
        return [
          {
            label: "Rallentamento",
            current: "50%",
            next: null,
          },
          {
            label: "Durata rallentamento",
            current: `${(
              5.2 * this.abilityStrengthForLevel(current, upgrade.powerId)
            ).toFixed(1)}s`,
            next: `${(
              5.2 * this.abilityStrengthForLevel(nextLevel, upgrade.powerId)
            ).toFixed(1)}s`,
          },
          {
            label: "Danno immediato",
            current: (
              8 +
              this.damageBonus * 0.25 +
              currentEffect * 2.2
            ).toFixed(1),
            next: (8 + this.damageBonus * 0.25 + nextEffect * 2.2).toFixed(1),
          },
          this.cooldownMetricRowForPowerId(
            upgrade.powerId,
            this.getUpgradeLevel("cooldownPower"),
            null
          ),
        ];
      }
      if (upgrade.id === "arrowsPower") {
        return [
          {
            label: "Quantità di frecce",
            current: this.guidedArrowCountForLevel(current),
            next: this.guidedArrowCountForLevel(nextLevel),
          },
          {
            label: "Danno per freccia",
            current: Math.round(
              20 + this.damageBonus * 0.8 + currentEffect * 3.4
            ),
            next: Math.round(20 + this.damageBonus * 0.8 + nextEffect * 3.4),
          },
          this.cooldownMetricRowForPowerId(
            upgrade.powerId,
            this.getUpgradeLevel("cooldownPower"),
            null
          ),
        ];
      }
      if (upgrade.id === "barrierPower") {
        return [
          {
            label: "Durata barriera",
            current: `${(3.2 + currentEffect * 0.55).toFixed(1)}s`,
            next: `${(3.2 + nextEffect * 0.55).toFixed(1)}s`,
          },
          {
            label: "Danni bloccati",
            current: "100%",
            next: null,
          },
          this.cooldownMetricRowForPowerId(
            upgrade.powerId,
            this.getUpgradeLevel("cooldownPower"),
            null
          ),
        ];
      }
      if (upgrade.id === "kickPower") {
        return [
          {
            label: "Danno calcio rotante",
            current: Math.round(
              34 + this.damageBonus * 0.65 + currentEffect * 4
            ),
            next: Math.round(34 + this.damageBonus * 0.65 + nextEffect * 4),
          },
          {
            label: "Portata",
            current: `${Math.round(
              Math.min(1, 0.82 + currentEffect * 0.025) * 100
            )}%`,
            next: `${Math.round(
              Math.min(1, 0.82 + nextEffect * 0.025) * 100
            )}%`,
          },
          {
            label: "Respinta",
            current: Math.round(
              110 * this.abilityStrengthForLevel(current, upgrade.powerId)
            ),
            next: Math.round(
              110 * this.abilityStrengthForLevel(nextLevel, upgrade.powerId)
            ),
          },
          this.cooldownMetricRowForPowerId(
            upgrade.powerId,
            this.getUpgradeLevel("cooldownPower"),
            null
          ),
        ];
      }
      if (upgrade.id === "barbaroPower") {
        return [
          {
            label: "HP Barbaro",
            current: 150 + current * 24,
            next: 150 + nextLevel * 24,
          },
          {
            label: "Danno",
            current: Math.round(24 + this.damageBonus * 0.18 + current * 5.2),
            next: Math.round(24 + this.damageBonus * 0.18 + nextLevel * 5.2),
          },
          {
            label: "Velocità",
            current: 96 + current * 5,
            next: 96 + nextLevel * 5,
          },
          {
            label: "Attacchi/sec",
            current: (1 / Math.max(0.48, 0.82 - current * 0.026)).toFixed(2),
            next: (1 / Math.max(0.48, 0.82 - nextLevel * 0.026)).toFixed(2),
          },
          this.cooldownMetricRowForPowerId(
            upgrade.powerId,
            this.getUpgradeLevel("cooldownPower"),
            null
          ),
        ];
      }
      return [
        {
          label: "Livello",
          current,
          next: nextLevel,
        },
      ];
    },
    damageCurveCost(level) {
      return Math.floor(40 * Math.pow(1.55, level));
    },
    upgradeCost(upgrade) {
      const fixedCosts = UPGRADE_COSTS[upgrade.id];
      if (fixedCosts) {
        const costIndex =
          this.upgradeMinLevel(upgrade) === 1
            ? upgrade.level - 1
            : upgrade.level;
        return fixedCosts[costIndex] || 0;
      }
      return Math.floor(upgrade.baseCost * Math.pow(1.55, upgrade.level));
    },
    upgradeLevelLabel(upgrade) {
      return `Livello ${upgrade.level} / ${upgrade.max}`;
    },
    upgradeMinLevel(upgrade) {
      return ["arrows", "walls"].includes(upgrade.id) ? 1 : 0;
    },
    upgradeProgress(upgrade) {
      if (upgrade.id === "arrows") {
        return clamp((upgrade.level / upgrade.max) * 100, 0, 100);
      }
      if (upgrade.id === "walls") {
        return clamp(((upgrade.level - 1) / (upgrade.max - 1)) * 100, 0, 100);
      }
      return clamp((upgrade.level / upgrade.max) * 100, 0, 100);
    },
    upgradeNextText(upgrade) {
      if (upgrade.level >= upgrade.max) {
        return "Potenziamento al massimo.";
      }
      if (upgrade.id === "damage") {
        return `Prossimo: +1 danno per freccia.`;
      }
      if (upgrade.id === "speed") {
        return `Prossimo: cadenza di tiro piu rapida.`;
      }
      if (upgrade.id === "arrows") {
        return `Prossimo: ${upgrade.level + 1} frecce insieme.`;
      }
      if (upgrade.id === "walls") {
        return `Prossimo: ${this.castleMaxHpForWallLevel(
          upgrade.level + 1
        )} HP castello.`;
      }
      if (upgrade.id === "mana") {
        return `Prossimo: ${this.castleMaxManaForLevel(
          upgrade.level + 1
        )} mana massimo.`;
      }
      if (upgrade.id === "manaRegen") {
        return `Prossimo: ${this.manaRegenRateForLevel(
          upgrade.level + 1
        ).toFixed(1)} mana al secondo.`;
      }
      if (upgrade.id === "goldHunter") {
        return `Prossimo: +${(upgrade.level + 1) * 5}% oro dai nemici.`;
      }
      if (upgrade.id === "sghiubbePower") {
        return "Prossimo: nebbia piu lunga e piu dannosa.";
      }
      if (upgrade.id === "arrowsPower") {
        return "Prossimo: raffica predittiva piu fitta e dannosa.";
      }
      if (upgrade.id === "barrierPower") {
        return "Prossimo: barriera attiva piu a lungo.";
      }
      if (upgrade.id === "kickPower") {
        return "Prossimo: calcio rotante piu largo e piu potente.";
      }
      if (upgrade.id === "barbaroPower") {
        return "Prossimo: Barbaro piu resistente e cattivo.";
      }
      if (upgrade.id === "cooldownPower") {
        return "Prossimo: abilita pronte piu in fretta.";
      }
      return `Prossimo: potenziamento abilita.`;
    },
    enemyGoldReward(enemy) {
      const multiplier = 1 + this.getUpgradeLevel("goldHunter") * 0.05;
      return Math.round(enemy.reward * multiplier);
    },
    buyUpgrade(upgradeId) {
      const upgrade = this.upgrades.find((item) => item.id === upgradeId);
      if (!upgrade || upgrade.level >= upgrade.max) {
        return;
      }
      const lockedReason = this.upgradeLockedReason(upgrade);
      if (lockedReason) {
        this.message = lockedReason;
        return;
      }

      const cost = this.upgradeCost(upgrade);
      if (this.gold < cost) {
        return;
      }

      const previousCastleMax =
        upgradeId === "walls"
          ? this.castleMaxHpForWallLevel(upgrade.level)
          : this.castle.maxHp;
      const previousManaMax =
        upgradeId === "mana"
          ? this.castleMaxManaForLevel(upgrade.level)
          : this.castle.maxMana;
      this.gold -= cost;
      upgrade.level += 1;

      if (upgradeId === "walls") {
        this.castle.maxHp = this.castleMaxHpForWallLevel(upgrade.level);
        this.castle.hp = Math.min(
          this.castle.maxHp,
          this.castle.hp + Math.max(0, this.castle.maxHp - previousCastleMax)
        );
      }
      if (upgradeId === "mana") {
        this.castle.maxMana = this.castleMaxManaForLevel(upgrade.level);
        this.castle.mana = Math.min(
          this.castle.maxMana,
          this.castle.mana + Math.max(0, this.castle.maxMana - previousManaMax)
        );
      }
      if (upgradeId === "arrows") {
        upgrade.icon = `x${upgrade.level}`;
      }
      if (upgradeId === "barbaroPower") {
        this.updateBarbaroIconForCurrentLevel();
      }

      this.message =
        upgrade.name + " acquistato. Livello " + upgrade.level + ".";
    },
    getUpgradeLevel(upgradeId) {
      const upgrade = this.upgrades.find((item) => item.id === upgradeId);
      return upgrade ? upgrade.level : 0;
    },
    castleMaxHpForWallLevel(level) {
      const index = clamp(Math.floor(level), 1, 25) - 1;
      return CASTLE_HP_BY_WALL_LEVEL[index];
    },
    castleMaxManaForLevel(level) {
      const normalizedLevel = clamp(Math.floor(level), 0, 20);
      return normalizedLevel <= 0 ? 0 : 40 + normalizedLevel * 10;
    },
    manaRegenRateForLevel(level) {
      const normalizedLevel = clamp(Math.floor(level), 0, 10);
      return normalizedLevel <= 0 ? 0 : 1.5 + normalizedLevel * 0.5;
    },
    resetAssaultField() {
      this.assaultBase.maxHp = 240 + this.wave * 36;
      this.assaultBase.hp = this.assaultBase.maxHp;
      this.assaultBase.shotTimer = 0.6;
      this.assaultBase.lavaTimer = 0;
      this.assaultUnits = [];
      this.cheeseProjectiles = [];
      this.effects = [];
      this.particles = [];
    },
    restartGame(resetCampaign = false) {
      if (typeof resetCampaign !== "boolean") {
        resetCampaign = false;
      }
      const startingLevel =
        this.gameMode === "defense"
          ? resetCampaign
            ? 1
            : clamp(this.selectedDefenseLevel, 1, this.defenseCurrentLevel)
          : 1;
      if (this.gameMode === "defense" && resetCampaign) {
        this.defenseCurrentLevel = 1;
        this.selectedDefenseLevel = 1;
      }
      this.wave = startingLevel;
      this.waveTimer = 0;
      this.spawnTimer = 0;
      this.levelKills = 0;
      this.levelGoldEarned = 0;
      this.levelTarget = this.defenseTargetForLevel(this.wave);
      this.bossSpawned = false;
      this.bossActive = false;
      this.castleDamagedThisLevel = false;
      this.resetBertoCastleIntro();
      this.levelComplete = false;
      this.completedLevel = 0;
      this.completedLevelGoldEarned = 0;
      this.completedCastleFlawlessBonus = 0;
      this.levelCompleteQuote = "";
      this.levelDefeatQuote = "";
      this.gamePaused = false;
      if (resetCampaign || this.gameMode !== "defense") {
        this.score = 0;
      }
      this.gameOver = false;
      this.storeOpen = false;
      this.selectedPower =
        this.gameMode === "assault"
          ? "organism"
          : this.gameMode === "berto"
          ? "graterSword"
          : "sghiubbe";
      this.castle.maxHp = this.castleMaxHpForWallLevel(this.wallUpgradeLevel);
      this.castle.hp = this.castle.maxHp;
      this.castle.maxMana = this.castleMaxManaForLevel(
        this.getUpgradeLevel("mana")
      );
      this.castle.mana = this.castle.maxMana;
      this.castle.barrierTimer = 0;
      this.castle.abilitiesDisabledTimer = 0;
      this.updateBarbaroIconForCurrentLevel();
      this.arrows = [];
      this.guidedArrowBursts = [];
      this.enemies = [];
      this.enemyProjectiles = [];
      this.barbaroAllies = [];
      this.activeBertoAbility = null;
      this.bertoAbilityTimer = 0;
      this.pendingBertoAbilityEffects = [];
      this.effects = [];
      this.particles = [];
      this.assaultUnits = [];
      this.cheeseProjectiles = [];
      this.bertoEnemies = [];
      this.bertoSpawnTimer = 0;
      this.berto.hp = this.berto.maxHp;
      this.berto.x = 240;
      this.berto.y = this.canvasSize.height * 0.5;
      this.berto.targetX = this.berto.x;
      this.berto.targetY = this.berto.y;
      [
        ...this.powers,
        ...this.assaultAbilities,
        ...this.bertoAbilities,
      ].forEach((ability) => {
        ability.cooldownLeft = 0;
      });
      if (this.gameMode === "assault") {
        this.resetAssaultField();
        this.message = "L'assalto comincia. Schiera truppe contro la base.";
      } else if (this.gameMode === "berto") {
        this.message = "Berto entra nella fossa. Muovilo col mouse.";
      } else {
        this.message = "Fortezza ricostruita. La difesa riparte.";
      }
    },
    drawGame() {
      const canvas = this.$refs.gameCanvas;
      if (!canvas) {
        return;
      }

      const context = canvas.getContext("2d");
      const width = this.canvasSize.width;
      const height = this.canvasSize.height;
      context.clearRect(0, 0, width, height);
      if (this.gameMode === "assault") {
        this.drawAssaultGame(context, width, height);
        return;
      }
      if (this.gameMode === "berto") {
        this.drawBertoGame(context, width, height);
        return;
      }
      this.drawBackground(context, width, height);
      this.drawCastle(context, height);
      this.drawBertoCastle(context, height);
      this.drawManaTower(context, height);
      this.drawEffects(context, "below");
      this.drawEnemies(context);
      this.drawEffects(context, "above");
      this.drawBarbaroAllies(context);
      this.drawEnemyProjectiles(context);
      this.drawArrows(context);
      this.drawTurret(context);
      this.drawBertoAbilityAnimation(context, height);
      this.drawParticles(context);
      this.drawAimLine(context);
    },
    drawAssaultGame(context, width, height) {
      this.drawBackground(context, width, height);
      context.fillStyle = "#30212a";
      context.fillRect(width - 150, 0, 150, height);
      context.fillStyle = "#f6df7a";
      context.fillRect(width - 132, height * 0.22, 92, height * 0.56);

      const lavaX = width * 0.73;
      const lavaGradient = context.createLinearGradient(
        lavaX - 24,
        0,
        lavaX + 24,
        0
      );
      lavaGradient.addColorStop(0, "rgba(255, 143, 90, 0.15)");
      lavaGradient.addColorStop(0.5, "rgba(255, 90, 42, 0.76)");
      lavaGradient.addColorStop(1, "rgba(255, 207, 90, 0.15)");
      context.fillStyle = lavaGradient;
      context.fillRect(lavaX - 24, 0, 48, height);

      this.assaultUnits.forEach((unit) => {
        context.fillStyle = unit.color;
        context.beginPath();
        context.arc(unit.x, unit.y, unit.radius, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = "rgba(0,0,0,0.35)";
        context.fillRect(unit.x - 22, unit.y - unit.radius - 14, 44, 5);
        context.fillStyle = "#62d7ad";
        context.fillRect(
          unit.x - 22,
          unit.y - unit.radius - 14,
          44 * clamp(unit.hp / unit.maxHp, 0, 1),
          5
        );
      });

      this.cheeseProjectiles.forEach((shot) => {
        context.fillStyle = "#f6df7a";
        context.beginPath();
        context.arc(shot.x, shot.y, shot.radius, 0, Math.PI * 2);
        context.fill();
      });

      this.drawEffects(context);
      this.drawParticles(context);
    },
    drawBertoGame(context, width, height) {
      const gradient = context.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, "#152034");
      gradient.addColorStop(1, "#2f2a24");
      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);

      context.strokeStyle = "rgba(255,255,255,0.05)";
      for (let x = 0; x < width; x += 70) {
        context.beginPath();
        context.moveTo(x, 0);
        context.lineTo(x, height);
        context.stroke();
      }
      for (let y = 0; y < height; y += 70) {
        context.beginPath();
        context.moveTo(0, y);
        context.lineTo(width, y);
        context.stroke();
      }

      this.bertoEnemies.forEach((enemy) => {
        context.fillStyle = enemy.color;
        context.beginPath();
        context.ellipse(
          enemy.x,
          enemy.y,
          enemy.radius * 1.18,
          enemy.radius,
          0,
          0,
          Math.PI * 2
        );
        context.fill();
      });

      context.fillStyle = "#ffcf5a";
      context.beginPath();
      context.arc(this.berto.x, this.berto.y, 22, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "#d8dde3";
      context.fillRect(this.berto.x + 12, this.berto.y - 4, 34, 8);

      this.drawEffects(context);
      this.drawParticles(context);
    },
    drawBackground(context, width, height) {
      const backgroundSprite =
        this.battleBackgroundSprites[this.battleBackgroundTier];
      const spriteReady = backgroundSprite && backgroundSprite.complete;

      if (spriteReady) {
        this.drawCoverImage(context, backgroundSprite, width, height);
        context.fillStyle = "rgba(10, 15, 20, 0.08)";
        context.fillRect(0, 0, width, height);
        return;
      }

      const gradient = context.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, "#18283a");
      gradient.addColorStop(0.48, "#243f35");
      gradient.addColorStop(1, "#3b3428");
      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);

      context.strokeStyle = "rgba(255,255,255,0.05)";
      context.lineWidth = 1;
      for (let x = BASE_WIDTH + 34; x < width; x += 72) {
        context.beginPath();
        context.moveTo(x, 0);
        context.lineTo(x - 80, height);
        context.stroke();
      }

      for (let y = 70; y < height; y += 86) {
        context.fillStyle = "rgba(125, 214, 109, 0.09)";
        context.fillRect(BASE_WIDTH + 20, y, width - BASE_WIDTH - 20, 8);
      }
    },
    drawCoverImage(context, image, width, height) {
      const scale = Math.max(width / image.width, height / image.height);
      const drawWidth = image.width * scale;
      const drawHeight = image.height * scale;
      const offsetX = (width - drawWidth) * 0.5;
      const offsetY = (height - drawHeight) * 0.5;
      context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
    },
    drawCastle(context, height) {
      const backgroundSprite =
        this.battleBackgroundSprites[this.battleBackgroundTier];
      if (backgroundSprite && backgroundSprite.complete) {
        return;
      }

      const spriteReady =
        this.castleBackdropSprite && this.castleBackdropSprite.complete;

      if (spriteReady) {
        const width =
          height *
          (this.castleBackdropSprite.width / this.castleBackdropSprite.height);
        context.save();
        context.shadowColor = "rgba(0, 0, 0, 0.35)";
        context.shadowBlur = 22;
        context.shadowOffsetX = 8;
        context.drawImage(this.castleBackdropSprite, 0, 0, width, height);
        context.restore();

        context.fillStyle = "rgba(0, 0, 0, 0.18)";
        context.fillRect(BASE_WIDTH - 3, 0, 6, height);
        return;
      }

      context.fillStyle = "#3d3742";
      context.fillRect(0, 0, BASE_WIDTH, height);

      context.fillStyle = "#615968";
      for (let y = 0; y < height; y += 52) {
        context.fillRect(0, y, BASE_WIDTH - 26, 34);
        context.fillRect(BASE_WIDTH - 42, y + 10, 42, 24);
      }

      context.fillStyle = "#2c2832";
      context.fillRect(BASE_WIDTH - 20, 0, 20, height);

      context.strokeStyle = "rgba(255, 231, 178, 0.22)";
      context.lineWidth = 3;
      context.beginPath();
      context.moveTo(BASE_WIDTH - 4, 0);
      context.lineTo(BASE_WIDTH - 4, height);
      context.stroke();

      context.fillStyle = "#7ad66d";
      context.beginPath();
      context.arc(52, 96, 24, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "#f5da82";
      context.fillRect(38, 122, 28, 46);

      context.fillStyle = "#67c7ff";
      context.fillRect(34, height - 160, 48, 62);
      context.fillStyle = "#222a3c";
      context.fillRect(43, height - 146, 30, 10);
      context.fillRect(43, height - 126, 30, 10);
    },
    drawBertoCastle(context, canvasHeight) {
      const spriteReady =
        this.bertoCastleSprite && this.bertoCastleSprite.complete;
      if (!spriteReady) {
        return;
      }

      const frame =
        BERTO_CASTLE_FRAMES[this.bertoCastleFrame] || BERTO_CASTLE_FRAMES.sleep;
      const drawHeight = clamp(canvasHeight * 0.13, 84, 112);
      const drawWidth = drawHeight * (frame.w / frame.h);
      const centerX = BASE_WIDTH * 0.43;
      const drawX = centerX - drawWidth / 2;
      const drawY = this.turret.y - drawHeight * 1.18;

      context.save();
      context.shadowColor = "rgba(0, 0, 0, 0.42)";
      context.shadowBlur = 10;
      context.shadowOffsetY = 5;
      context.drawImage(
        this.bertoCastleSprite,
        frame.x,
        frame.y,
        frame.w,
        frame.h,
        drawX,
        drawY,
        drawWidth,
        drawHeight
      );
      context.restore();
    },
    drawManaTower(context, canvasHeight) {
      const spriteReady = this.manaTowerSprite && this.manaTowerSprite.complete;
      if (!spriteReady || this.manaTowerFrameIndex < 0) {
        return;
      }

      const bertoDrawHeight = clamp(canvasHeight * 0.13, 84, 112);
      const bertoTopY = this.turret.y - bertoDrawHeight * 1.18;
      const drawHeight = clamp(canvasHeight * 0.16, 110, 146);
      const drawWidth =
        drawHeight * (MANA_TOWER_FRAME.width / MANA_TOWER_FRAME.cropHeight);
      const drawX = BASE_WIDTH * 0.45 - drawWidth / 2;
      const drawY = bertoTopY - drawHeight * 0.64;
      const sourceX = this.manaTowerFrameIndex * MANA_TOWER_FRAME.width;

      context.save();
      context.shadowColor = "rgba(52, 220, 255, 0.46)";
      context.shadowBlur = 18;
      context.shadowOffsetY = 5;
      context.drawImage(
        this.manaTowerSprite,
        sourceX,
        MANA_TOWER_FRAME.cropY,
        MANA_TOWER_FRAME.width,
        MANA_TOWER_FRAME.cropHeight,
        drawX,
        drawY,
        drawWidth,
        drawHeight
      );
      context.restore();
    },
    drawTurret(context) {
      const angle = Math.atan2(
        this.pointer.y - this.turret.y,
        this.pointer.x - this.turret.x
      );
      const spriteReady = this.crossbowSprite && this.crossbowSprite.complete;

      context.save();
      context.translate(this.turret.x, this.turret.y);
      context.rotate(angle);

      if (spriteReady) {
        const width = 132;
        const height = 84;
        const sourceX = 214;
        const sourceY = 146;
        const sourceWidth = 1130;
        const sourceHeight = 720;
        context.shadowColor = "rgba(0, 0, 0, 0.38)";
        context.shadowBlur = 12;
        context.shadowOffsetY = 6;
        context.drawImage(
          this.crossbowSprite,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          -38,
          -height / 2,
          width,
          height
        );
      } else {
        context.strokeStyle = "#ffe3a3";
        context.lineWidth = 9;
        context.lineCap = "round";
        context.beginPath();
        context.arc(0, 0, 30, -0.9, 0.9);
        context.stroke();
        context.strokeStyle = "#5b3529";
        context.lineWidth = 5;
        context.beginPath();
        context.moveTo(-8, 0);
        context.lineTo(42, 0);
        context.stroke();
      }
      context.restore();

      context.fillStyle = "rgba(29, 21, 32, 0.92)";
      context.beginPath();
      context.arc(this.turret.x, this.turret.y, 23, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "#ffcf5a";
      context.beginPath();
      context.arc(this.turret.x, this.turret.y, 8, 0, Math.PI * 2);
      context.fill();
    },
    drawBertoAbilityAnimation(context, canvasHeight) {
      if (!this.activeBertoAbility) {
        return;
      }
      const animation = BERTO_ABILITY_ANIMATIONS[this.activeBertoAbility];
      const frames = this.bertoAbilityFrames[this.activeBertoAbility];
      if (!animation || !frames || !frames.length) {
        return;
      }

      const elapsed = animation.duration - this.bertoAbilityTimer;
      const progress = clamp(elapsed / animation.duration, 0, 1);
      const frameIndex = Math.min(
        frames.length - 1,
        Math.floor(progress * frames.length)
      );
      const frame = frames[frameIndex];
      if (!frame || !frame.canvas) {
        return;
      }

      const drawHeight = clamp(
        canvasHeight * (animation.drawHeightFactor || 0.26),
        animation.minDrawHeight || 150,
        animation.maxDrawHeight || 218
      );
      const drawWidth = drawHeight * (frame.width / frame.height);
      const drawX = this.turret.x + (animation.drawOffsetX ?? 4) - 24;
      const drawY =
        this.turret.y - drawHeight * (animation.drawYOffsetFactor || 0.58);
      const fadeIn = clamp(progress / 0.12, 0, 1);
      const fadeOut = clamp((1 - progress) / 0.18, 0, 1);

      context.save();
      context.globalAlpha = Math.min(fadeIn, fadeOut);
      context.shadowColor = "rgba(0, 0, 0, 0.42)";
      context.shadowBlur = 12;
      context.shadowOffsetY = 6;
      context.drawImage(frame.canvas, drawX, drawY, drawWidth, drawHeight);
      context.restore();
    },
    drawAimLine(context) {
      if (this.storeOpen || this.gameOver) {
        return;
      }

      context.strokeStyle = "rgba(255, 238, 170, 0.24)";
      context.lineWidth = 2;
      context.setLineDash([9, 12]);
      context.beginPath();
      context.moveTo(this.turret.x, this.turret.y);
      context.lineTo(this.pointer.x, this.pointer.y);
      context.stroke();
      context.setLineDash([]);
    },
    drawArrows(context) {
      this.arrows.forEach((arrow) => {
        context.save();
        context.translate(arrow.x, arrow.y);
        context.rotate(arrow.angle);
        context.strokeStyle = arrow.guided ? "#8be7ff" : "#f7e3a2";
        context.lineWidth = 4;
        context.lineCap = "round";
        context.beginPath();
        context.moveTo(-16, 0);
        context.lineTo(18, 0);
        context.stroke();
        context.fillStyle = arrow.guided ? "#67c7ff" : "#f0bc57";
        context.beginPath();
        context.moveTo(22, 0);
        context.lineTo(10, -6);
        context.lineTo(10, 6);
        context.closePath();
        context.fill();
        context.restore();
      });
    },
    drawEnemyProjectiles(context) {
      this.enemyProjectiles.forEach((shot) => {
        context.save();
        context.translate(shot.x, shot.y);
        if (
          shot.isGolemRock &&
          this.golemArmProjectileGlowingSprite &&
          this.golemArmProjectileGlowingSprite.complete &&
          this.golemArmProjectileSprite &&
          this.golemArmProjectileSprite.complete
        ) {
          const glowWidth = shot.radius * 6.8;
          const glowHeight = shot.radius * 3.25;
          const coreWidth = shot.radius * 4.6;
          const coreHeight = shot.radius * 1.85;
          context.rotate(shot.angle || 0);
          context.globalAlpha = 0.78;
          context.drawImage(
            this.golemArmProjectileGlowingSprite,
            GOLEM_ARM_GLOWING_CROP.x,
            GOLEM_ARM_GLOWING_CROP.y,
            GOLEM_ARM_GLOWING_CROP.w,
            GOLEM_ARM_GLOWING_CROP.h,
            -glowWidth * 0.56,
            -glowHeight * 0.5,
            glowWidth,
            glowHeight
          );
          context.globalAlpha = 1;
          context.drawImage(
            this.golemArmProjectileSprite,
            GOLEM_ARM_PROJECTILE_CROP.x,
            GOLEM_ARM_PROJECTILE_CROP.y,
            GOLEM_ARM_PROJECTILE_CROP.w,
            GOLEM_ARM_PROJECTILE_CROP.h,
            -coreWidth * 0.54,
            -coreHeight * 0.5,
            coreWidth,
            coreHeight
          );
          context.restore();
          return;
        }
        context.fillStyle = shot.color;
        context.beginPath();
        context.arc(0, 0, shot.radius, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = "rgba(120, 65, 24, 0.28)";
        context.beginPath();
        context.arc(
          -shot.radius * 0.22,
          -shot.radius * 0.12,
          shot.radius * 0.24,
          0,
          Math.PI * 2
        );
        context.fill();
        if (shot.isCatapult) {
          context.strokeStyle = "rgba(255, 143, 90, 0.65)";
          context.lineWidth = 4;
          context.beginPath();
          context.arc(0, 0, shot.radius + 5, 0, Math.PI * 2);
          context.stroke();
        }
        if (shot.disablesAbilities) {
          context.strokeStyle = "rgba(141, 92, 255, 0.85)";
          context.lineWidth = 3;
          context.beginPath();
          context.moveTo(-shot.radius - 4, 0);
          context.lineTo(shot.radius + 4, 0);
          context.moveTo(0, -shot.radius - 4);
          context.lineTo(0, shot.radius + 4);
          context.stroke();
        }
        context.restore();
      });
    },
    cheeseSoldierAnimationForEnemy(enemy) {
      if (enemy.hp <= 0 || enemy.defeated) {
        return "death";
      }
      if (enemy.hitAnimTimer > 0) {
        return "hit";
      }
      if (enemy.attackAnimTimer > 0) {
        return "attack";
      }
      if (enemy.allyTargetId) {
        return "idle";
      }
      if (enemy.x > BASE_WIDTH + enemy.radius + 6) {
        return "walk";
      }
      return "idle";
    },
    cheeseSoldierFrameIndex(enemy, animationKey, animation) {
      const frameCount = CHEESE_SOLDIER_FRAMES[animation.source].length;
      if (animation.loop) {
        return Math.floor(enemy.animTime * animation.fps) % frameCount;
      }
      if (animationKey === "death") {
        const elapsed = animation.duration - (enemy.deathTimer || 0);
        return clamp(
          Math.floor((elapsed / animation.duration) * frameCount),
          0,
          frameCount - 1
        );
      }
      if (animationKey === "hit") {
        const elapsed = animation.duration - (enemy.hitAnimTimer || 0);
        return clamp(
          Math.floor((elapsed / animation.duration) * frameCount),
          0,
          frameCount - 1
        );
      }
      const elapsed = animation.duration - (enemy.attackAnimTimer || 0);
      return clamp(
        Math.floor((elapsed / animation.duration) * frameCount),
        0,
        frameCount - 1
      );
    },
    drawCheeseSoldierEnemy(context, enemy) {
      const animationKey = this.cheeseSoldierAnimationForEnemy(enemy);
      const animation =
        CHEESE_SOLDIER_ANIMATIONS[animationKey] ||
        CHEESE_SOLDIER_ANIMATIONS.idle;
      const sprite = this.cheeseSoldierSprites[animation.source];
      if (!sprite || !sprite.complete) {
        return false;
      }

      const frameIndex = Math.min(
        this.cheeseSoldierFrameIndex(enemy, animationKey, animation),
        CHEESE_SOLDIER_FRAMES[animation.source].length - 1
      );
      const sourceFrame = CHEESE_SOLDIER_FRAMES[animation.source][frameIndex];
      const sourceWidth = sourceFrame.w;
      const sourceHeight = sourceFrame.h;
      const spriteScale =
        enemy.radius * (animationKey === "death" ? 0.029 : 0.031);
      const drawWidth = sourceWidth * spriteScale;
      const drawHeight = sourceHeight * spriteScale;
      enemy.spriteTopY = enemy.y - drawHeight * 0.9;

      context.save();
      context.translate(enemy.x, enemy.y);
      context.scale(-1, 1);
      context.rotate(Math.sin(enemy.wobble) * 0.025);
      context.shadowColor = "rgba(0, 0, 0, 0.34)";
      context.shadowBlur = 10;
      context.shadowOffsetY = 7;
      context.drawImage(
        sprite,
        sourceFrame.x,
        sourceFrame.y,
        sourceWidth,
        sourceHeight,
        -drawWidth / 2,
        -drawHeight * (animationKey === "death" ? 0.62 : 0.82),
        drawWidth,
        drawHeight
      );
      context.restore();
      return true;
    },
    wizardAnimationForEnemy(enemy) {
      if (enemy.hp <= 0 || enemy.defeated) {
        return "death";
      }
      if (enemy.attackAnimTimer > 0) {
        return enemy.attackKind === "melee" ? "attack1" : "attack2";
      }
      if (enemy.hitAnimTimer > 0) {
        return "hit";
      }
      if (enemy.allyTargetId) {
        return "idle";
      }
      if (enemy.x > this.enemyRangedStopX(enemy) + 6) {
        return "run";
      }
      return "idle";
    },
    wizardFrameIndex(enemy, animationKey, animation) {
      const frameCount = animation.frames;
      if (animation.loop) {
        return Math.floor(enemy.animTime * animation.fps) % frameCount;
      }
      if (animationKey === "death") {
        const elapsed = animation.duration - (enemy.deathTimer || 0);
        return clamp(
          Math.floor((elapsed / animation.duration) * frameCount),
          0,
          frameCount - 1
        );
      }
      if (animationKey === "hit") {
        const elapsed = animation.duration - (enemy.hitAnimTimer || 0);
        return clamp(
          Math.floor((elapsed / animation.duration) * frameCount),
          0,
          frameCount - 1
        );
      }
      const elapsed = animation.duration - (enemy.attackAnimTimer || 0);
      return clamp(
        Math.floor((elapsed / animation.duration) * frameCount),
        0,
        frameCount - 1
      );
    },
    drawWizardEnemy(context, enemy) {
      const animationKey = this.wizardAnimationForEnemy(enemy);
      const animation = WIZARD_ANIMATIONS[animationKey];
      const sprite = this.wizardSprites[animation.source];
      if (!sprite || !sprite.complete) {
        return false;
      }

      const frameCount = Math.max(1, Math.round(sprite.width / sprite.height));
      const frameWidth = sprite.width / frameCount;
      const frameHeight = sprite.height;
      const frameIndex = Math.min(
        this.wizardFrameIndex(enemy, animationKey, animation),
        frameCount - 1
      );
      const drawHeight = enemy.radius * (enemy.isBoss ? 13.95 : 12.75);
      const drawWidth = drawHeight * (frameWidth / frameHeight);
      enemy.spriteTopY = enemy.y - drawHeight * 0.38;

      context.save();
      context.translate(enemy.x, enemy.y);
      context.rotate(Math.sin(enemy.wobble) * 0.04);
      context.drawImage(
        sprite,
        frameIndex * frameWidth,
        0,
        frameWidth,
        frameHeight,
        -drawWidth / 2,
        -drawHeight * 0.82,
        drawWidth,
        drawHeight
      );
      context.restore();
      return true;
    },
    golemAnimationForEnemy(enemy) {
      if (enemy.hp <= 0 || enemy.defeated) {
        return "death";
      }
      if (enemy.hitAnimTimer > 0) {
        return "hurt";
      }
      if (
        enemy.golemPhase === "laserCharge" ||
        enemy.golemPhase === "laserRecover"
      ) {
        return "laser";
      }
      if (enemy.attackAnimTimer > 0) {
        return enemy.golemState === "laser" ? "laser" : "rock";
      }
      if (
        enemy.golemPhase === "approach" ||
        enemy.golemPhase === "retreat" ||
        enemy.x > this.golemFarStopX() + 6
      ) {
        return "walk";
      }
      return "idle";
    },
    golemFrameIndex(enemy, animationKey, animation) {
      if (animationKey === "idle" || animationKey === "walk") {
        return animation.frames[
          Math.floor(enemy.animTime * animation.fps) % animation.frames.length
        ];
      }
      if (animationKey === "death") {
        const elapsed = animation.duration - (enemy.deathTimer || 0);
        return animation.frames[
          clamp(
            Math.floor(
              (elapsed / animation.duration) * animation.frames.length
            ),
            0,
            animation.frames.length - 1
          )
        ];
      }
      if (animationKey === "hurt") {
        const elapsed = animation.duration - (enemy.hitAnimTimer || 0);
        return animation.frames[
          clamp(
            Math.floor(
              (elapsed / animation.duration) * animation.frames.length
            ),
            0,
            animation.frames.length - 1
          )
        ];
      }
      const elapsed =
        GOLEM_BOSS_ANIMATIONS[animationKey].duration -
        (enemy.attackAnimTimer || 0);
      return animation.frames[
        clamp(
          Math.floor((elapsed / animation.duration) * animation.frames.length),
          0,
          animation.frames.length - 1
        )
      ];
    },
    drawGolemBossEnemy(context, enemy) {
      const sprite = this.golemBossSprite;
      if (!sprite || !sprite.complete) {
        return false;
      }
      const animationKey = this.golemAnimationForEnemy(enemy);
      const animation =
        GOLEM_BOSS_ANIMATIONS[animationKey] || GOLEM_BOSS_ANIMATIONS.idle;
      const frameColumn = this.golemFrameIndex(enemy, animationKey, animation);
      const frameSize = GOLEM_BOSS_CELL_SIZE;
      const drawHeight = enemy.radius * 5.15;
      const drawWidth = drawHeight;
      enemy.spriteTopY = enemy.y - drawHeight * 0.6;

      context.save();
      context.translate(enemy.x, enemy.y);
      context.scale(-1, 1);
      context.rotate(Math.sin(enemy.wobble) * 0.025);
      context.shadowColor = "rgba(0, 0, 0, 0.38)";
      context.shadowBlur = 12;
      context.shadowOffsetY = 8;
      context.drawImage(
        sprite,
        frameColumn * frameSize,
        animation.row * frameSize,
        frameSize,
        frameSize,
        -drawWidth / 2,
        -drawHeight * 0.7,
        drawWidth,
        drawHeight
      );
      context.restore();
      return true;
    },
    drawEnemyHealthBar(context, enemy, healthPercent, barWidth, y = null) {
      if (enemy.hp <= 0 || enemy.defeated) {
        return;
      }
      const barHeight = enemy.isBoss ? 7.2 : 4.8;
      const barY =
        y === null || y === undefined ? enemy.y - enemy.radius - 20 : y;
      context.fillStyle = "rgba(0, 0, 0, 0.38)";
      context.fillRect(enemy.x - barWidth / 2, barY, barWidth, barHeight);
      context.fillStyle = enemy.isBoss
        ? enemy.isSuperBoss
          ? "#ff4f5f"
          : "#ff8f5a"
        : enemy.slow > 0
        ? "#7ad66d"
        : "#ffcf5a";
      context.fillRect(
        enemy.x - barWidth / 2,
        barY,
        barWidth * healthPercent,
        barHeight
      );
      if (enemy.isBoss) {
        context.fillStyle = "#fff7e8";
        context.font = "700 13px sans-serif";
        context.textAlign = "center";
        context.fillText(
          enemy.isSuperBoss ? "SUPERBOSS" : "BOSS",
          enemy.x,
          barY - 8
        );
      }
    },
    drawEnemies(context) {
      this.enemies.forEach((enemy) => {
        const healthPercent = clamp(enemy.hp / enemy.maxHp, 0, 1);
        const barWidth = enemy.isBoss ? enemy.radius * 2.4 : 50;
        if (
          enemy.archetype === "wizard" &&
          this.drawWizardEnemy(context, enemy)
        ) {
          this.drawEnemyHealthBar(
            context,
            enemy,
            healthPercent,
            barWidth,
            enemy.spriteTopY - 10
          );
          return;
        }
        if (
          enemy.archetype === "golemBoss" &&
          this.drawGolemBossEnemy(context, enemy)
        ) {
          this.drawEnemyHealthBar(
            context,
            enemy,
            healthPercent,
            barWidth,
            enemy.spriteTopY - 8
          );
          return;
        }
        if (
          enemy.spriteSet === "cheeseSoldier" &&
          this.drawCheeseSoldierEnemy(context, enemy)
        ) {
          this.drawEnemyHealthBar(
            context,
            enemy,
            healthPercent,
            barWidth,
            enemy.spriteTopY - 7
          );
          return;
        }
        context.save();
        context.translate(enemy.x, enemy.y);
        context.rotate(Math.sin(enemy.wobble) * 0.12);
        context.fillStyle = enemy.rind;
        context.beginPath();
        context.ellipse(
          0,
          0,
          enemy.radius * 1.12,
          enemy.radius,
          0,
          0,
          Math.PI * 2
        );
        context.fill();
        context.fillStyle = enemy.color;
        context.beginPath();
        context.moveTo(-enemy.radius * 0.75, -enemy.radius * 0.65);
        context.lineTo(enemy.radius * 0.85, -enemy.radius * 0.35);
        context.lineTo(enemy.radius * 0.55, enemy.radius * 0.75);
        context.lineTo(-enemy.radius * 0.95, enemy.radius * 0.52);
        context.closePath();
        context.fill();
        context.fillStyle = "rgba(70, 44, 22, 0.28)";
        context.beginPath();
        context.arc(
          -enemy.radius * 0.22,
          -enemy.radius * 0.12,
          enemy.radius * 0.16,
          0,
          Math.PI * 2
        );
        context.arc(
          enemy.radius * 0.36,
          enemy.radius * 0.18,
          enemy.radius * 0.12,
          0,
          Math.PI * 2
        );
        context.fill();
        if (enemy.archetype === "archer") {
          context.strokeStyle = "#6b3f25";
          context.lineWidth = 3;
          context.beginPath();
          context.arc(enemy.radius * 0.7, 0, enemy.radius * 0.62, -1.15, 1.15);
          context.stroke();
          context.fillStyle = "#fff1c7";
          context.fillRect(enemy.radius * 0.45, -2, enemy.radius * 0.86, 4);
        }
        if (enemy.archetype === "orc") {
          context.fillStyle = "rgba(60, 35, 20, 0.46)";
          context.fillRect(-enemy.radius * 0.95, -enemy.radius * 0.82, 12, 24);
          context.fillRect(enemy.radius * 0.65, -enemy.radius * 0.82, 12, 24);
        }
        if (enemy.archetype === "catapult") {
          context.strokeStyle = "#5b3529";
          context.lineWidth = 5;
          context.beginPath();
          context.moveTo(-enemy.radius * 0.9, enemy.radius * 0.72);
          context.lineTo(enemy.radius * 0.92, enemy.radius * 0.72);
          context.moveTo(-enemy.radius * 0.28, enemy.radius * 0.7);
          context.lineTo(enemy.radius * 0.72, -enemy.radius * 0.72);
          context.stroke();
        }
        if (enemy.archetype === "ninja") {
          context.strokeStyle = "#2f3443";
          context.lineWidth = 7;
          context.beginPath();
          context.moveTo(-enemy.radius * 0.8, -enemy.radius * 0.25);
          context.lineTo(enemy.radius * 0.82, -enemy.radius * 0.25);
          context.stroke();
        }
        if (enemy.archetype === "dragon") {
          context.fillStyle = "rgba(255, 143, 90, 0.72)";
          context.beginPath();
          context.moveTo(-enemy.radius * 0.22, 0);
          context.lineTo(-enemy.radius * 1.25, -enemy.radius * 0.82);
          context.lineTo(-enemy.radius * 0.62, enemy.radius * 0.06);
          context.lineTo(-enemy.radius * 1.2, enemy.radius * 0.82);
          context.closePath();
          context.fill();
          context.fillStyle = "#ffcf5a";
          context.beginPath();
          context.moveTo(enemy.radius * 0.72, 0);
          context.lineTo(enemy.radius * 1.42, -enemy.radius * 0.28);
          context.lineTo(enemy.radius * 1.42, enemy.radius * 0.28);
          context.closePath();
          context.fill();
        }
        if (enemy.archetype === "wizard") {
          context.fillStyle = "#08060d";
          context.beginPath();
          context.moveTo(0, -enemy.radius * 1.25);
          context.lineTo(enemy.radius * 0.9, enemy.radius * 0.78);
          context.lineTo(-enemy.radius * 0.9, enemy.radius * 0.78);
          context.closePath();
          context.fill();
          context.fillStyle = "#8d5cff";
          context.beginPath();
          context.arc(
            0,
            -enemy.radius * 0.24,
            enemy.radius * 0.16,
            0,
            Math.PI * 2
          );
          context.fill();
        }
        context.restore();

        context.fillStyle = "rgba(0, 0, 0, 0.38)";
        context.fillRect(
          enemy.x - barWidth / 2,
          enemy.y - enemy.radius - 20,
          barWidth,
          enemy.isBoss ? 9 : 6
        );
        context.fillStyle = enemy.isBoss
          ? enemy.isSuperBoss
            ? "#ff4f5f"
            : "#ff8f5a"
          : enemy.slow > 0
          ? "#7ad66d"
          : "#ffcf5a";
        context.fillRect(
          enemy.x - barWidth / 2,
          enemy.y - enemy.radius - 20,
          barWidth * healthPercent,
          enemy.isBoss ? 9 : 6
        );
        if (enemy.isBoss) {
          context.fillStyle = "#fff7e8";
          context.font = "700 13px sans-serif";
          context.textAlign = "center";
          context.fillText(
            enemy.isSuperBoss ? "SUPERBOSS" : "BOSS",
            enemy.x,
            enemy.y - enemy.radius - 28
          );
        }
      });
    },
    drawBarbaroAllies(context) {
      this.barbaroAllies.forEach((ally) => {
        const healthPercent = clamp(ally.hp / ally.maxHp, 0, 1);
        const barbaroTier = ally.barbaroTier || 0;
        const barbaroSprite =
          this.barbaroSprites[barbaroTier] || this.barbaroSprite;
        const spriteReady = barbaroSprite && barbaroSprite.complete;
        const animation =
          BARBARO_ANIMATIONS[ally.state] || BARBARO_ANIMATIONS.idle;
        const framePosition =
          ally.state === "death"
            ? clamp(
                Math.floor(
                  (1 - clamp(ally.deathTimer / 0.85, 0, 1)) *
                    animation.frames.length
                ),
                0,
                animation.frames.length - 1
              )
            : Math.floor(
                (ally.animTime * animation.fps + ally.frameOffset) %
                  animation.frames.length
              );
        const frameIndex = animation.frames[framePosition];
        const sourceFrame = BARBARO_FRAMES[animation.source][frameIndex];
        const sourceFrameRect = spriteReady
          ? this.barbaroFrameRect(sourceFrame, barbaroSprite, barbaroTier)
          : null;
        const barbaroFrames =
          this.barbaroFrameCanvasesByTier[barbaroTier] ||
          this.barbaroFrameCanvases;
        const cleanFrame =
          barbaroFrames[animation.source] &&
          barbaroFrames[animation.source][frameIndex];
        const sourceCanvas = cleanFrame && cleanFrame.canvas;
        const sourceWidth = cleanFrame
          ? cleanFrame.width
          : sourceFrameRect
          ? sourceFrameRect.w
          : BARBARO_NORMALIZED_FRAME.width;
        const sourceHeight = cleanFrame
          ? cleanFrame.height
          : sourceFrameRect
          ? sourceFrameRect.h
          : BARBARO_NORMALIZED_FRAME.height;
        const drawHeight =
          ally.state === "death" ? 104 : ally.state === "attack" ? 172 : 156;
        const drawWidth = drawHeight * (sourceWidth / sourceHeight);
        const bob =
          ally.state === "walk"
            ? Math.sin(ally.animTime * 10) * 3
            : ally.state === "idle"
            ? Math.sin(ally.animTime * 4) * 1.5
            : 0;

        context.save();
        context.translate(ally.x, ally.y + bob);
        context.shadowColor = "rgba(0, 0, 0, 0.32)";
        context.shadowBlur = 12;
        context.shadowOffsetY = 7;

        if (sourceCanvas) {
          context.drawImage(
            sourceCanvas,
            -drawWidth / 2,
            -drawHeight * 0.74,
            drawWidth,
            drawHeight
          );
        } else if (spriteReady) {
          context.drawImage(
            barbaroSprite,
            sourceFrameRect.x,
            sourceFrameRect.y,
            sourceFrameRect.w,
            sourceFrameRect.h,
            -drawWidth / 2,
            -drawHeight * 0.74,
            drawWidth,
            drawHeight
          );
        } else {
          context.fillStyle = "#b65c24";
          context.beginPath();
          context.arc(0, -18, 24, 0, Math.PI * 2);
          context.fill();
          context.strokeStyle = "#d8dde3";
          context.lineWidth = 7;
          context.beginPath();
          context.moveTo(4, -22);
          context.lineTo(48, -38);
          context.stroke();
        }

        if (ally.attackFlash > 0) {
          context.globalAlpha = clamp(ally.attackFlash / 0.18, 0, 1);
          context.strokeStyle = "#ffcf5a";
          context.lineWidth = 5;
          context.beginPath();
          context.arc(22, -28, 30, -0.7, 0.8);
          context.stroke();
        }
        context.restore();

        if (ally.hp > 0) {
          const barWidth = 64;
          const barHeight = 5.6;
          const barY = ally.y - drawHeight * 0.74 - 10;
          context.fillStyle = "rgba(0, 0, 0, 0.42)";
          context.fillRect(ally.x - barWidth / 2, barY, barWidth, barHeight);
          context.fillStyle = "#62d7ad";
          context.fillRect(
            ally.x - barWidth / 2,
            barY,
            barWidth * healthPercent,
            barHeight
          );
          context.strokeStyle = "rgba(255, 247, 232, 0.26)";
          context.lineWidth = 1;
          context.strokeRect(ally.x - barWidth / 2, barY, barWidth, barHeight);
        }
      });
    },
    drawEffects(context, layer = "all") {
      this.effects.forEach((effect) => {
        const isAboveEffect =
          effect.type === "golemLaserCharge" || effect.type === "golemLaser";
        if (
          (layer === "below" && isAboveEffect) ||
          (layer === "above" && !isAboveEffect)
        ) {
          return;
        }
        const alpha = clamp(effect.life / effect.maxLife, 0, 1);
        context.save();
        if (effect.type === "field") {
          context.globalAlpha = alpha * 0.16;
          context.fillStyle = effect.color;
          context.beginPath();
          context.arc(effect.x, effect.y, effect.radius, 0, Math.PI * 2);
          context.fill();
        } else if (effect.type === "golemLaserCharge") {
          const progress = 1 - alpha;
          context.globalAlpha = Math.min(1, clamp(progress / 0.12, 0, 1));
          if (this.golemLaserSprite && this.golemLaserSprite.complete) {
            const frameHeight = GOLEM_LASER_FRAME_HEIGHT;
            const frameIndex = clamp(Math.floor(progress * 7), 0, 6);
            context.drawImage(
              this.golemLaserSprite,
              0,
              frameIndex * frameHeight,
              110,
              frameHeight,
              effect.x - 56,
              effect.y - 68,
              112,
              136
            );
          }
          context.shadowColor = "rgba(105, 231, 255, 0.85)";
          context.shadowBlur = 22;
          context.fillStyle = "rgba(180, 250, 255, 0.82)";
          context.beginPath();
          context.arc(effect.x, effect.y, 10 + progress * 18, 0, Math.PI * 2);
          context.fill();
        } else if (effect.type === "golemLaser") {
          const laserLength = Math.max(20, effect.x - effect.targetX);
          const progress = 1 - alpha;
          const beamAlpha = Math.min(
            clamp(progress / 0.08, 0, 1),
            clamp(effect.life / 0.22, 0, 1)
          );
          context.globalAlpha = beamAlpha;
          if (this.golemLaserSprite && this.golemLaserSprite.complete) {
            const frameHeight = GOLEM_LASER_FRAME_HEIGHT;
            const frameIndex = 7 + (Math.floor(progress * 18) % 3);
            context.save();
            context.translate(effect.x, effect.y - frameHeight * 0.22);
            context.scale(-1, 1);
            context.drawImage(
              this.golemLaserSprite,
              0,
              frameIndex * frameHeight,
              this.golemLaserSprite.width,
              frameHeight,
              0,
              0,
              laserLength,
              46
            );
            context.restore();
          }
          const gradient = context.createLinearGradient(
            effect.x,
            effect.y,
            effect.targetX,
            effect.targetY
          );
          gradient.addColorStop(0, "rgba(126, 239, 255, 0.95)");
          gradient.addColorStop(0.55, "rgba(222, 252, 255, 0.78)");
          gradient.addColorStop(1, "rgba(105, 231, 255, 0.08)");
          context.strokeStyle = gradient;
          context.lineCap = "round";
          context.lineWidth = 16;
          context.beginPath();
          context.moveTo(effect.x, effect.y);
          context.lineTo(effect.targetX, effect.targetY);
          context.stroke();
          context.globalAlpha = beamAlpha * 0.48;
          context.lineWidth = 34;
          context.beginPath();
          context.moveTo(effect.x, effect.y);
          context.lineTo(effect.targetX, effect.targetY);
          context.stroke();
        } else {
          context.globalAlpha = alpha;
          context.strokeStyle = effect.color;
          context.lineWidth = effect.type === "wave" ? 18 : 5;
          context.lineCap = "round";
          context.beginPath();
          if (effect.type === "wave") {
            context.arc(
              effect.x,
              effect.y,
              effect.radius,
              -Math.PI * 0.34,
              Math.PI * 0.34
            );
          } else {
            context.arc(effect.x, effect.y, effect.radius, 0, Math.PI * 2);
          }
          context.stroke();
          if (effect.type === "wave") {
            context.globalAlpha = alpha * 0.36;
            context.lineWidth = 7;
            context.beginPath();
            context.arc(
              effect.x,
              effect.y,
              effect.radius + 24,
              -Math.PI * 0.3,
              Math.PI * 0.3
            );
            context.stroke();
          }
        }
        context.restore();
      });
    },
    drawParticles(context) {
      this.particles.forEach((particle) => {
        context.globalAlpha = clamp(particle.life / 0.6, 0, 1);
        context.fillStyle = particle.color;
        context.beginPath();
        context.arc(particle.x, particle.y, 3.5, 0, Math.PI * 2);
        context.fill();
        context.globalAlpha = 1;
      });
    },
  },
};
</script>

<style scoped>
.secret-page {
  box-sizing: border-box;
  display: grid;
  width: 100%;
  height: calc(100vh - 100px);
  min-height: 620px;
  padding: 10px 24px 16px;
  align-self: stretch;
  overflow-y: auto;
  overflow-x: hidden;
  place-items: start center;
  color: #fff7e8;
  background: radial-gradient(
      circle at 50% 28%,
      rgba(255, 207, 90, 0.1),
      transparent 28%
    ),
    linear-gradient(135deg, #121827 0%, #172536 48%, #182f28 100%);
}

.secret-page-menu {
  height: 100vh;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  place-items: stretch;
  background: #0b111d;
}

.gate-card {
  width: min(520px, 100%);
  margin-top: clamp(72px, 18vh, 160px);
  padding: 34px;
  text-align: center;
  border: 1px solid rgba(255, 239, 210, 0.14);
  border-radius: 10px;
  background: linear-gradient(
    145deg,
    rgba(22, 31, 47, 0.94),
    rgba(26, 44, 39, 0.92)
  );
  box-shadow: 0 22px 70px rgba(0, 0, 0, 0.24);
}

.eyebrow {
  margin: 0 0 8px;
  color: #ffcf5a;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 0.72rem;
  font-weight: 800;
}

h1,
h2,
p {
  margin-top: 0;
}

h1,
h2 {
  letter-spacing: 0;
}

.gate-card h1 {
  margin-bottom: 26px;
  font-size: clamp(2rem, 4vw, 3.1rem);
  line-height: 1.05;
}

.gate-form {
  display: flex;
  gap: 12px;
  justify-content: center;
  width: 100%;
}

.gate-form input {
  flex: 1 1 260px;
  min-width: 0;
  padding: 13px 15px;
  color: #fff7e8;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  outline: none;
}

.primary-button,
.secondary-button,
.icon-button {
  border-radius: 8px;
  cursor: pointer;
  font-weight: 800;
}

.primary-button,
.secondary-button {
  padding: 12px 18px;
}

.primary-button {
  border: 0;
  color: #1d1721;
  background: linear-gradient(135deg, #ffcf5a, #ff8f5a);
  box-shadow: 0 14px 34px rgba(255, 143, 90, 0.24);
}

.secondary-button {
  color: #fff7e8;
  border: 1px solid rgba(255, 247, 232, 0.18);
  background: rgba(255, 255, 255, 0.08);
}

.error {
  margin: 16px 0 0;
  color: #ff8f8f;
  font-weight: 800;
}

.hub-shell {
  position: relative;
  box-sizing: border-box;
  width: min(1840px, 100%);
  padding: 22px 28px 26px;
  border: 1px solid rgba(255, 239, 210, 0.14);
  border-radius: 12px;
  background: rgba(11, 17, 29, 0.58);
  box-shadow: 0 26px 90px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(10px);
}

.hub-shell-menu {
  display: grid;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: radial-gradient(
      circle at 50% 38%,
      rgba(255, 207, 90, 0.08),
      transparent 36%
    ),
    linear-gradient(180deg, #070b12 0%, #101826 58%, #080b12 100%);
  box-shadow: none;
}

.hub-topbar,
.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.hub-topbar h1,
.section-heading h2 {
  margin: 0;
  line-height: 1.02;
}

.hub-topbar h1 {
  font-size: clamp(1.75rem, 2.25vw, 2.45rem);
}

.hub-title-cluster {
  display: flex;
  align-items: center;
  gap: 14px;
}

.hub-wallet {
  display: inline-flex;
  min-width: 128px;
  padding: 10px 14px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  border: 1px solid rgba(255, 247, 232, 0.13);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
}

.hub-wallet span,
.upgrade-level {
  display: block;
  margin: 0;
  color: rgba(255, 247, 232, 0.68);
  font-size: 0.78rem;
}

.upgrade-level {
  display: inline-flex;
  width: fit-content;
  margin: 0 0 8px;
  padding: 4px 8px;
  align-items: center;
  color: #ffcf5a;
  border: 1px solid rgba(255, 207, 90, 0.34);
  border-radius: 999px;
  background: rgba(255, 207, 90, 0.11);
  font-size: 0.84rem;
  font-weight: 1000;
  line-height: 1;
}

.hub-wallet strong {
  color: #ffcf5a;
  font-size: 1.35rem;
  line-height: 1;
}

.currency-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.currency-label.centered {
  justify-content: center;
}

.coin-icon {
  display: inline-block;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 28%, #fff1a8 0 16%, transparent 17%),
    linear-gradient(135deg, #ffe27a, #c98221);
  box-shadow: inset 0 -2px 0 rgba(105, 62, 15, 0.24),
    0 1px 4px rgba(0, 0, 0, 0.28);
  vertical-align: -3px;
}

.swords-icon {
  position: relative;
  display: inline-block;
  width: 18px;
  height: 18px;
  transform: translateY(3px);
}

.swords-icon::before,
.swords-icon::after {
  position: absolute;
  left: 8px;
  top: 1px;
  width: 3px;
  height: 17px;
  border-radius: 2px;
  content: "";
  background: linear-gradient(180deg, #edf4ff 0 58%, #a86a25 58% 100%);
  box-shadow: 0 0 0 1px rgba(8, 12, 17, 0.36);
  transform-origin: 50% 82%;
}

.swords-icon::before {
  transform: rotate(42deg);
}

.swords-icon::after {
  transform: rotate(-42deg);
}

.hub-wallet .currency-label,
.shop-status-item .currency-label,
.resource-stat .currency-label {
  display: inline-flex;
}

.hub-menu {
  display: grid;
  grid-template-columns: minmax(820px, 1120px) minmax(360px, 520px);
  align-items: center;
  justify-content: center;
  gap: clamp(36px, 5vw, 92px);
  margin-top: 0;
}

.hub-menu.title-screen {
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  padding: 0;
  border: 0;
  color: inherit;
  background: transparent;
  cursor: pointer;
}

.hub-shell-menu .hub-menu {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  grid-template-columns: minmax(0, 1fr) clamp(340px, 27vw, 520px);
  align-items: stretch;
  justify-content: stretch;
  gap: clamp(14px, 1.8vw, 38px);
}

.hub-shell-menu .hub-menu.title-screen {
  display: block;
}

.castle-showcase {
  position: relative;
  overflow: hidden;
  display: grid;
  width: clamp(820px, 58vw, 1120px);
  aspect-ratio: 3 / 2;
  margin: 0;
  place-items: center;
  border: 1px solid rgba(255, 247, 232, 0.12);
  border-radius: 12px;
  background: linear-gradient(
    180deg,
    rgba(16, 24, 45, 0.95) 0%,
    rgba(23, 40, 58, 0.86) 52%,
    rgba(10, 16, 24, 0.95) 100%
  );
}

.hub-shell-menu .castle-showcase {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  max-width: 100%;
  aspect-ratio: auto;
  justify-self: stretch;
  align-self: stretch;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.castle-showcase::before {
  position: absolute;
  inset: 0;
  content: "";
  background: radial-gradient(
      circle at 70% 18%,
      rgba(255, 207, 90, 0.12),
      transparent 18%
    ),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04), transparent 18% 82%);
}

.hub-shell-menu .castle-showcase::after {
  position: absolute;
  inset: 0;
  z-index: 2;
  content: "";
  pointer-events: none;
  background: linear-gradient(
      180deg,
      rgba(4, 7, 12, 0.54) 0%,
      rgba(4, 7, 12, 0.26) 18%,
      transparent 36% 68%,
      rgba(4, 7, 12, 0.42) 100%
    ),
    radial-gradient(ellipse at 50% 13%, rgba(4, 7, 12, 0.42), transparent 38%);
}

.castle-artwork {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.hub-shell-menu .castle-artwork {
  object-fit: cover;
  transition: transform 0.55s ease, filter 0.22s ease;
  transform: scale(1);
  will-change: transform;
}

.menu-title-overlay {
  position: absolute;
  z-index: 4;
  top: clamp(24px, 4.2vh, 48px);
  left: 50%;
  display: grid;
  gap: 2px;
  width: min(780px, calc(100% - 48px));
  padding: 0;
  justify-items: center;
  color: #fff7e8;
  border: 0;
  background: transparent;
  box-shadow: none;
  font-size: clamp(1.85rem, 3.2vw, 4rem);
  font-weight: 900;
  letter-spacing: 0;
  line-height: 0.94;
  text-align: center;
  text-shadow: 0 3px 0 rgba(0, 0, 0, 0.38), 0 10px 30px rgba(0, 0, 0, 0.76),
    0 0 32px rgba(255, 247, 232, 0.18);
  transform: translateX(-50%);
}

.menu-title-overlay::before {
  content: none;
}

.menu-title-overlay > span,
.menu-title-overlay > strong {
  display: block;
}

.menu-title-overlay > strong {
  color: #fff7e8;
  font-size: 1.08em;
}

.menu-start-overlay {
  position: absolute;
  z-index: 4;
  left: 50%;
  bottom: clamp(52px, 8vh, 104px);
  display: inline-grid;
  min-width: 0;
  padding: 0;
  place-items: center;
  color: #ffe27a;
  border: 0;
  background: transparent;
  box-shadow: none;
  font-size: clamp(1.25rem, 2.45vw, 2.55rem);
  font-weight: 1000;
  letter-spacing: 0.12em;
  line-height: 1;
  text-shadow: 0 3px 0 rgba(96, 42, 6, 0.72), 0 0 12px rgba(255, 207, 90, 0.72),
    0 0 28px rgba(255, 143, 90, 0.48);
  text-transform: uppercase;
  transform: translateX(-50%);
  animation: startBlink 1.12s steps(2, end) infinite;
  transition: transform 0.2s ease, filter 0.2s ease;
}

.title-screen:hover .castle-artwork {
  filter: brightness(1.03);
}

.title-screen:focus-visible .castle-artwork {
  filter: brightness(1.04);
}

.title-screen:hover .menu-start-overlay,
.title-screen:focus-visible .menu-start-overlay {
  filter: brightness(1.08);
  transform: translateX(-50%) scale(1.035);
}

.title-screen:has(.menu-start-overlay:hover) .castle-artwork {
  filter: brightness(1.06);
  transform: scale(1.018);
}

.title-screen:focus-visible {
  outline: 3px solid rgba(255, 207, 90, 0.72);
  outline-offset: -8px;
}

@keyframes startBlink {
  0%,
  58% {
    opacity: 1;
  }

  59%,
  100% {
    opacity: 0.28;
  }
}

.battle-glow {
  position: absolute;
  pointer-events: none;
  left: 12%;
  right: 12%;
  bottom: 0;
  height: 120px;
  background: radial-gradient(
    ellipse at center,
    rgba(255, 143, 90, 0.34),
    transparent 68%
  );
}

.menu-actions {
  display: grid;
  width: 92%;
  gap: 10px;
}

.hub-shell-menu .menu-actions {
  align-self: center;
  align-content: center;
  grid-template-rows: repeat(4, minmax(86px, 1fr));
  gap: clamp(12px, 2vh, 20px);
  max-height: min(100%, 720px);
}

.menu-wallet {
  position: absolute;
  top: 18px;
  right: 28px;
  z-index: 4;
}

.menu-action,
.back-button {
  border: 1px solid rgba(255, 247, 232, 0.12);
  border-radius: 8px;
  color: #fff7e8;
  background: rgba(255, 255, 255, 0.08);
  cursor: pointer;
}

.menu-action {
  min-height: 104px;
  padding: 18px 20px;
  text-align: left;
  transition: transform 0.16s ease, border-color 0.16s ease,
    background 0.16s ease;
}

.menu-action:hover,
.back-button:hover {
  border-color: rgba(255, 207, 90, 0.62);
  transform: translateY(-2px);
}

.menu-action.primary {
  background: linear-gradient(
    135deg,
    rgba(255, 207, 90, 0.22),
    rgba(255, 143, 90, 0.18)
  );
}

.menu-action span,
.menu-action strong {
  display: block;
}

.menu-action span {
  margin-bottom: 8px;
  color: #ffcf5a;
  font-size: 1.7rem;
  font-weight: 900;
}

.menu-action strong {
  color: rgba(255, 247, 232, 0.72);
  font-size: 0.95rem;
}

.hub-section {
  display: grid;
  gap: 22px;
  margin-top: 28px;
}

.hub-shell-shop .hub-section,
.hub-shell-story .hub-section:first-of-type,
.hub-shell-extras .hub-section:first-of-type {
  margin-top: 0;
}

.menu-hall {
  gap: 18px;
}

.section-heading h2 {
  font-size: clamp(2.2rem, 4.8vw, 5rem);
}

.back-button {
  padding: 12px 15px;
  font-weight: 800;
}

.back-button.compact {
  padding: 10px 12px;
}

.shop-play-button {
  flex: 0 0 auto;
  min-width: 150px;
  padding-inline: 22px;
  white-space: nowrap;
}

.button-arrow {
  display: inline-block;
  margin-left: 12px;
  font-size: 1.62em;
  font-weight: 1000;
  line-height: 1;
  text-shadow: 0.9px 0 currentColor, -0.35px 0 currentColor;
  transform: translateY(2px) scaleX(1.18);
}

.shop-hero,
.story-empty,
.glossary-panel,
.bestiary-card,
.upgrade-card {
  border: 1px solid rgba(255, 247, 232, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
}

.shop-hero {
  display: grid;
  grid-template-columns:
    minmax(360px, 1fr) minmax(126px, auto) minmax(220px, auto)
    minmax(390px, auto);
  align-items: center;
  gap: 18px clamp(18px, 2.2vw, 36px);
  padding: 22px 26px;
}

.shop-hero-copy {
  min-width: 0;
  justify-self: start;
  text-align: left;
}

.shop-hero-copy h1 {
  margin: 0 0 6px;
  color: #fff7e8;
  font-size: clamp(1.45rem, 2.15vw, 2.15rem);
  line-height: 1.02;
}

.shop-hero-copy p {
  max-width: 760px;
  margin: 0;
  color: rgba(255, 247, 232, 0.76);
  font-size: clamp(0.88rem, 1vw, 1rem);
  font-weight: 800;
  line-height: 1.35;
}

.shop-status-item {
  display: grid;
  gap: 7px;
}

.shop-status-item span {
  color: rgba(255, 247, 232, 0.68);
  font-size: 0.82rem;
  font-weight: 800;
}

.shop-status-item strong {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: #fff7e8;
  font-size: 1.48rem;
  line-height: 1;
}

.shop-status-item.gold {
  min-width: 108px;
  justify-items: start;
}

.shop-status-item.level {
  min-width: 220px;
  justify-items: start;
}

.shop-status-item.gold strong {
  color: #ffcf5a;
  font-size: 1.58rem;
}

.shop-level-control {
  display: inline-grid;
  grid-template-columns: 38px minmax(82px, 1fr) 38px;
  align-items: center;
  gap: 8px;
}

.shop-level-control button,
.shop-level-control select {
  height: 38px;
  color: #fff7e8;
  border: 1px solid rgba(255, 247, 232, 0.16);
  border-radius: 8px;
  background: rgba(9, 13, 22, 0.58);
  font-weight: 900;
}

.shop-level-control button {
  cursor: pointer;
}

.shop-level-control button:disabled {
  cursor: default;
  opacity: 0.38;
}

.shop-level-control select {
  min-width: 82px;
  text-align: center;
  text-align-last: center;
}

.shop-play-button {
  min-width: 170px;
  white-space: nowrap;
}

.shop-command-group {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.hall-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.hall-tab {
  display: grid;
  gap: 4px;
  min-height: 66px;
  padding: 12px 16px;
  color: rgba(255, 247, 232, 0.72);
  text-align: left;
  border: 1px solid rgba(255, 247, 232, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.055);
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease,
    transform 0.18s ease;
}

.hall-tab:hover {
  border-color: rgba(255, 207, 90, 0.42);
  background: rgba(255, 255, 255, 0.085);
  transform: translateY(-1px);
}

.hall-tab.active {
  color: #fff7e8;
  border-color: rgba(255, 207, 90, 0.72);
  background: linear-gradient(
    135deg,
    rgba(255, 207, 90, 0.18),
    rgba(103, 224, 255, 0.08)
  );
  box-shadow: inset 0 0 0 1px rgba(255, 207, 90, 0.08);
}

.hall-tab span {
  color: inherit;
  font-size: 1rem;
  font-weight: 950;
  line-height: 1.05;
}

.hall-tab small {
  color: rgba(255, 247, 232, 0.52);
  font-size: 0.76rem;
  font-weight: 800;
  line-height: 1.25;
}

.shop-dev-button {
  min-width: 126px;
  white-space: nowrap;
}

@media (max-width: 1280px) {
  .shop-hero {
    grid-template-columns:
      minmax(360px, 1fr) minmax(126px, auto)
      minmax(220px, auto);
  }

  .shop-command-group {
    grid-column: 1 / -1;
    justify-content: flex-start;
  }
}

.dev-modal-backdrop {
  position: fixed;
  z-index: 2147483200;
  inset: 0;
  display: grid;
  align-items: start;
  justify-items: center;
  padding: clamp(18px, 5vh, 46px) 24px 24px;
  background: rgba(4, 7, 12, 0.72);
  backdrop-filter: blur(7px);
}

.dev-modal {
  display: grid;
  width: min(920px, 100%);
  max-height: calc(100vh - clamp(42px, 9vh, 70px));
  overflow-y: auto;
  gap: 20px;
  padding: 24px;
  border: 1px solid rgba(255, 207, 90, 0.28);
  border-radius: 12px;
  color: #fff7e8;
  background: linear-gradient(
    145deg,
    rgba(14, 22, 34, 0.96),
    rgba(20, 39, 34, 0.94)
  );
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.45);
}

.dev-modal-header,
.dev-modal-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.dev-modal-header h2 {
  margin: 0;
  font-size: clamp(1.8rem, 3vw, 3rem);
}

.dev-modal-header .icon-button {
  flex: 0 0 auto;
  border: 1px solid rgba(255, 247, 232, 0.16);
}

.dev-config-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.dev-field,
.dev-ability-row {
  display: grid;
  gap: 10px;
  padding: 14px;
  border: 1px solid rgba(255, 247, 232, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.07);
}

.dev-field span,
.dev-ability-row strong {
  font-weight: 900;
}

.dev-field input[type="number"],
.dev-ability-row input[type="number"] {
  width: 100%;
  min-width: 0;
  padding: 10px 11px;
  color: #fff7e8;
  border: 1px solid rgba(255, 247, 232, 0.16);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  font: inherit;
  font-weight: 800;
  outline: none;
}

.dev-field input[type="range"],
.dev-ability-row input[type="range"] {
  width: 100%;
  accent-color: #ffcf5a;
}

.dev-abilities {
  display: grid;
  gap: 10px;
}

.dev-ability-row {
  grid-template-columns: minmax(180px, 0.8fr) minmax(200px, 1fr) 86px;
  align-items: center;
}

.dev-ability-row span {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.dev-ability-row img {
  width: 42px;
  height: 42px;
  object-fit: cover;
  border: 1px solid rgba(255, 207, 90, 0.28);
  border-radius: 8px;
}

.dev-modal-actions {
  justify-content: flex-end;
}

.status-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.shop-hero h3 {
  margin: 0 0 10px;
  font-size: 1.35rem;
}

.shop-hero p {
  margin-bottom: 0;
  color: rgba(255, 247, 232, 0.76);
  line-height: 1.6;
}

.upgrade-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.shop-upgrade-sections {
  display: grid;
  gap: 22px;
}

.upgrade-section {
  display: grid;
  gap: 12px;
}

.upgrade-section-heading {
  display: flex;
  align-items: center;
}

.upgrade-section-heading .eyebrow {
  margin-bottom: 12px;
  font-size: 1rem;
  letter-spacing: 0.2em;
}

.upgrade-card {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 18px;
  min-height: 150px;
  border: 1px solid rgba(255, 247, 232, 0.12);
  border-radius: 8px;
  padding: 18px;
  overflow: visible;
  color: #fff7e8;
  background: rgba(255, 255, 255, 0.08);
  cursor: default;
  text-align: left;
  outline: none;
}

.upgrade-card::before {
  position: absolute;
  inset: 0;
  content: "";
  opacity: 0.18;
  background: radial-gradient(circle at 15% 18%, #ffcf5a, transparent 34%),
    linear-gradient(135deg, transparent 34%, rgba(255, 255, 255, 0.1));
  pointer-events: none;
}

.upgrade-card:hover::before {
  opacity: 0.28;
}

.upgrade-card > div,
.upgrade-card-max,
.upgrade-card-cost,
.upgrade-icon {
  position: relative;
  z-index: 1;
}

.upgrade-card strong,
.upgrade-card span {
  display: block;
}

.upgrade-card strong {
  margin-bottom: 8px;
  font-size: 1.2rem;
}

.upgrade-card span {
  color: rgba(255, 247, 232, 0.72);
  line-height: 1.45;
}

.upgrade-ability-description {
  display: block;
  margin: 8px 0 7px;
  padding: 8px 10px;
  border: 1px solid rgba(103, 199, 255, 0.16);
  border-radius: 8px;
  color: rgba(231, 245, 255, 0.86) !important;
  background: rgba(103, 199, 255, 0.075);
  font-size: 0.86rem;
  font-weight: 800;
  line-height: 1.32 !important;
}

.upgrade-ability-description em {
  display: inline-block;
  margin-right: 8px;
  color: #67c7ff;
  font-size: 0.68rem;
  font-style: normal;
  font-weight: 1000;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.upgrade-card-max,
.upgrade-card-cost {
  flex: 0 0 auto;
  align-self: center;
  font-weight: 900;
}

.upgrade-card-cost {
  display: inline-grid;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 128px;
  min-width: 128px;
  min-height: 58px;
  padding: 9px 12px 8px;
  border: 1px solid rgba(255, 207, 90, 0.5);
  border-radius: 12px;
  color: #1d1721;
  background: linear-gradient(135deg, #ffcf5a, #ff8f5a);
  box-shadow: 0 10px 24px rgba(255, 143, 90, 0.2);
  cursor: pointer;
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1;
  transition: transform 0.16s ease, filter 0.16s ease, opacity 0.16s ease;
}

.upgrade-cost-amount {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #1d1721 !important;
  font-size: 0.98rem;
  line-height: 1;
  white-space: nowrap;
}

.upgrade-cost-label {
  display: block;
  color: rgba(29, 23, 33, 0.78) !important;
  font-size: 0.64rem;
  font-weight: 1000;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
}

.upgrade-card-cost:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.06);
}

.upgrade-card-cost:disabled {
  cursor: not-allowed;
  filter: grayscale(0.35);
  opacity: 0.55;
}

.upgrade-card-cost.locked:disabled {
  border-color: rgba(103, 199, 255, 0.28);
  color: rgba(255, 247, 232, 0.74);
  background: linear-gradient(
    135deg,
    rgba(32, 48, 68, 0.92),
    rgba(21, 29, 43, 0.88)
  );
  opacity: 0.88;
}

.upgrade-card-cost.locked .upgrade-cost-amount,
.upgrade-card-cost.locked .upgrade-cost-label {
  color: rgba(255, 247, 232, 0.78) !important;
}

.upgrade-card-max {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 108px;
  padding: 9px 11px;
  border: 1px solid rgba(98, 215, 173, 0.35);
  border-radius: 999px;
  color: #9fe8cf;
  background: rgba(98, 215, 173, 0.1);
  font-size: 0.82rem;
  white-space: nowrap;
}

.upgrade-card-values {
  display: grid;
  gap: 7px;
  margin-top: 12px;
}

.upgrade-card-value {
  display: grid;
  grid-template-columns: minmax(110px, 1fr) auto auto;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.055);
}

.upgrade-card-value.has-details {
  position: relative;
  z-index: 4;
  cursor: pointer;
  outline: none;
  transition: background 0.16s ease, box-shadow 0.16s ease;
}

.upgrade-card-value.has-details:hover,
.upgrade-card-value.has-details:focus,
.upgrade-card-value.has-details.open {
  background: rgba(255, 255, 255, 0.082);
  box-shadow: inset 0 0 0 1px rgba(255, 207, 90, 0.18);
}

.upgrade-card-value.has-details > span:first-child::after {
  content: "Dettagli";
  display: inline-flex;
  margin-left: 8px;
  padding: 2px 6px;
  color: rgba(255, 207, 90, 0.82);
  border: 1px solid rgba(255, 207, 90, 0.28);
  border-radius: 999px;
  background: rgba(255, 207, 90, 0.08);
  font-size: 0.62rem;
  line-height: 1;
  text-transform: uppercase;
}

.upgrade-card-value.single-value {
  grid-template-columns: minmax(110px, 1fr) auto;
}

.upgrade-detail-popover {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 8;
  display: grid;
  gap: 6px;
  width: min(520px, calc(100vw - 96px));
  min-width: 430px;
  padding: 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: rgba(7, 11, 16, 0);
  opacity: 0;
  pointer-events: none;
  transform: translateY(-3px);
  visibility: hidden;
  transition: opacity 0.16s ease, transform 0.16s ease, background 0.16s ease,
    border-color 0.16s ease, visibility 0.16s ease;
}

.upgrade-card-value.has-details:hover .upgrade-detail-popover,
.upgrade-card-value.has-details:focus .upgrade-detail-popover,
.upgrade-card-value.has-details.open .upgrade-detail-popover {
  border-color: rgba(255, 247, 232, 0.1);
  background: rgba(7, 11, 16, 0.28);
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
  visibility: visible;
}

.upgrade-detail-row {
  display: grid;
  grid-template-columns: minmax(92px, 1fr) auto auto;
  align-items: center;
  gap: 8px;
}

.upgrade-card-value span,
.upgrade-card-value strong,
.upgrade-detail-row span,
.upgrade-detail-row strong {
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.2;
}

.upgrade-card-value span,
.upgrade-detail-row span {
  color: rgba(255, 247, 232, 0.68);
  font-weight: 800;
}

.upgrade-card-value strong,
.upgrade-detail-row strong {
  color: #fff7e8;
  font-weight: 900;
}

.upgrade-icon {
  display: grid;
  flex: 0 0 88px;
  width: 88px;
  height: 88px;
  place-items: center;
  align-self: center;
  border: 1px solid rgba(255, 247, 232, 0.22);
  border-radius: 10px;
  color: #1d1721 !important;
  background: rgba(255, 207, 90, 0.14);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.3);
  font-size: 0.9rem;
  font-weight: 1000;
  line-height: 1;
  overflow: hidden;
}

.upgrade-sghiubbePower .upgrade-icon,
.upgrade-arrowsPower .upgrade-icon,
.upgrade-barrierPower .upgrade-icon,
.upgrade-kickPower .upgrade-icon,
.upgrade-barbaroPower .upgrade-icon {
  align-self: flex-start;
  margin-top: 28px;
}

.upgrade-icon img {
  display: block;
  width: 108%;
  height: 108%;
  object-fit: cover;
  transform: scale(1.1);
}

.upgrade-damage .upgrade-icon img,
.upgrade-speed .upgrade-icon img,
.upgrade-arrows .upgrade-icon img,
.upgrade-walls .upgrade-icon img {
  width: 104%;
  height: 104%;
  object-fit: contain;
  transform: scale(1.01);
}

.upgrade-sghiubbePower .upgrade-icon img,
.upgrade-arrowsPower .upgrade-icon img,
.upgrade-barrierPower .upgrade-icon img,
.upgrade-kickPower .upgrade-icon img {
  width: 118%;
  height: 118%;
  object-fit: cover;
  transform: scale(1.12);
}

.upgrade-mana .upgrade-icon img,
.upgrade-manaRegen .upgrade-icon img,
.upgrade-goldHunter .upgrade-icon img,
.upgrade-cooldownPower .upgrade-icon img {
  width: 112%;
  height: 112%;
  object-fit: cover;
  transform: scale(1.06);
}

.upgrade-track {
  overflow: hidden;
  height: 8px;
  margin-top: 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
}

.upgrade-track-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #62d7ad, #ffcf5a, #ff8f5a);
}

.upgrade-arrows::before {
  opacity: 0.24;
  background: radial-gradient(circle at 14% 18%, #67c7ff, transparent 34%),
    linear-gradient(135deg, rgba(255, 207, 90, 0.2), transparent 58%);
}

.upgrade-arrows .upgrade-icon {
  background: rgba(103, 199, 255, 0.15);
}

.upgrade-walls .upgrade-icon {
  background: rgba(184, 197, 208, 0.15);
}

.upgrade-power .upgrade-icon {
  background: rgba(98, 215, 173, 0.15);
}

.story-empty {
  display: grid;
  gap: 10px;
  min-height: 220px;
  padding: clamp(22px, 4vw, 46px);
  align-content: center;
}

.story-empty h3 {
  margin: 0;
  color: #ffcf5a;
  font-size: clamp(1.6rem, 2.6vw, 2.8rem);
  line-height: 1.04;
}

.story-empty p {
  max-width: 680px;
  margin: 0;
  color: rgba(255, 247, 232, 0.68);
  font-weight: 750;
  line-height: 1.45;
}

.glossary-panel {
  display: grid;
  gap: 22px;
  padding: clamp(18px, 2.2vw, 28px);
}

.glossary-intro {
  max-width: 960px;
}

.glossary-intro h3 {
  margin: 4px 0 8px;
  color: #fff7e8;
  font-size: clamp(1.4rem, 2vw, 2rem);
  line-height: 1.08;
}

.glossary-intro p:not(.eyebrow) {
  margin: 0;
  color: rgba(255, 247, 232, 0.78);
  font-size: 0.98rem;
  font-weight: 800;
  line-height: 1.45;
}

.bestiary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.bestiary-card {
  display: grid;
  grid-template-columns: 124px minmax(0, 1fr);
  gap: 18px;
  min-height: 250px;
  padding: 18px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.08),
    rgba(255, 255, 255, 0.045)
  );
}

.bestiary-portrait {
  position: relative;
  display: grid;
  align-self: start;
  width: 112px;
  aspect-ratio: 1;
  place-items: center;
  overflow: hidden;
  border: 1px solid rgba(255, 247, 232, 0.14);
  border-radius: 8px;
  background: radial-gradient(
      circle at 50% 44%,
      rgba(255, 207, 90, 0.12),
      transparent 54%
    ),
    rgba(5, 10, 16, 0.56);
  box-shadow: inset 0 0 28px rgba(0, 0, 0, 0.36),
    0 14px 30px rgba(0, 0, 0, 0.24);
}

.bestiary-portrait img {
  width: 118%;
  height: 118%;
  object-fit: contain;
  object-position: center 66%;
  filter: drop-shadow(0 8px 12px rgba(0, 0, 0, 0.42));
}

.sprite-strip-preview {
  display: block;
  width: var(--preview-width, 96px);
  height: var(--preview-height, 120px);
  background-repeat: no-repeat;
  background-size: var(--preview-size, 900% auto);
  background-position: var(--preview-position, 25% 51%);
  filter: drop-shadow(0 8px 12px rgba(0, 0, 0, 0.42));
}

.cheese-avatar {
  position: relative;
  display: block;
  width: 72px;
  height: 72px;
  transform: rotate(-8deg);
}

.cheese-core {
  position: absolute;
  inset: 7px;
  border-radius: 42% 58% 48% 52%;
  background: var(--cheese-color);
  box-shadow: inset 0 -13px 0 var(--rind-color), 0 9px 18px rgba(0, 0, 0, 0.32);
}

.cheese-mark {
  position: absolute;
  z-index: 1;
  border-radius: 50%;
  background: rgba(80, 50, 20, 0.28);
}

.cheese-mark.one {
  top: 25px;
  left: 28px;
  width: 8px;
  height: 8px;
}

.cheese-mark.two {
  right: 20px;
  bottom: 25px;
  width: 10px;
  height: 7px;
}

.archetype-archer .cheese-avatar::after,
.archetype-catapult .cheese-avatar::after,
.archetype-ninja .cheese-avatar::after,
.archetype-dragon .cheese-avatar::after,
.archetype-boss .cheese-avatar::after,
.archetype-superboss .cheese-avatar::after {
  position: absolute;
  z-index: 2;
  content: "";
  background: rgba(255, 247, 232, 0.86);
}

.archetype-archer .cheese-avatar::after {
  top: 31px;
  right: 1px;
  width: 33px;
  height: 4px;
  border-radius: 999px;
}

.archetype-catapult .cheese-avatar::after {
  right: 4px;
  bottom: 16px;
  width: 42px;
  height: 6px;
  border-radius: 999px;
  transform: rotate(-28deg);
}

.archetype-ninja .cheese-avatar::after {
  top: 29px;
  left: 8px;
  width: 56px;
  height: 5px;
  border-radius: 999px;
}

.archetype-dragon .cheese-avatar::after {
  top: 10px;
  left: -8px;
  width: 42px;
  height: 28px;
  clip-path: polygon(0 50%, 100% 0, 70% 50%, 100% 100%);
}

.archetype-boss .cheese-avatar::after,
.archetype-superboss .cheese-avatar::after {
  top: 2px;
  left: 18px;
  width: 34px;
  height: 20px;
  background: #ffcf5a;
  clip-path: polygon(
    0 100%,
    12% 26%,
    34% 74%,
    50% 0,
    66% 74%,
    88% 26%,
    100% 100%
  );
}

.bestiary-copy {
  min-width: 0;
}

.bestiary-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.bestiary-unlock {
  margin: 0 0 4px;
  color: #ffcf5a;
  font-size: 0.72rem;
  font-weight: 1000;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.bestiary-title-row h3 {
  margin: 0;
  color: #fff7e8;
  font-size: clamp(1.05rem, 1.3vw, 1.32rem);
  line-height: 1.08;
}

.bestiary-title-row > span {
  flex: 0 0 auto;
  max-width: 120px;
  padding: 5px 8px;
  border: 1px solid rgba(255, 207, 90, 0.22);
  border-radius: 999px;
  color: #ffcf5a;
  background: rgba(255, 207, 90, 0.08);
  font-size: 0.68rem;
  font-weight: 1000;
  line-height: 1;
  text-align: center;
  text-transform: uppercase;
}

.bestiary-technical,
.bestiary-lore,
.bestiary-growth {
  margin: 0;
  line-height: 1.42;
  text-align: left;
}

.bestiary-technical {
  color: rgba(255, 247, 232, 0.82);
  font-size: 0.88rem;
  font-weight: 850;
}

.bestiary-lore {
  margin-top: 8px;
  color: rgba(255, 247, 232, 0.62);
  font-size: 0.84rem;
  font-weight: 700;
}

.bestiary-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 7px;
  margin-top: 13px;
}

.bestiary-stat {
  display: grid;
  gap: 3px;
  min-width: 0;
  padding: 7px 8px;
  border-radius: 8px;
  background: rgba(6, 10, 14, 0.34);
}

.bestiary-stat em {
  overflow: hidden;
  color: rgba(255, 247, 232, 0.58);
  font-size: 0.62rem;
  font-style: normal;
  font-weight: 900;
  line-height: 1;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.bestiary-stat strong {
  overflow: hidden;
  color: #fff7e8;
  font-size: 0.86rem;
  line-height: 1.05;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bestiary-growth {
  margin-top: 10px;
  color: rgba(255, 247, 232, 0.52);
  font-size: 0.78rem;
  font-weight: 760;
}

.game-shell {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  overflow: hidden;
  min-height: 100vh;
  cursor: crosshair;
  user-select: none;
  background: #131b25;
}

.game-shell.paused,
.game-shell.finished {
  cursor: default;
}

.game-canvas {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
}

.hud {
  position: absolute;
  z-index: 2;
}

.top-hud {
  top: 18px;
  left: 18px;
  right: 18px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  pointer-events: none;
}

.title-block,
.power-button,
.store-panel,
.bottom-bar,
.game-over > div {
  border: 1px solid rgba(255, 247, 232, 0.14);
  border-radius: 8px;
  background: rgba(12, 18, 29, 0.76);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.24);
  backdrop-filter: blur(12px);
}

.title-block {
  padding: 16px 18px;
  pointer-events: auto;
}

.title-block h1 {
  margin: 0;
  font-size: clamp(1.4rem, 2.4vw, 2.5rem);
}

.hud-menu-button {
  padding: 8px 12px;
  border: 1px solid rgba(255, 247, 232, 0.16);
  border-radius: 8px;
  color: #fff7e8;
  background: rgba(255, 255, 255, 0.08);
  cursor: pointer;
  font-weight: 800;
}

.hud-menu-button:disabled {
  cursor: default;
  opacity: 0.65;
}

.pause-button-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 16px;
  height: 16px;
  margin-right: 7px;
  vertical-align: -2px;
}

.pause-button-icon span {
  width: 4px;
  height: 13px;
  border-radius: 999px;
  background: currentColor;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.24);
}

.floating-menu-button {
  align-self: flex-start;
  margin: 0;
  pointer-events: auto;
}

.level-panel {
  flex: 0 1 520px;
  max-width: 540px;
  min-width: 260px;
  padding: 8px 10px 7px;
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    rgba(9, 13, 18, 0.52),
    rgba(9, 13, 18, 0.26)
  );
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
  pointer-events: none;
}

.level-banner {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 6px;
}

.level-banner span {
  color: rgba(255, 247, 232, 0.72);
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.level-banner strong {
  color: #ffcf5a;
  font-size: 1.35rem;
  line-height: 1;
}

.level-progress-track {
  position: relative;
  height: 7px;
  border-radius: 999px;
  background: rgba(6, 10, 14, 0.48);
}

.level-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #62d7ad, #ffcf5a, #ff8f5a);
  transition: width 0.2s ease;
}

.cheese-marker {
  position: absolute;
  top: 50%;
  width: 20px;
  height: 16px;
  transform: translate(-50%, -50%);
  transition: left 0.2s ease;
}

.cheese-marker span {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 45% 55% 48% 52%;
  background: #f6df7a;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.28);
  clip-path: polygon(0 10%, 100% 50%, 0 92%);
}

.cheese-marker span::before,
.cheese-marker span::after {
  position: absolute;
  content: "";
  border-radius: 50%;
  background: rgba(112, 70, 24, 0.35);
}

.cheese-marker span::before {
  width: 5px;
  height: 5px;
  left: 7px;
  top: 8px;
}

.cheese-marker span::after {
  width: 4px;
  height: 4px;
  left: 14px;
  top: 14px;
}

.level-progress small {
  display: block;
  margin-top: 4px;
  color: rgba(255, 247, 232, 0.72);
  font-size: 0.72rem;
  text-align: center;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(92px, 1fr));
  gap: 8px;
  width: min(300px, 26vw);
}

.stat-grid.defense {
  grid-template-columns: minmax(96px, auto);
  justify-content: end;
  width: auto;
}

.stat-pill {
  min-height: 0;
  padding: 7px 12px;
  color: #fff7e8;
  text-align: right;
  border-radius: 999px;
  background: rgba(8, 12, 17, 0.34);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.14);
  backdrop-filter: blur(8px);
}

.stat-pill span,
.bottom-bar span,
.power-button small,
.upgrade-row span {
  color: rgba(255, 247, 232, 0.72);
  font-size: 0.78rem;
}

.stat-pill strong {
  display: inline-block;
  margin-left: 8px;
  font-size: 1.12rem;
  line-height: 1;
}

.resource-stat span {
  display: inline-block;
}

.meter {
  overflow: hidden;
  height: 8px;
  margin-top: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
}

.meter-fill {
  height: 100%;
  border-radius: inherit;
  transition: width 0.18s ease;
}

.meter-fill.health {
  background: linear-gradient(90deg, #62d7ad, #ffcf5a);
}

.meter-fill.health.healthy {
  background: linear-gradient(90deg, #35d287, #8fe66d);
}

.meter-fill.health.warning {
  background: linear-gradient(90deg, #ffcf5a, #ff9f43);
}

.meter-fill.health.danger {
  background: linear-gradient(90deg, #ff5f57, #c92936);
}

.meter-fill.mana {
  background: linear-gradient(90deg, #4fb8ff, #8d5cff);
}

.wall-resource-hud {
  left: 10px;
  bottom: 14px;
  display: grid;
  gap: 7px;
  width: min(330px, 28vw);
  padding: 8px 10px;
  pointer-events: none;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    rgba(6, 9, 14, 0.62),
    rgba(6, 9, 14, 0.22)
  );
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(8px);
}

.resource-line {
  display: grid;
  grid-template-columns: 30px minmax(120px, 1fr) 64px;
  align-items: center;
  gap: 8px;
}

.resource-line em {
  color: #ffcf5a;
  font-family: "Trebuchet MS", Avenir, sans-serif;
  font-size: 0.88rem;
  font-style: normal;
  font-weight: 1000;
  letter-spacing: 0.05em;
  text-align: right;
}

.resource-line:nth-child(2) em {
  color: #67c7ff;
}

.resource-line strong {
  color: #fff7e8;
  font-size: 0.86rem;
  line-height: 1;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.55);
}

.resource-meter {
  overflow: hidden;
  height: 9px;
  border-radius: 999px;
  background: rgba(6, 9, 14, 0.62);
  box-shadow: inset 0 0 0 1px rgba(255, 247, 232, 0.08);
}

.resource-fill {
  height: 100%;
  border-radius: inherit;
  transition: width 0.18s ease;
}

.resource-fill.health {
  background: linear-gradient(90deg, #35d287, #8fe66d);
}

.resource-fill.health.warning {
  background: linear-gradient(90deg, #ffcf5a, #ff9f43);
}

.resource-fill.health.danger {
  background: linear-gradient(90deg, #ff5f57, #c92936);
}

.resource-fill.mana {
  background: linear-gradient(90deg, #4fb8ff, #8d5cff);
}

.curse-hud {
  left: 50%;
  bottom: 112px;
  padding: 9px 14px;
  color: #f2eaff;
  border: 1px solid rgba(141, 92, 255, 0.55);
  border-radius: 8px;
  background: rgba(18, 10, 28, 0.82);
  box-shadow: 0 16px 44px rgba(50, 18, 90, 0.3);
  transform: translateX(-50%);
  font-size: 0.84rem;
  font-weight: 900;
  pointer-events: none;
}

.power-dock {
  left: 50%;
  right: auto;
  bottom: 18px;
  display: grid;
  grid-template-columns: repeat(5, 78px);
  gap: 12px;
  width: auto;
  transform: translateX(-50%);
}

.power-button {
  position: relative;
  display: grid;
  width: 78px;
  height: 78px;
  min-height: 0;
  overflow: hidden;
  padding: 0;
  place-items: center;
  color: #fff7e8;
  text-align: center;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease,
    background 0.15s ease;
}

.power-button:hover:not(:disabled) {
  transform: translateY(-2px);
}

.power-button:disabled {
  cursor: not-allowed;
  opacity: 1;
}

.power-button.cursed {
  filter: grayscale(0.45) brightness(0.72);
}

.power-button.no-mana:not(.cooling) .power-icon::after {
  position: absolute;
  inset: 0;
  content: "";
  background: rgba(22, 42, 78, 0.62);
  box-shadow: inset 0 0 28px rgba(79, 184, 255, 0.32);
}

.power-button.locked:not(.cooling) .power-icon::after {
  position: absolute;
  inset: 0;
  content: "";
  background: rgba(4, 8, 14, 0.68);
  box-shadow: inset 0 0 0 2px rgba(103, 199, 255, 0.18);
}

.power-button.locked:not(.cooling) .power-icon::before {
  position: absolute;
  z-index: 3;
  left: 50%;
  top: 50%;
  content: "LOCK";
  color: rgba(255, 247, 232, 0.76);
  font-size: 0.56rem;
  font-weight: 1000;
  letter-spacing: 0.08em;
  transform: translate(-50%, -50%) rotate(-8deg);
}

.power-key {
  position: absolute;
  z-index: 2;
  right: 0;
  bottom: 0;
  display: grid;
  width: 26px;
  height: 26px;
  place-items: center;
  border-radius: 8px 0 0 0;
  color: #1d1721;
  background: #ffcf5a;
  font-size: 0.72rem;
  font-weight: 900;
}

.power-cost {
  position: absolute;
  z-index: 2;
  bottom: 0;
  left: 0;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  min-width: 34px;
  height: 22px;
  padding: 0 7px 0 6px;
  border: 1px solid rgba(103, 199, 255, 0.24);
  border-bottom: 0;
  border-left: 0;
  border-radius: 0 8px 0 0;
  color: #dff4ff;
  background: rgba(5, 12, 20, 0.58);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.2);
  font-size: 0.68rem;
  font-weight: 1000;
  line-height: 1;
  pointer-events: none;
}

.power-cost em {
  color: rgba(223, 244, 255, 0.68);
  font-size: 0.52rem;
  font-style: normal;
  letter-spacing: 0.04em;
}

.power-icon {
  position: absolute;
  inset: 0;
  display: grid;
  overflow: hidden;
  width: 100%;
  height: 100%;
  place-items: center;
  border-radius: inherit;
  color: #1d1721;
  background: rgba(255, 207, 90, 0.16);
  font-weight: 900;
}

.power-icon img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.16);
}

.power-barbaro .power-icon img,
.upgrade-barbaroPower .upgrade-icon img {
  object-fit: contain;
  transform: scale(1.18) translateY(-2%);
}

.power-cooldown-veil {
  position: absolute;
  z-index: 2;
  top: 0;
  right: 0;
  left: 0;
  overflow: visible;
  border-radius: 8px 8px 0 0;
  background: rgba(4, 8, 13, 0.72);
  box-shadow: inset 0 0 24px rgba(0, 0, 0, 0.72);
  pointer-events: none;
  transition: height 0.12s linear;
}

.power-cooldown-line {
  position: absolute;
  right: 0;
  bottom: -2px;
  left: 0;
  height: 4px;
  background: linear-gradient(90deg, #62d7ad, #ffcf5a, #ff8f5a);
  box-shadow: 0 0 14px rgba(255, 207, 90, 0.86);
}

.power-tooltip {
  position: absolute;
  z-index: 5;
  right: 0;
  bottom: calc(100% + 10px);
  display: none;
  width: 190px;
  padding: 10px;
  color: rgba(255, 247, 232, 0.78);
  text-align: left;
  border: 1px solid rgba(255, 247, 232, 0.14);
  border-radius: 8px;
  background: rgba(12, 18, 29, 0.92);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.32);
}

.power-tooltip strong {
  display: block;
  margin-bottom: 4px;
  color: #fff7e8;
}

.power-button:hover .power-tooltip {
  display: block;
}

.store-toggle {
  top: 118px;
  right: 18px;
}

.store-panel {
  position: absolute;
  z-index: 4;
  top: 174px;
  right: 18px;
  width: min(390px, calc(100% - 36px));
  padding: 16px;
}

.store-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.store-header h2 {
  margin: 0 0 12px;
}

.icon-button {
  width: 34px;
  height: 34px;
  color: #fff7e8;
  background: rgba(255, 255, 255, 0.1);
}

.store-list {
  display: grid;
  gap: 10px;
}

.upgrade-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 76px;
  padding: 12px;
  color: #fff7e8;
  text-align: left;
  border: 1px solid rgba(255, 247, 232, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.07);
  cursor: pointer;
}

.upgrade-row:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

.upgrade-row strong,
.upgrade-row span {
  display: block;
}

.upgrade-row span {
  margin-top: 4px;
}

.upgrade-row em {
  flex: 0 0 auto;
  color: #ffcf5a;
  font-style: normal;
  font-weight: 900;
}

.bottom-bar {
  left: 18px;
  right: 18px;
  bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  min-height: 42px;
  padding: 10px 14px;
  pointer-events: none;
}

.bottom-bar p {
  margin: 0;
  font-weight: 800;
}

.pause-overlay {
  position: absolute;
  z-index: 6;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 22px;
  background: rgba(8, 15, 24, 0.58);
  backdrop-filter: blur(3px);
  cursor: default;
}

.pause-panel {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(18px, 4vw, 56px);
  width: min(680px, calc(100% - 38px));
  padding: clamp(24px, 4vw, 42px);
  border: 1px solid rgba(255, 247, 232, 0.18);
  border-radius: 8px;
  background: linear-gradient(
    135deg,
    rgba(10, 14, 22, 0.82),
    rgba(20, 31, 43, 0.72)
  );
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.42);
}

.pause-action {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding: 0;
  border: 0;
  color: rgba(255, 247, 232, 0.86);
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-weight: 900;
}

.pause-action-icon {
  display: grid;
  place-items: center;
  width: clamp(68px, 9vw, 92px);
  aspect-ratio: 1;
  border: 4px solid rgba(13, 18, 28, 0.9);
  border-radius: 50%;
  color: #0c1520;
  background: radial-gradient(circle at 34% 28%, #9fe6ff 0 12%, transparent 13%),
    linear-gradient(135deg, #77d4ff, #2d8fd8);
  box-shadow: inset 0 0 0 3px rgba(255, 255, 255, 0.16),
    0 8px 18px rgba(0, 0, 0, 0.34);
  font-size: clamp(2.15rem, 4.6vw, 3.3rem);
  line-height: 1;
  transition: transform 0.16s ease, filter 0.16s ease;
}

.pause-action strong {
  overflow: hidden;
  max-width: 100%;
  color: #fff7e8;
  font-size: clamp(0.78rem, 1.3vw, 0.95rem);
  letter-spacing: 0;
  line-height: 1.1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pause-action:hover .pause-action-icon,
.pause-action:focus-visible .pause-action-icon {
  filter: brightness(1.08);
  transform: translateY(-2px);
}

.pause-action.primary .pause-action-icon {
  background: radial-gradient(circle at 34% 28%, #fff0a6 0 12%, transparent 13%),
    linear-gradient(135deg, #ffcf5a, #ff8f5a);
}

.game-over {
  position: absolute;
  z-index: 5;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 22px;
  background: rgba(8, 10, 15, 0.72);
}

.game-over > div {
  width: min(760px, calc(100% - 40px));
  padding: clamp(36px, 4vw, 52px) clamp(42px, 5vw, 68px);
  text-align: center;
}

.game-over h2 {
  margin-bottom: 12px;
  font-size: clamp(1.85rem, 2.4vw, 2.45rem);
  line-height: 1.08;
}

.game-over > div > .eyebrow {
  color: #ffcf5a;
  font-size: clamp(0.92rem, 1.25vw, 1.18rem);
  letter-spacing: 0.22em;
}

.game-over:not(.level-complete) h2 {
  max-width: 780px;
  margin: 18px auto 0;
  font-size: clamp(1.2rem, 1.8vw, 1.65rem);
  line-height: 1.22;
}

.level-complete-quote {
  margin: 0 auto;
  max-width: 100%;
  color: rgba(255, 247, 232, 0.84);
  overflow: hidden;
  font-size: clamp(0.92rem, 1.18vw, 1.16rem);
  font-weight: 800;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.level-complete-rewards {
  display: grid;
  gap: 15px;
  width: min(390px, 100%);
  margin-top: 34px;
  margin-inline: auto;
}

.defeat-rewards {
  width: min(430px, 100%);
  margin-top: 36px;
}

.level-complete-reward-line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(54px, max-content);
  align-items: center;
  gap: 22px;
  margin: 0;
  color: rgba(255, 247, 232, 0.86);
  font-weight: 900;
}

.level-complete-reward-line .currency-label {
  justify-self: start;
}

.level-complete-reward-line strong {
  justify-self: start;
  min-width: 54px;
  text-align: left;
}

.flawless-bonus-line {
  color: rgba(255, 247, 232, 0.78);
}

.level-complete-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin-top: 38px;
}

@media (max-width: 900px) {
  .hub-shell {
    min-height: auto;
  }

  .hub-topbar,
  .section-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .shop-hero {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }

  .shop-command-group {
    justify-content: flex-start;
  }

  .hall-tabs {
    grid-template-columns: 1fr;
  }

  .hub-menu {
    grid-template-columns: 1fr;
    justify-items: center;
    padding-top: 70px;
  }

  .castle-showcase {
    width: min(100%, 760px);
  }

  .upgrade-grid,
  .bestiary-grid {
    grid-template-columns: 1fr;
  }

  .bestiary-card {
    grid-template-columns: 96px minmax(0, 1fr);
  }

  .bestiary-portrait {
    width: 92px;
  }

  .bestiary-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dev-config-grid,
  .dev-ability-row {
    grid-template-columns: 1fr;
  }

  .top-hud {
    flex-direction: column;
  }

  .stat-grid {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
    width: min(520px, 100%);
  }

  .level-panel {
    width: min(520px, 100%);
    max-width: none;
  }

  .store-toggle {
    top: auto;
    right: 14px;
    bottom: 176px;
  }

  .power-dock {
    left: 50%;
    right: auto;
    bottom: 14px;
    grid-template-columns: repeat(5, 68px);
  }

  .wall-resource-hud {
    left: 10px;
    bottom: 82px;
    width: min(330px, calc(100% - 92px));
  }

  .bottom-bar {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }
}

@media (max-width: 620px) {
  .secret-page {
    height: calc(100vh - 86px);
    min-height: 0;
    padding: 22px 14px;
  }

  .gate-card {
    padding: 28px 20px;
  }

  .gate-form {
    flex-direction: column;
  }

  .gate-form input {
    min-width: 0;
    width: 100%;
  }

  .hub-shell {
    padding: 14px;
  }

  .hub-topbar h1 {
    font-size: 1.9rem;
  }

  .menu-action {
    min-height: 94px;
  }

  .castle-showcase {
    width: min(100%, 520px);
  }

  .menu-title-overlay {
    top: 20px;
    left: 50%;
    width: min(360px, calc(100% - 32px));
    font-size: clamp(1.35rem, 8vw, 2.5rem);
  }

  .menu-start-overlay {
    bottom: 26px;
    min-width: min(210px, calc(100% - 36px));
  }

  .menu-wallet {
    top: 12px;
    right: 14px;
  }

  .title-block {
    padding: 12px;
  }

  .stat-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .stat-pill {
    padding: 8px;
  }

  .wall-resource-hud {
    bottom: 78px;
    width: calc(100% - 92px);
  }

  .power-button {
    width: 68px;
    height: 68px;
  }
}
</style>

<style>
body.adalberto-secret-route,
html.adalberto-secret-route,
body.adalberto-page-active,
html.adalberto-page-active {
  background: #121827;
}

body.adalberto-page-active,
html.adalberto-page-active {
  overflow: hidden;
}

body.adalberto-secret-route .notizie-sidebar,
body.adalberto-secret-route .sidebar-reveal-zone,
body.adalberto-secret-route footer {
  display: none !important;
}

body.adalberto-secret-route .container {
  height: 100vh !important;
  background: #121827 !important;
}

body.adalberto-page-active .navbar,
body.adalberto-page-active .topbar,
body.adalberto-page-active .header-icons,
body.adalberto-page-active .icons-container,
body.adalberto-page-active .menu-container,
body.adalberto-page-active .strumenti,
body.adalberto-page-active .strumenti2 {
  display: none !important;
}

body.adalberto-page-active .container {
  height: 100vh !important;
  overflow: hidden !important;
  background: #121827 !important;
}

body.adalberto-page-active .secret-page,
body.adalberto-page-active .game-shell {
  z-index: 2147483000 !important;
}

body.adalberto-page-active .secret-page {
  height: 100vh !important;
  min-height: 100vh !important;
}
</style>
